import React, {
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Nav,
  NavItem,
  NavLink,
  Row,
  Col,
  Input,
  Label,
  FormGroup,
  Button,
  Spinner,
  CardTitle,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotification } from "contexts/NotificationContext";
import ReactDatetime from "components/CustomDatePicker";
import moment from "moment";
import {
  formatDateForPicker,
  formatDateForAPI,
  formatDateForDisplay,
  getCurrentDateForAPI,
  parseDateFromAPI,
} from "utils/dateUtils";
import { formatPriceWithDollar, toNumber } from "utils/currency";
import {
  getRemainingLateFeeDue,
  getSchedulePaymentAmount,
  getScheduleTotalDue,
} from "utils/paymentSchedule";
import { getAccountStatusColor } from "utils/accountStatusColors";
import {
  getPaymentCommentSuggestions,
  addPaymentCommentSuggestion,
  filterPaymentCommentSuggestions,
} from "utils/paymentCommentSuggestions";
import {
  useCreatePayment,
  useCreatePromisePay,
  useGetPromisePayments,
  useDeletePromisePay,
  useCreateSideNote,
  useCreatePayoff,
  usePrintPayoff,
} from "api/payments/service";
import { useGetChannels } from "api/channels/service";
import { useUpdateCalculus, useGetCalculusDetail, usePrintCalculus, useSendAccountSMS, useChargeOffCalculus, useReverseChargeOffCalculus } from "api/calculuses/service";
import { useCreateWriteDown } from "api/write-downs/service";
import { useGetDocuments } from "api/documents/service";
import { useSendMail } from "api/mails/service";
import { useGetRecordSpecificReports } from "api/reports/service";
import {
  MdChevronRight,
  MdPayment,
  MdCheckCircle,
  MdDateRange,
  MdTrendingDown,
  MdRadioButtonUnchecked,
  MdNoteAdd,
  MdReportProblem,
  MdRestore,
  MdSave,
  MdSecurity,
  MdAddAlert,
  MdEdit,
  MdLock,
  MdLockOpen,
  MdAddPhotoAlternate,
  MdInsertDriveFile,
  MdFlag,
  MdAssessment,
  MdPrint,
  MdEmail,
  MdMessage,
  MdInfo,
  MdReceipt,
  MdAttachMoney,
  MdMoneyOff,
} from "react-icons/md";
import { IoMdCloseCircle } from "react-icons/io";
import { RiDeleteBinLine } from "react-icons/ri";
import Select from "components/Select";
import StatesInput from "components/StatesInput";
import DollarInput from "components/DollarInput";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import SendEmail from "components/SendEmail";

import NotesTab from "views/inventory/tabs/NotesTab";
import FlagTab from "views/inventory/tabs/FlagTab";
import ReceiptDrawer from "./ReceiptDrawer";
import { formatDateTimeForDisplay } from "utils/dateUtils";

const createPaymentFormDefaults = () => ({
  date: getCurrentDateForAPI(),
  effect_date: getCurrentDateForAPI(),
  amount: formatPriceWithDollar(0),
  delivery_method: "",
  payment_form: "", // Default to empty to force user selection
  amount_received: formatPriceWithDollar(0),
  ref_number: "",
  note: "",
  action_type: "take_payment", // "take_payment" | "waive_late_fees"
  balance_type: "",
});

const PAYMENT_POST_TYPE_OPTIONS = [
  { value: "", label: "All Payments" },
  { value: "DP", label: "Down Payments" },
  { value: "RP", label: "Reg Payments" },
  { value: "MP", label: "Misc Payments" },
  { value: "OP", label: "Side Note Payments" },
  { value: "IP", label: "CPI Payments" },
  { value: "PP", label: "Principal Only" },
];

const PINNED_ACCOUNT_RECORD_REPORT_VALUES = new Set([
  "account_notes_by_account",
  "account_transactions_by_account_id",
]);

const getPaymentPostTypeCode = (item) => {
  const directCode = item?.payment_post_type || item?.post_type;
  if (directCode && PAYMENT_POST_TYPE_OPTIONS.some((o) => o.value === directCode)) {
    return directCode;
  }

  const type = String(item?.type || "").toLowerCase();
  if (type === "def_down") return "DP";
  if (type === "regular_payment") return "RP";
  if (type === "misc" || type === "misc_payment" || type === "misc_fees" || type === "misc_fee") {
    return "MP";
  }
  if (type === "side_note" || type === "side_note_payment" || type === "other_payment") {
    return "OP";
  }
  if (
    type === "cpi" ||
    type === "reccuring_cpi" ||
    type === "recurring_cpi" ||
    type === "cpi_balance"
  ) {
    return "IP";
  }
  if (type === "principal_only" || type === "principal") return "PP";

  const desc = String(item?.description || item?.note || "").toLowerCase();
  if (desc.includes("down pay")) return "DP";
  if (desc.includes("regular pay")) return "RP";
  if (desc.includes("misc")) return "MP";
  if (desc.includes("side note")) return "OP";
  if (desc.includes("cpi")) return "IP";
  if (desc.includes("principal only") || desc.includes("principal")) return "PP";

  return "RP";
};

const parseAppldInputValue = (val) =>
  parseFloat(String(val ?? "").replace(/[^0-9.]/g, "")) || 0;

/** IDMS waive late fee davranışı doğrulanana kadar satır checkbox'ları kapalı. */
const WAIVE_LATE_FEE_ROW_CHECKBOX_ENABLED = false;

const getEnteredLateFeeAppld = (idx, item, lateFeeAppldValues, waiveLateFeeValues) => {
  if (waiveLateFeeValues[idx]) return 0;
  if (lateFeeAppldValues[idx] !== undefined) {
    return parseAppldInputValue(lateFeeAppldValues[idx]);
  }
  return parseAppldInputValue(item?.late_fee_applied ?? 0);
};

const getEnteredPmtAppld = (idx, item, pmtAppldValues) => {
  if (pmtAppldValues[idx] !== undefined) {
    return parseAppldInputValue(pmtAppldValues[idx]);
  }
  return parseFloat(item?.payment_applied ?? 0) || 0;
};

const createSideNoteFormDefaults = () => ({
  description: "",
  type: "CPI Balance",
  amount: "",
  date: getCurrentDateForAPI(),
  rate: "",
  frequency: "Weekly",
});

const createMiscFeeAdjustmentFormDefaults = (calculus) => ({
  date_due: getCurrentDateForAPI(),
  adjustment_type: "Increase",
  description: "",
  amount: 0,
});

const normalizeWaiveFeeDateFromApi = (v) => {
  if (v == null || v === "") return "";
  if (typeof v === "string") {
    const day = v.split("T")[0];
    if (day.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(day)) return day.slice(0, 10);
  }
  return formatDateForAPI(v) || "";
};

const createLateFeeAccrualFormDefaults = (calculus) => {
  const wf =
    calculus?.waive_fee && typeof calculus.waive_fee === "object"
      ? calculus.waive_fee
      : {};
  return {
    is_waive_late_fee: !!(
      wf.is_waive_late_fee ?? calculus?.is_waive_late_fee
    ),
    is_waive_late_fee_date: normalizeWaiveFeeDateFromApi(
      wf.is_waive_late_fee_date ?? calculus?.is_waive_late_fee_date
    ),
  };
};

const createRecordRepoFormDefaults = () => ({
  repo_effective_date: "",
  how_repo_obtained: "",
  repo_charge: formatPriceWithDollar(0),
  vehicle_location: "",
  recovered_by: "",
  recovered_at: "",
  witness_1: "",
  witness_2: "",
  address_1: "",
  address_2: "",
  city: "",
  state: null,
  zip_code: "",
  condition_notes: "",
  personal_property: false,
  notified: false,
  notified_on: "",
  days_to_claim: "0",
  claim_deadline: "",
  description: "",
});

const AccountsRightDrawer = forwardRef(function AccountsRightDrawer(
  props,
  ref
) {
  const { accountData, activeColorStyle, promiseSummary, bookingId, worksheetId, activeColor, status, isLightTheme, shellThemeVariant = "1", insurance_history, flags, permission, onAddDesiredVehicle, onRemoveDesiredVehicle, hasDesiredVehicle, onAddBorrowerFile, onAddCollateralImage, onAddCollateralFile, ...rest } = props;

  const isTheme2 = shellThemeVariant === "2";
  const theme2SelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const drawerFormFieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".accounts-right-drawer.accounts-right-drawer--theme2",
            fieldsSelector: ".accounts-right-drawer-form-fields",
          })
        : "",
    [isTheme2]
  );

  const DrawerSelect = useCallback(
    ({ styles, className = "react-select", ...selectProps }) => (
      <Select
        {...selectProps}
        className={className}
        styles={isTheme2 ? (styles ?? theme2SelectStyles) : styles}
      />
    ),
    [isTheme2, theme2SelectStyles]
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const canPostPayments = permission?.can_post_payments === true;
  const canPostExtensions = permission?.can_post_extensions === true;
  const canPostPayoff = permission?.can_post_payoff === true;
  const canPostChargeOff = permission?.can_post_charge_off === true;
  const canCreateSideNote = permission?.can_create_side_note === true;
  const canPostReversal = permission?.can_post_reversal === true;
  const isChargeOffAccount = status === "charge_off";
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [drawerActiveTab, setDrawerActiveTab] = useState("action");
  const [drawerFullSize, setDrawerFullSize] = useState(false);
  const [postSubView, setPostSubView] = useState("menu"); // 'menu' | 'payment'
  const [drawerSubView, setDrawerSubView] = useState(null); // null, 'add_desired_vehicle'
  const [selectedExtensionIdx, setSelectedExtensionIdx] = useState(null);
  const [isPayoffPosting, setIsPayoffPosting] = useState(false);
  const [payoffReceiptData, setPayoffReceiptData] = useState(null);
  const [sendView, setSendView] = useState("options"); // 'options', 'text_auth', 'email'
  const [approvalCode, setApprovalCode] = useState("");
  const [sendEmailData, setSendEmailData] = useState(null);
  const [reversalComment, setReversalComment] = useState("");
  const [selectedReversalId, setSelectedReversalId] = useState(null);
  const [lastPostedPayment, setLastPostedPayment] = useState(null);
  const [paymentReceiptOpen, setPaymentReceiptOpen] = useState(false);
  const scrollableRef = useRef(null);
  const drawerTabScrollRef = useRef(null);
  const drawerWidthDragRef = useRef(null);
  const [canScrollDrawerTabsRight, setCanScrollDrawerTabsRight] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const checkDrawerTabScroll = useCallback(() => {
    const el = drawerTabScrollRef.current;
    if (!el) {
      setCanScrollDrawerTabsRight(false);
      return;
    }
    setCanScrollDrawerTabsRight(
      el.scrollLeft < el.scrollWidth - el.clientWidth - 1
    );
  }, []);

  useEffect(() => {
    if (!drawerOpen || !isMobile) {
      setCanScrollDrawerTabsRight(false);
      return undefined;
    }
    const runCheck = () => {
      requestAnimationFrame(checkDrawerTabScroll);
    };
    runCheck();
    const scrollEl = drawerTabScrollRef.current;
    scrollEl?.addEventListener("scroll", checkDrawerTabScroll);
    window.addEventListener("resize", runCheck);
    return () => {
      scrollEl?.removeEventListener("scroll", checkDrawerTabScroll);
      window.removeEventListener("resize", runCheck);
    };
  }, [
    drawerOpen,
    isMobile,
    drawerActiveTab,
    drawerSubView,
    postSubView,
    sendView,
    checkDrawerTabScroll,
  ]);

  const handleDrawerTabScrollRight = useCallback(() => {
    if (drawerTabScrollRef.current && canScrollDrawerTabsRight) {
      drawerTabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  }, [canScrollDrawerTabsRight]);

  const [promiseRows, setPromiseRows] = useState([]);
  const [deletingPromiseId, setDeletingPromiseId] = useState(null);

  const getPromiseStatusColor = useCallback((promiseStatus) => {
    if (!promiseStatus) {
      return "#7a7a8c";
    }

    const normalizedStatus = promiseStatus.toLowerCase();

    if (normalizedStatus === "kept") {
      return "#16c784";
    }

    if (normalizedStatus === "broken") {
      return "#ff5c5c";
    }

    if (normalizedStatus === "pending") {
      return "#1890ff";
    }

    return "#7a7a8c";
  }, []);

  const getDefaultPromiseDate = useCallback(() => {
    const date = new Date();
    date.setDate(date.getDate());
    return formatDateForDisplay(date);
  }, []);

  const promiseTypeOptions = useMemo(
    () => [
      { value: "Payment", label: "Payment" },
      { value: "insurance", label: "Insurance" },
    ],
    []
  );

  const deliveryMethodOptions = useMemo(
    () => [
      { value: "Walk-In", label: "Walk-In" },
      { value: "Mail", label: "Mail" },
      { value: "Phone", label: "Phone" },
      { value: "Web", label: "Web" },
      { value: "Fax", label: "Fax" },
      { value: "Lockbox", label: "Lockbox" },
      { value: "Email", label: "Email" },
      { value: "TEXT", label: "TEXT" },
      { value: "IVR", label: "IVR" },
      { value: "Kiosk", label: "Kiosk" },
    ],
    []
  );

  const paymentFormOptions = useMemo(
    () => [
      { value: "ACH", label: "ACH" },
      { value: "Cash", label: "Cash" },
      { value: "Cashier Check", label: "Cashier Check" },
      { value: "Check", label: "Check" },
      { value: "Credit Card", label: "Credit Card" },
      { value: "Customer Credit", label: "Customer Credit" },
      { value: "Debit Card", label: "Debit Card" },
      { value: "Draft", label: "Draft" },
      { value: "FEX EPS", label: "FEX EPS" },
      { value: "Gift Card", label: "Gift Card" },
      { value: "MoneyGram", label: "MoneyGram" },
      { value: "Money Order", label: "Money Order" },
      { value: "Others", label: "Others" },
      { value: "PayNearMe", label: "PayNearMe" },
      { value: "PayPal", label: "PayPal" },
      { value: "Speed Pay", label: "Speed Pay" },
      { value: "Western Union", label: "Western Union" },
      { value: "Wire Transfer", label: "Wire Transfer" },
      { value: "Zelle", label: "Zelle" },
    ],
    []
  );

  const skipOrChargeOffOptions = useMemo(
    () => [
      { value: "Charge Off", label: "Charge Off" },
      { value: "Skip", label: "Skip" },
    ],
    []
  );

  const yesNoOptions = useMemo(
    () => [
      { value: "Yes", label: "Yes" },
      { value: "No", label: "No" },
    ],
    []
  );

  const miscFeeAdjustmentTypeOptions = useMemo(
    () => [
      { value: "Increase", label: "Increase" },
      { value: "Decrease", label: "Decrease" },
    ],
    []
  );

  const mileageStatusOptions = useMemo(
    () => [
      { label: "Actual Miles", value: "Actual Miles" },
      { label: "Not Actual Miles", value: "Not Actual Miles" },
      { label: "Exempt", value: "Exempt" },
    ],
    []
  );


  const createPaymentMutation = useCreatePayment();
  const createSideNoteMutation = useCreateSideNote();
  const createPromisePayMutation = useCreatePromisePay();
  const createWriteDownMutation = useCreateWriteDown();
  const createPayoffMutation = useCreatePayoff();
  const printPayoffMutation = usePrintPayoff();
  const sendAccountSMSMutation = useSendAccountSMS();
  const sendMailMutation = useSendMail();
  const { data: existingPromisePayments, isLoading: isPromisePaymentsLoading } =
    useGetPromisePayments(bookingId);
  const deletePromisePayMutation = useDeletePromisePay();
  const chargeOffMutation = useChargeOffCalculus();
  const chargeOffReversalMutation = useReverseChargeOffCalculus();
  const [paymentForm, setPaymentForm] = useState(() =>
    createPaymentFormDefaults()
  );
  const [commentSuggestionsOpen, setCommentSuggestionsOpen] = useState(false);
  const [commentSuggestionHighlight, setCommentSuggestionHighlight] = useState(-1);
  const [commentSuggestionVersion, setCommentSuggestionVersion] = useState(0);
  const commentBlurTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (commentBlurTimerRef.current) {
        clearTimeout(commentBlurTimerRef.current);
      }
    };
  }, []);

  const [payoffForm, setPayoffForm] = useState(() =>
    createPaymentFormDefaults()
  );
  const [pmtAppldValues, setPmtAppldValues] = useState({});
  const [lateFeeAppldValues, setLateFeeAppldValues] = useState({});
  const [waiveLateFeeValues, setWaiveLateFeeValues] = useState({});
  const [selectedPaymentPostType, setSelectedPaymentPostType] = useState("");
  const [printSubView, setPrintSubView] = useState("menu");

  useEffect(() => {
    if (!drawerOpen || !isMobile) return;
    requestAnimationFrame(checkDrawerTabScroll);
  }, [drawerOpen, isMobile, printSubView, checkDrawerTabScroll]);

  const [chargeOffForm, setChargeOffForm] = useState({
    skip_or_charge_off: "Charge Off",
    add_to_inventory: "Yes",
    sales_location: "[No Change]",
    mileage: "",
    mileage_status: "Actual Miles",
    collateral_acv: formatPriceWithDollar(0),
    strict_foreclosure: "No",
    post_recovery: formatPriceWithDollar(0),
    release_customer: "No",
    comments: ""
  });
  const [chargeOffReversalComments, setChargeOffReversalComments] = useState("");

  const handleChargeOffInputChange = (field, value) => {
    setChargeOffForm(prev => ({ ...prev, [field]: value }));
  };
  const [viewerSource, setViewerSource] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);

  const resetPrintDrawerState = useCallback(() => {
    setPrintSubView("menu");
    setViewerSource(null);
    setSelectedDocuments([]);
    setDrawerFullSize(false);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    resetPrintDrawerState();
  }, [resetPrintDrawerState]);

  const printCalculusMutation = usePrintCalculus();
  const { data: documentsData, isLoading: isDocumentsLoading } = useGetDocuments("accounts");
  const shouldLoadRecordReports = drawerOpen && drawerActiveTab === "print" && printSubView === "reports";
  const { data: recordReportsData, isLoading: isRecordReportsLoading } = useGetRecordSpecificReports(
    "account",
    shouldLoadRecordReports
  );

  const accountRecordReports = useMemo(() => {
    const reports = (recordReportsData?.results || []).slice();
    reports.sort((a, b) => {
      const aPinned = PINNED_ACCOUNT_RECORD_REPORT_VALUES.has(a.value);
      const bPinned = PINNED_ACCOUNT_RECORD_REPORT_VALUES.has(b.value);
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
    return reports;
  }, [recordReportsData?.results]);

  /* useEffect(() => {
    if (documentsData?.data) {
      const allIds = documentsData.data.map((d) => d.id);
      setSelectedDocuments(allIds);
    }
  }, [documentsData]); */

  // Cleanup PDF URL when drawer closes or component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const filteredPaymentFormOptions = useMemo(() => {
    const method = payoffForm.delivery_method;
    const alwaysAllowed = (opt) => opt.value === "ACH" || opt.value === "Zelle";

    if (method === "Walk-In") {
      // Walk-In list: Cash, Cashier Check, Check, Credit Card, Customer Credit, Debit Card, Draft, FEX EPS, Gift Card, Money Order, Others, ACH, Zelle
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || (opt.value !== "Speed Pay" && opt.value !== "Wire Transfer"));
    }
    if (method === "Mail") {
      // Mail list: ACH, Cashier Check, Check, Credit Card, Debit Card, Draft, Money Order, Others, Zelle
      const mailOptions = ["ACH", "Cashier Check", "Check", "Credit Card", "Debit Card", "Draft", "Money Order", "Others", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || mailOptions.includes(opt.value));
    }
    if (method === "Phone") {
      // Phone list: ACH, Check, Credit Card, Customer Credit, Debit Card, FEX EPS, Others, Speed Pay, Wire Transfer, Zelle
      const phoneOptions = ["ACH", "Check", "Credit Card", "Customer Credit", "Debit Card", "FEX EPS", "Others", "Speed Pay", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || phoneOptions.includes(opt.value));
    }
    if (method === "Web") {
      // Web list: ACH, Credit Card, Debit Card, MoneyGram, Others, PayNearMe, PayPal, Speed Pay, Western Union, Zelle
      const webOptions = ["ACH", "Credit Card", "Debit Card", "MoneyGram", "Others", "PayNearMe", "PayPal", "Speed Pay", "Western Union", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || webOptions.includes(opt.value));
    }
    if (method === "Fax") {
      // Fax list: ACH, Credit Card, Debit Card, Draft, Others, Wire Transfer, Zelle
      const faxOptions = ["ACH", "Credit Card", "Debit Card", "Draft", "Others", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || faxOptions.includes(opt.value));
    }
    if (method === "Lockbox") {
      // Lockbox list: Cash, Cashier Check, Check, Draft, Money Order, Others, ACH, Zelle
      const lockboxOptions = ["ACH", "Cash", "Cashier Check", "Check", "Draft", "Money Order", "Others", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || lockboxOptions.includes(opt.value));
    }
    if (method === "Email") {
      // Email list: ACH, Credit Card, Debit Card, Draft, Others, Wire Transfer, Zelle
      const emailOptions = ["ACH", "Credit Card", "Debit Card", "Draft", "Others", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || emailOptions.includes(opt.value));
    }
    if (method === "TEXT") {
      // TEXT list: ACH, Credit Card, Debit Card, Draft, FEX EPS, Others, Wire Transfer, Zelle
      const textOptions = ["ACH", "Credit Card", "Debit Card", "Draft", "FEX EPS", "Others", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || textOptions.includes(opt.value));
    }
    if (method === "IVR") {
      // IVR list: ACH, Credit Card, Debit Card, FEX EPS, Others, Zelle
      const ivrOptions = ["ACH", "Credit Card", "Debit Card", "FEX EPS", "Others", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || ivrOptions.includes(opt.value));
    }
    if (method === "Kiosk") {
      // Kiosk list: Cash, FEX EPS, Others, ACH, Zelle
      const kioskOptions = ["ACH", "Cash", "FEX EPS", "Others", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || kioskOptions.includes(opt.value));
    }
    return paymentFormOptions;
  }, [payoffForm.delivery_method, paymentFormOptions]);

  const filteredPostPaymentOptions = useMemo(() => {
    const method = paymentForm.delivery_method;
    const alwaysAllowed = (opt) => opt.value === "ACH" || opt.value === "Zelle";

    if (method === "Walk-In") {
      const walkInOptions = ["ACH", "Cash", "Cashier Check", "Check", "Credit Card", "Customer Credit", "Debit Card", "Draft", "FEX EPS", "Gift Card", "Money Order", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || walkInOptions.includes(opt.value));
    }
    if (method === "Mail") {
      const mailOptions = ["ACH", "Cashier Check", "Check", "Credit Card", "Debit Card", "Draft", "Money Order", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || mailOptions.includes(opt.value));
    }
    if (method === "Phone") {
      const phoneOptions = ["ACH", "Check", "Credit Card", "Customer Credit", "Debit Card", "FEX EPS", "Speed Pay", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || phoneOptions.includes(opt.value));
    }
    if (method === "Web") {
      const webOptions = ["ACH", "Credit Card", "Debit Card", "FEX EPS", "MoneyGram", "PayNearMe", "PayPal", "Speed Pay", "Western Union", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || webOptions.includes(opt.value));
    }
    if (method === "Fax") {
      const faxOptions = ["ACH", "Credit Card", "Debit Card", "Draft", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || faxOptions.includes(opt.value));
    }
    if (method === "Lockbox") {
      const lockboxOptions = ["ACH", "Cash", "Cashier Check", "Check", "Draft", "Money Order", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || lockboxOptions.includes(opt.value));
    }
    if (method === "Email") {
      const emailOptions = ["ACH", "Credit Card", "Debit Card", "Draft", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || emailOptions.includes(opt.value));
    }
    if (method === "TEXT") {
      const textOptions = ["ACH", "Credit Card", "Debit Card", "Draft", "FEX EPS", "Wire Transfer", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || textOptions.includes(opt.value));
    }
    if (method === "IVR") {
      const ivrOptions = ["ACH", "Credit Card", "Debit Card", "FEX EPS", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || ivrOptions.includes(opt.value));
    }
    if (method === "Kiosk") {
      const kioskOptions = ["ACH", "Cash", "FEX EPS", "Zelle"];
      return paymentFormOptions.filter(opt => alwaysAllowed(opt) || kioskOptions.includes(opt.value));
    }
    return paymentFormOptions;
  }, [paymentForm.delivery_method, paymentFormOptions]);

  const [showInformationName, setShowInformationName] = useState("");

  useEffect(() => {
    if (!drawerOpen || !isMobile) return;
    requestAnimationFrame(checkDrawerTabScroll);
  }, [drawerOpen, isMobile, showInformationName, checkDrawerTabScroll]);

  const [insuranceForm, setInsuranceForm] = useState({
    action: "update",
    insurance_company: "",
    insurance_company_name: "",
    insurance_policy_number: "",
    insurance_company_phone: "",
    insurance_agent: "",
    insurance_agent_name: "",
    insurance_agent_policy_number: "",
    insurance_agent_phone: "",
    effective_date: "",
    expire_date: "",
    collision_deductible: "",
    comprehensive_deductible: "",
  });

  const [repoForm, setRepoForm] = useState({
    agent: null,
    reason: "",
    notes: "",
  });

  // Cancel Repo form state
  const [cancelRepoForm, setCancelRepoForm] = useState({
    notes: "",
  });

  const [redeemRepoForm, setRedeemRepoForm] = useState({
    notes: "",
  });

  const [recordRepoForm, setRecordRepoForm] = useState(() =>
    createRecordRepoFormDefaults()
  );

  const [sideNoteForm, setSideNoteForm] = useState(() =>
    createSideNoteFormDefaults()
  );

  const [miscFeeAdjustmentForm, setMiscFeeAdjustmentForm] = useState(() =>
    createMiscFeeAdjustmentFormDefaults()
  );

  const [lateFeeAccrualForm, setLateFeeAccrualForm] = useState(() =>
    createLateFeeAccrualFormDefaults()
  );

  const [showErrors, setShowErrors] = useState(false);

  const [cpiSchedule, setCpiSchedule] = useState([
    { id: 1, count: "0", amount: "", frequency: "Weekly", firstDate: formatDateForAPI(moment().add(7, 'days')), calculated: "" },
    { id: 2, count: "0", amount: "", frequency: "Weekly", firstDate: "", calculated: "" },
    { id: 3, count: "0", amount: "", frequency: "Weekly", firstDate: "", calculated: "" },
  ]);

  const [writeDownForm, setWriteDownForm] = useState({
    balance_type: "All Balances",
    comments: "",
  });

  const [alertForm, setAlertForm] = useState({
    useDefaultStyle: false,
    content: "",
    font_color: "",
    background_color: "",
  });

  const fontColorRef = useRef(null);
  const backgroundColorRef = useRef(null);

  const handleCpiScheduleChange = useCallback((id, field, value) => {
    setCpiSchedule((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  }, []);

  const handleCalculate = useCallback(() => {
    const loanAmount = toNumber(sideNoteForm.amount);
    if (!sideNoteForm.description || loanAmount <= 0) {
      setShowErrors(true);
      return;
    }

    setShowErrors(false);

    // Identify active rows (rows with valid amount)
    const activeRowsIndices = [];
    cpiSchedule.forEach((row, index) => {
      if (toNumber(row.amount) > 0) {
        activeRowsIndices.push(index);
      }
    });

    if (activeRowsIndices.length === 0) {
      return;
    }

    let remainingBalance = loanAmount;

    const newSchedule = cpiSchedule.map((row) => ({ ...row }));

    activeRowsIndices.forEach((rowIndex, i) => {
      const row = newSchedule[rowIndex];
      const amount = toNumber(row.amount);
      const isLastActive = i === activeRowsIndices.length - 1;

      if (!isLastActive) {
        // Treat as fixed
        const count = parseInt(row.count || "0");
        const deduction = count * amount;
        remainingBalance -= deduction;
        remainingBalance = parseFloat(remainingBalance.toFixed(2));

        // Ensure calculated amount matches format
        row.calculated = `$${amount.toFixed(2)}`;
      } else {
        // Solve for this row
        if (remainingBalance <= 0) {
          row.count = "0";
          row.calculated = `$${amount.toFixed(2)}`;
        } else {
          const count = Math.ceil(remainingBalance / amount);
          row.count = count.toString();
          row.calculated = `$${amount.toFixed(2)}`;
          // logic consumes the rest
          remainingBalance = 0;
        }
      }
    });

    setCpiSchedule(newSchedule);
  }, [sideNoteForm.amount, sideNoteForm.description, cpiSchedule]);

  const [companySearch, setCompanySearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");

  const { data: companies, isLoading: isCompaniesLoading } = useGetChannels("insurance_company", 1, 100, companySearch);
  const { data: agents, isLoading: isAgentsLoading } = useGetChannels("insurance_agent", 1, 100, agentSearch);
  const { data: repoAgents } = useGetChannels("repo_agent");
  const { data: salesLocations } = useGetChannels("sales_location_branch");
  const updateCalculusMutation = useUpdateCalculus();
  const { showNotification } = useNotification();

  const recordRepoYesNoOptions = useMemo(
    () => [
      { value: false, label: "No" },
      { value: true, label: "Yes" },
    ],
    []
  );

  const recordRepoHowObtainedOptions = useMemo(
    () => [
      { value: "Voluntary Repo", label: "Voluntary Repo" },
      { value: "Involuntary Repo", label: "Involuntary Repo" },
    ],
    []
  );

  const recordRepoLocationSelectOptions = useMemo(
    () =>
      repoAgents?.results?.map((agent) => ({
        value: agent.id,
        label: agent.name,
      })) || [],
    [repoAgents?.results]
  );

  const recordRepoRecoveredAtOptions = useMemo(
    () => [
      {
        value: "Customer's Current Address",
        label: "Customer's Current Address",
      },
      {
        value: "Customer's Employment Address",
        label: "Customer's Employment Address",
      },
      { value: "Other", label: "Other" },
    ],
    []
  );

  const recordRepoAgentSelectOptions = useMemo(
    () =>
      repoAgents?.results?.map((agent) => ({
        value: agent.id,
        label: agent.name,
      })) || [],
    [repoAgents?.results]
  );
  const { data: calculusData } = useGetCalculusDetail(bookingId);
  const { data: transactionsData, isLoading: isTransactionsLoading } = useGetCalculusDetail(bookingId, "TRANSACTIONS");

  const remainingPaymentsList = useMemo(
    () =>
      Array.isArray(calculusData?.remaining_payments)
        ? calculusData.remaining_payments
        : [],
    [calculusData?.remaining_payments]
  );

  const postPaymentTableRows = useMemo(() => {
    const rows = remainingPaymentsList.map((item, originalIdx) => ({
      item,
      originalIdx,
    }));
    if (!selectedPaymentPostType) return rows;
    return rows.filter(
      ({ item }) => getPaymentPostTypeCode(item) === selectedPaymentPostType
    );
  }, [remainingPaymentsList, selectedPaymentPostType]);

  const miscFeeIncreaseCap = useMemo(() => {
    const raw = calculusData?.balances?.current_misc_fees;
    const n = typeof raw === "number" ? raw : parseFloat(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [calculusData?.balances?.current_misc_fees]);

  const isMiscFeeDecreaseOverCap = useMemo(() => {
    if (miscFeeAdjustmentForm.adjustment_type !== "Decrease") return false;
    return toNumber(miscFeeAdjustmentForm.amount) > miscFeeIncreaseCap;
  }, [
    miscFeeAdjustmentForm.adjustment_type,
    miscFeeAdjustmentForm.amount,
    miscFeeIncreaseCap,
  ]);

  // Amount değişince önce late fee, sonra ana ödeme olarak sırayla dağıt
  useEffect(() => {
    const amount = toNumber(paymentForm.amount);
    if (amount <= 0) {
      setPmtAppldValues({});
      setLateFeeAppldValues({});
      return;
    }

    const rowsToApply = remainingPaymentsList
      .map((item, originalIdx) => ({ item, originalIdx }))
      .filter(
        ({ item }) =>
          !selectedPaymentPostType ||
          getPaymentPostTypeCode(item) === selectedPaymentPostType
      );

    let remaining = amount;
    const newLateFeeAppldValues = {};
    const newPmtAppldValues = {};
    let lastProcessedIdx = null;

    for (const { item, originalIdx } of rowsToApply) {
      if (remaining <= 0) break;

      const isWaived = !!waiveLateFeeValues[originalIdx];
      const schedAmt = getSchedulePaymentAmount(item);
      const remainingLateFee = isWaived ? 0 : getRemainingLateFeeDue(item);

      if (remainingLateFee > 0) {
        const lateFeeToApply = Math.min(remaining, remainingLateFee);
        newLateFeeAppldValues[originalIdx] = formatPriceWithDollar(lateFeeToApply);
        remaining -= lateFeeToApply;
        lastProcessedIdx = originalIdx;
      }

      if (remaining > 0 && schedAmt > 0) {
        lastProcessedIdx = originalIdx;
        if (remaining >= schedAmt) {
          newPmtAppldValues[originalIdx] = formatPriceWithDollar(schedAmt);
          remaining -= schedAmt;
        } else {
          newPmtAppldValues[originalIdx] = formatPriceWithDollar(remaining);
          remaining = 0;
        }
      }
    }

    if (remaining > 0) {
      if (lastProcessedIdx !== null) {
        const currentPmtVal = parseAppldInputValue(newPmtAppldValues[lastProcessedIdx]);
        newPmtAppldValues[lastProcessedIdx] = formatPriceWithDollar(currentPmtVal + remaining);
      } else if (rowsToApply.length > 0) {
        const { originalIdx } = rowsToApply[0];
        newPmtAppldValues[originalIdx] = formatPriceWithDollar(remaining);
      } else if (remainingPaymentsList.length > 0 && !selectedPaymentPostType) {
        newPmtAppldValues[0] = formatPriceWithDollar(amount);
      }
    }

    setLateFeeAppldValues(newLateFeeAppldValues);
    setPmtAppldValues(newPmtAppldValues);
  }, [paymentForm.amount, remainingPaymentsList, selectedPaymentPostType, waiveLateFeeValues]);

  useEffect(() => {
    if (showInformationName === 'update-insurance') {
      if (insurance_history && insurance_history.length > 0) {
        const latest = insurance_history[0];
        setInsuranceForm(prev => ({
          ...prev,
          action: 'update',
          insurance_company: latest.insurance_company || "",
          insurance_company_name: latest.insurance_company_name || "",
          insurance_policy_number: latest.insurance_policy_number || "",
          insurance_company_phone: latest.insurance_company_phone || "",
          insurance_agent: latest.insurance_agent || "",
          insurance_agent_name: latest.insurance_agent_name || "",
          insurance_agent_policy_number: latest.insurance_agent_policy_number || "",
          insurance_agent_phone: latest.insurance_agent_phone || "",
          effective_date: latest.effective_date || "",
          expire_date: latest.expire_date || "",
          collision_deductible: formatPriceWithDollar(latest.collision_deductible || 0),
          comprehensive_deductible: formatPriceWithDollar(latest.comprehensive_deductible || 0),
        }));
      } else {
        setInsuranceForm(prev => ({
          ...prev,
          action: 'new',
          insurance_company: "",
          insurance_company_name: "",
          insurance_policy_number: "",
          insurance_company_phone: "",
          insurance_agent: "",
          insurance_agent_name: "",
          insurance_agent_policy_number: "",
          insurance_agent_phone: "",
          effective_date: "",
          expire_date: "",
          collision_deductible: "",
          comprehensive_deductible: "",
        }));
      }
    } else if (showInformationName === "record-repo") {
      const repossession = accountData?.repossession;
      if (repossession) {
        const repossessionStateId = Number(repossession.state);
        setRecordRepoForm({
          ...createRecordRepoFormDefaults(),
          repo_effective_date: repossession.effective_date || "",
          how_repo_obtained: repossession.was_obtained || "",
          repo_charge: formatPriceWithDollar(repossession.repo_charge || 0),
          vehicle_location:
            repossession.vehicle_location?.id ||
            repossession.vehicle_location ||
            "",
          recovered_by:
            repossession.recovered_repo?.id ||
            repossession.recovered_repo ||
            "",
          recovered_at: repossession.recovered_at ?? "",
          address_1: repossession.address_1 || "",
          address_2: repossession.address_2 || "",
          city: repossession.city || "",
          state:
            Number.isFinite(repossessionStateId) && repossessionStateId > 0
              ? {
                id: repossessionStateId,
                abbreviation: repossession.state_name || "",
              }
              : null,
          zip_code: repossession.zip_code || "",
          condition_notes: repossession.notes || "",
        });
      } else {
        setRecordRepoForm(createRecordRepoFormDefaults());
      }
    } else if (showInformationName === "redeem-repo") {
      setRedeemRepoForm({ notes: "" });
    } else if (showInformationName === 'update-alert' && calculusData) {
      setAlertForm({
        useDefaultStyle: calculusData.alert_default_style || false,
        content: calculusData.alert_message || "",
        font_color: calculusData.alert_font_color || "",
        background_color: calculusData.alert_background_color || "",
      });
    }
  }, [showInformationName, insurance_history, calculusData, accountData?.repossession]);

  useEffect(() => {
    if (showInformationName !== "record-repo") return;

    const recoveredAt = recordRepoForm.recovered_at;
    const customer = accountData?.customer;
    if (!customer) return;

    const firstNonEmpty = (...values) =>
      values.find(
        (v) =>
          v !== undefined &&
          v !== null &&
          !(typeof v === "string" && v.trim() === "")
      ) || "";

    if (recoveredAt === "Customer's Current Address") {
      const currentAddress = customer.current_address || {};
      const currentAddress1 = firstNonEmpty(
        currentAddress.address_1,
        customer.address_1
      );
      const currentAddress2 = firstNonEmpty(
        currentAddress.address_2,
        customer.address_2
      );
      const currentCity = firstNonEmpty(currentAddress.city, customer.city);
      const currentZip = firstNonEmpty(
        currentAddress.zip_code,
        currentAddress.zip,
        customer.zip_code,
        customer.zip
      );
      const currentStateRaw = firstNonEmpty(currentAddress.state, customer.state);
      const currentStateName = firstNonEmpty(
        currentAddress.state_name,
        customer.state_name
      );
      const stateId = Number(currentStateRaw);

      setRecordRepoForm((prev) => ({
        ...prev,
        address_1: currentAddress1,
        address_2: currentAddress2,
        city: currentCity,
        zip_code: currentZip,
        state:
          Number.isFinite(stateId) && stateId > 0
            ? {
              id: stateId,
              abbreviation: currentStateName,
            }
            : null,
      }));
      return;
    }

    if (recoveredAt === "Customer's Employment Address") {
      const currentEmployment = customer.current_employment || {};
      const employmentAddress1 =
        customer.employment_address_1 || currentEmployment.address_1 || "";
      const employmentAddress2 =
        customer.employment_address_2 || currentEmployment.address_2 || "";
      const employmentCity =
        customer.employment_city || currentEmployment.city || "";
      const employmentZip =
        customer.employment_zip_code ||
        customer.employment_zip ||
        currentEmployment.zip_code ||
        currentEmployment.zip ||
        "";
      const employmentState =
        customer.employment_state || currentEmployment.state;
      const employmentStateId = Number(employmentState);

      setRecordRepoForm((prev) => ({
        ...prev,
        address_1: employmentAddress1,
        address_2: employmentAddress2,
        city: employmentCity,
        zip_code: employmentZip,
        state:
          Number.isFinite(employmentStateId) && employmentStateId > 0
            ? {
              id: employmentStateId,
              abbreviation: "",
            }
            : null,
      }));
    }
  }, [
    showInformationName,
    recordRepoForm.recovered_at,
    accountData?.customer,
  ]);

  // Set initial balance_type when calculusData loads
  useEffect(() => {
    if (calculusData?.payment_select_type && calculusData.payment_select_type.length > 0) {
      setWriteDownForm(prev => ({
        ...prev,
        balance_type: calculusData.payment_select_type[0].value,
      }));

      const allBalancesOption = calculusData.payment_select_type.find(item => item.value === "All Balances");
      setPayoffForm(prev => ({
        ...prev,
        balance_type: allBalancesOption ? "All Balances" : calculusData.payment_select_type[0].value,
      }));
    }
  }, [calculusData?.payment_select_type]);

  useImperativeHandle(
    ref,
    () => ({
      open: (type, initialData = null) => {
        setIsDrawerVisible(true);
        setSendEmailData(initialData);

        // Reset all forms and states to ensure fresh start
        setPaymentForm(createPaymentFormDefaults());
        setPmtAppldValues({});
        setLateFeeAppldValues({});
        setWaiveLateFeeValues({});
        setSelectedPaymentPostType("");
        let initialPayoffForm = createPaymentFormDefaults();
        if (calculusData?.payment_select_type && calculusData.payment_select_type.length > 0) {
          const allBalancesOption = calculusData.payment_select_type.find(item => item.value === "All Balances");
          initialPayoffForm.balance_type = allBalancesOption ? "All Balances" : calculusData.payment_select_type[0].value;
        }
        setPayoffForm(initialPayoffForm);
        setSideNoteForm(createSideNoteFormDefaults());
        setSideNoteForm(createSideNoteFormDefaults());
        setMiscFeeAdjustmentForm(createMiscFeeAdjustmentFormDefaults());
        setLateFeeAccrualForm(createLateFeeAccrualFormDefaults());
        setShowInformationName("");
        setRepoForm({ agent: null, reason: "", notes: "" });
        setCancelRepoForm({ notes: "" });
        setRedeemRepoForm({ notes: "" });
        setRecordRepoForm(createRecordRepoFormDefaults());
        setSelectedExtensionIdx(null);
        setIsPayoffPosting(false);
        setPayoffReceiptData(null);
        setViewerSource(null);
        setReversalComment("");
        setSelectedReversalId(null);
        resetPrintDrawerState();

        // Default tab selection logic
        setTimeout(() => setDrawerOpen(true), 0);
        if (type === "promise") setDrawerActiveTab("promiseToPay");
        else if (type === "payment") {
          setDrawerActiveTab("post");
          setPostSubView("menu");
        } else if (type === "notes") setDrawerActiveTab("notes");
        else if (type === "more") setDrawerActiveTab("more");
        else if (type === "flags") setDrawerActiveTab("flags");
        else if (type === "print") setDrawerActiveTab("print");
        else if (type === "send") {
          setDrawerActiveTab("send");
          if (initialData) {
            setSendView("email");
          } else {
            setSendView("options");
          }
        }
        else setDrawerActiveTab("action");
      },
      close: () => {
        closeDrawer();
      },
    }),
    [calculusData, resetPrintDrawerState, closeDrawer]
  );

  const endDrawerWidthResizeInteraction = useCallback((clientX) => {
    const drag = drawerWidthDragRef.current;
    if (!drag) return;
    drawerWidthDragRef.current = null;
    const dx = clientX - drag.startX;
    if (Math.abs(dx) < 14) {
      setDrawerFullSize(!drag.startFull);
    } else if (dx < -42) {
      setDrawerFullSize(true);
    } else if (dx > 42) {
      setDrawerFullSize(false);
    }
  }, []);

  const onDrawerWidthHandlePointerDown = useCallback(
    (e) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      if (typeof el.setPointerCapture === "function") {
        el.setPointerCapture(e.pointerId);
      }
      drawerWidthDragRef.current = {
        startX: e.clientX,
        startFull: drawerFullSize,
      };
      const onMove = () => {};
      const onUp = (ev) => {
        endDrawerWidthResizeInteraction(ev.clientX);
        try {
          if (typeof el.releasePointerCapture === "function") {
            el.releasePointerCapture(ev.pointerId);
          }
        } catch (_) {
          /* ignore */
        }
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
      };
      el.addEventListener("pointermove", onMove, { passive: true });
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    },
    [isMobile, drawerFullSize, endDrawerWidthResizeInteraction]
  );

  const drawerStyles = useMemo(
    () => ({
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 2000,
        opacity: 0,
        transition: "opacity 300ms ease",
      },
      panel: {
        position: "fixed",
        top: 0,
        right: 0,
        width: isMobile ? "100%" : (drawerFullSize ? "85vw" : "45vw"),
        maxWidth: isMobile ? "100%" : "90vw",
        height: "100vh",
        backgroundColor: isLightTheme ? "#fff" : "#1e1e2f",
        color: isLightTheme ? "#1e1e2f" : "#fff",
        boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
        zIndex: 2001,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        transform: "translateX(100%)",
        transition: "transform 300ms ease, width 220ms ease, max-width 220ms ease",
      },
      drawerResizeHandle: {
        width: 18,
        flexShrink: 0,
        cursor: "ew-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        borderRight: isLightTheme
          ? "1px solid rgba(0,0,0,0.08)"
          : "1px solid rgba(255,255,255,0.12)",
        backgroundColor: isLightTheme
          ? "rgba(0,0,0,0.03)"
          : "rgba(255,255,255,0.04)",
      },
      mainColumn: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      },
      header: {
        padding: isMobile ? "8px 12px" : "0px 16px",
        flexShrink: 0,
        borderBottom: isLightTheme
          ? "1px solid rgba(0,0,0,0.1)"
          : "1px solid  rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: isMobile ? 8 : 0,
        minWidth: 0,
      },
      headerTabsScroll: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
      },
      body: {
        padding: "8px 16px 16px 16px",
        overflow: "auto",
        flex: 1,
        minHeight: 0,
      },
      closeBtn: {
        ...activeColorStyle,
        color: "white",
        border: 0,
        padding: "6px 12px",
        fontWeight: 600,
        cursor: "pointer",
      },
      closeBtnMobile: {
        flexShrink: 0,
        alignSelf: "center",
        background: "transparent",
        border: 0,
        padding: "6px 8px",
        marginRight: 4,
        cursor: "pointer",
        color: isLightTheme ? "#1e1e2f" : "#fff",
        lineHeight: 1,
      },
      tabScrollNextBtn: {
        flexShrink: 0,
        alignSelf: "center",
        background: "transparent",
        border: 0,
        padding: "4px 6px",
        cursor: "pointer",
        color: activeColorStyle.borderColor,
        lineHeight: 1,
      },
    }),
    [activeColorStyle, drawerFullSize, isLightTheme, isMobile]
  );

  const handleAddPromiseRow = useCallback(() => {
    const newRow = {
      id: Date.now(),
      promiseType: "Payment",
      date: getDefaultPromiseDate(),
      balanceType: "all",
      amount: "",
      deliveryMethod: "Phone",
      paymentForm: "ACH",
      account: "",
    };
    setPromiseRows((prev) => [...prev, newRow]);
  }, [getDefaultPromiseDate]);

  const handlePromiseFieldChange = useCallback((rowId, field, value) => {
    setPromiseRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
    );
  }, []);

  const promisePayments = existingPromisePayments?.results || [];

  const handleRemovePromiseRow = useCallback((rowId) => {
    setPromiseRows((prev) => prev.filter((r) => r.id !== rowId));
  }, []);

  const handleDeletePromise = useCallback(
    (promiseId) => {
      if (!promiseId) return;
      setDeletingPromiseId(promiseId);
      deletePromisePayMutation.mutate(promiseId, {
        onSettled: () => setDeletingPromiseId(null),
      });
    },
    [deletePromisePayMutation]
  );

  const isSavePromisesDisabled = promiseRows.some(
    (row) => !row.amount || toNumber(row.amount) <= 0
  );

  const filteredCommentSuggestions = useMemo(
    () =>
      filterPaymentCommentSuggestions(
        getPaymentCommentSuggestions(),
        paymentForm.note
      ),
    [paymentForm.note, commentSuggestionVersion]
  );

  const openCommentSuggestions = useCallback(() => {
    if (commentBlurTimerRef.current) {
      clearTimeout(commentBlurTimerRef.current);
      commentBlurTimerRef.current = null;
    }
    setCommentSuggestionsOpen(true);
    setCommentSuggestionHighlight(-1);
  }, []);

  const closeCommentSuggestions = useCallback(() => {
    setCommentSuggestionsOpen(false);
    setCommentSuggestionHighlight(-1);
  }, []);

  const selectCommentSuggestion = useCallback(
    (text) => {
      setPaymentForm((prev) => ({ ...prev, note: text }));
      closeCommentSuggestions();
    },
    [closeCommentSuggestions]
  );

  const handleCommentSuggestionKeyDown = useCallback(
    (e) => {
      if (!commentSuggestionsOpen || filteredCommentSuggestions.length === 0) {
        if (e.key === "Escape") closeCommentSuggestions();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCommentSuggestionHighlight((prev) =>
          prev < filteredCommentSuggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCommentSuggestionHighlight((prev) =>
          prev > 0 ? prev - 1 : filteredCommentSuggestions.length - 1
        );
      } else if (e.key === "Enter" && commentSuggestionHighlight >= 0) {
        e.preventDefault();
        selectCommentSuggestion(
          filteredCommentSuggestions[commentSuggestionHighlight]
        );
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeCommentSuggestions();
      }
    },
    [
      commentSuggestionsOpen,
      filteredCommentSuggestions,
      commentSuggestionHighlight,
      selectCommentSuggestion,
      closeCommentSuggestions,
    ]
  );

  const handlePaymentInputChange = useCallback((field, value) => {
    setPaymentForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "amount") {
        const amountValue = toNumber(value);
        if (
          !prev.amount_received ||
          toNumber(prev.amount_received) < amountValue
        ) {
          next.amount_received = formatPriceWithDollar(amountValue);
        }
        return next;
      }

      if (field === "amount_received") {
        const amountValue = toNumber(next.amount);
        const receivedValue = toNumber(value);
        next.amount_received =
          receivedValue < amountValue
            ? formatPriceWithDollar(amountValue)
            : value;
        return next;
      }

      if (field === "date" && !value) {
        next.date = "";
      }

      return next;
    });
  }, []);

  const handlePayoffInputChange = useCallback((field, value) => {
    setPayoffForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "date" && !value) {
        next.date = "";
      }

      return next;
    });
  }, []);

  const handleSubmitPayment = useCallback(() => {
    const applied_payments = [];
    const rowsToSubmit = selectedPaymentPostType
      ? postPaymentTableRows
      : remainingPaymentsList.map((item, originalIdx) => ({ item, originalIdx }));

    rowsToSubmit.forEach(({ item, originalIdx }) => {
      const isWaiveLateFee = !!waiveLateFeeValues[originalIdx];
      const enteredLateFeeAppld = getEnteredLateFeeAppld(
        originalIdx,
        item,
        lateFeeAppldValues,
        waiveLateFeeValues
      );
      const enteredPmtAppld = getEnteredPmtAppld(originalIdx, item, pmtAppldValues);
      const totalApplied = enteredLateFeeAppld + enteredPmtAppld;

      if (totalApplied > 0 || isWaiveLateFee) {
        applied_payments.push({
          ...item,
          payment_post_type: getPaymentPostTypeCode(item),
          payment_applied: totalApplied,
          late_fee_applied: enteredLateFeeAppld,
          waive_late_fee: isWaiveLateFee,
        });
      }
    });

    const payload = {
      booking_id: bookingId,
      date: paymentForm.date ? moment(parseDateFromAPI(paymentForm.date)).set({ hour: moment().hour(), minute: moment().minute(), second: moment().second() }).toISOString() : null,
      effect_date: paymentForm.effect_date ? moment(parseDateFromAPI(paymentForm.effect_date)).set({ hour: moment().hour(), minute: moment().minute(), second: moment().second() }).toISOString() : null,
      amount: toNumber(paymentForm.amount),
      collected: toNumber(paymentForm.amount),
      delivery_method: paymentForm.delivery_method || null,
      payment_form: paymentForm.payment_form || null,
      amount_received: toNumber(paymentForm.amount_received),
      ref_number: paymentForm.ref_number || null,
      note: paymentForm.note || "",
      payment_type: "payment",
      payment_post_type: selectedPaymentPostType || null,
      description: "Payment",
      applied_payments: applied_payments,
    };
    createPaymentMutation.mutate(payload, {
      onSuccess: (response) => {
        if (paymentForm.note?.trim()) {
          addPaymentCommentSuggestion(paymentForm.note);
          setCommentSuggestionVersion((v) => v + 1);
        }
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId],
        });
        setLastPostedPayment(response?.data || response || null);
        setPostSubView("payment-success");
        setPmtAppldValues({});
        setLateFeeAppldValues({});
        setWaiveLateFeeValues({});
        setSelectedPaymentPostType("");
      },
    });
  }, [
    createPaymentMutation,
    paymentForm,
    bookingId,
    queryClient,
    pmtAppldValues,
    lateFeeAppldValues,
    waiveLateFeeValues,
    selectedPaymentPostType,
    postPaymentTableRows,
    remainingPaymentsList,
  ]);

  const handleSubmitExtension = useCallback(() => {
    if (selectedExtensionIdx === null) return;
    const remainingPayments = Array.isArray(calculusData?.remaining_payments) ? calculusData.remaining_payments : [];
    const item = remainingPayments[selectedExtensionIdx];
    if (!item) return;

    const schedAmt = getSchedulePaymentAmount(item);
    const schedLateFee = getRemainingLateFeeDue(item);
    const schedTotal = getScheduleTotalDue(item);

    const payload = {
      booking_id: bookingId,
      date: new Date().toISOString(),
      amount: parseFloat(schedTotal),
      collected: parseFloat(schedTotal),
      delivery_method: null,
      payment_form: null,
      amount_received: parseFloat(schedTotal),
      ref_number: null,
      note: "Extension",
      payment_type: "extension",
      description: "Extension",
      applied_payments: [{
        ...item,
        payment_applied: parseFloat(schedTotal)
      }],
    };

    createPaymentMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId],
        });
        closeDrawer();
        setPostSubView("menu");
        setSelectedExtensionIdx(null);
      },
    });
  }, [selectedExtensionIdx, calculusData, bookingId, createPaymentMutation, queryClient, closeDrawer]);

  const handleSubmitReversal = useCallback(() => {
    if (!selectedReversalId) return;

    const transactionsList = Array.isArray(transactionsData?.payments) ? transactionsData.payments : [];
    const transaction = transactionsList.find(t => (t.id || t.documentId) === selectedReversalId);
    if (!transaction) return;

    const payload = {
      booking_id: bookingId,
      payment_type: "reversal",
      id: transaction.receipt_id || (transaction.id || transaction.documentId),
      note: reversalComment,
      date: new Date().toISOString(),
    };

    createPaymentMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId],
        });
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId, "TRANSACTIONS"],
        });
        closeDrawer();
        setPostSubView("menu");
        setSelectedReversalId(null);
        setReversalComment("");
      },
    });
  }, [selectedReversalId, transactionsData, bookingId, reversalComment, createPaymentMutation, queryClient, closeDrawer]);

  const handleSubmitMiscFeeAdjustment = useCallback(() => {
    const amount = toNumber(miscFeeAdjustmentForm.amount);
    if (!miscFeeAdjustmentForm.adjustment_type || amount === 0) return;

    const capRaw = calculusData?.balances?.current_misc_fees;
    const cap =
      typeof capRaw === "number"
        ? capRaw
        : parseFloat(capRaw);
    const decreaseCap = Number.isFinite(cap) && cap >= 0 ? cap : 0;

    if (
      miscFeeAdjustmentForm.adjustment_type === "Decrease" &&
      amount > decreaseCap
    ) {
      showNotification(
        "danger",
        `For a decrease, the amount cannot exceed ${formatPriceWithDollar(
          decreaseCap
        )} (misc fees increase balance). This transaction would result in a negative balance.`
      );
      return;
    }

    updateCalculusMutation.mutate(
      {
        id: bookingId,
        data: {
          misc_fee: {
            date_due: formatDateForAPI(miscFeeAdjustmentForm.date_due),
            adjustment_type: miscFeeAdjustmentForm.adjustment_type,
            description: (miscFeeAdjustmentForm.description || "").trim(),
            amount,
          },
        },
      },
      {
        onSuccess: () => {
          setMiscFeeAdjustmentForm(createMiscFeeAdjustmentFormDefaults());
          setPostSubView("menu");
        },
      }
    );
  }, [
    miscFeeAdjustmentForm,
    bookingId,
    updateCalculusMutation,
    calculusData?.balances?.current_misc_fees,
    showNotification,
  ]);

  const handlePostPayoffClick = useCallback(() => {
    const selectedItem = calculusData?.payment_select_type?.find(
      (item) => item.value === payoffForm.balance_type
    );
    const total = selectedItem?.total || 0;
    setPayoffForm(prev => ({
      ...prev,
      amount: formatPriceWithDollar(total),
      amount_received: formatPriceWithDollar(total),
      write_off: "No"
    }));
    setIsPayoffPosting(true);
  }, [calculusData, payoffForm.balance_type]);

  const handleSubmitPayoff = useCallback(() => {
    let payoffAmount = 0;
    const selectedItem = calculusData?.payment_select_type?.find(
      (item) => item.value === payoffForm.balance_type
    );
    payoffAmount = selectedItem?.total || 0;

    const payload = {
      booking_id: bookingId,
      date: parseDateFromAPI(payoffForm.date) || null,
      amount: payoffAmount,
      collected: toNumber(payoffForm.amount_received),
      delivery_method: payoffForm.delivery_method || null,
      payment_form: payoffForm.payment_form || null,
      amount_received: toNumber(payoffForm.amount_received),
      ref_number: payoffForm.ref_number || null,
      note: payoffForm.note || "",
      payment_type: "payoff",
      applied_payments: [],
      write_off: payoffForm.write_off === "Yes",
      balance: payoffForm.balance_type,
    };
    createPayoffMutation.mutate(payload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId],
        });
        setPayoffReceiptData({
          ...payload,
          ...(response || {})
        });
        setPostSubView("payoff-receipt");
        setIsPayoffPosting(false);
      },
    });
  }, [createPayoffMutation, payoffForm, bookingId, queryClient, closeDrawer, calculusData]);

  const handleSubmitSideNote = useCallback(() => {
    let apiType = "side_note";
    if (sideNoteForm.type === "Recurring CPI") apiType = "reccuring_cpi";
    else if (sideNoteForm.type === "CPI Balance") apiType = "cpi_balance";

    let finalFrequency = sideNoteForm.frequency ? sideNoteForm.frequency.toLowerCase() : null;
    let firstPaymentDate = null;
    let desiredPayment = null;
    let schedulePayment = null;

    if (sideNoteForm.type === "CPI Balance") {
      const firstRow = cpiSchedule.find(r => r.id === 1);
      if (firstRow) {
        finalFrequency = firstRow.frequency ? firstRow.frequency.toLowerCase() : null;
        firstPaymentDate = firstRow.firstDate || null;
        desiredPayment = toNumber(firstRow.amount);
      }
      schedulePayment = cpiSchedule;
    }

    const payload = {
      booking_id: bookingId,
      description: sideNoteForm.description,
      loan_amount: toNumber(sideNoteForm.amount),
      loan_date: sideNoteForm.date || null,
      type: apiType,
      frequency: finalFrequency,
      first_payment_date: firstPaymentDate,
      schedule_payment: schedulePayment,
      desired_payment: desiredPayment,
      rate: sideNoteForm.type !== "Recurring CPI" ? sideNoteForm.rate : null,
    };

    createSideNoteMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId],
        });
        setSideNoteForm(createSideNoteFormDefaults());
        // Reset CPI schedule to defaults
        setCpiSchedule([
          { id: 1, count: "0", amount: "", frequency: "Weekly", firstDate: formatDateForAPI(moment().add(7, 'days')), calculated: "" },
          { id: 2, count: "0", amount: "", frequency: "Weekly", firstDate: "", calculated: "" },
          { id: 3, count: "0", amount: "", frequency: "Weekly", firstDate: "", calculated: "" },
        ]);
        closeDrawer();
        setPostSubView("menu");
      },
    });
  }, [createSideNoteMutation, sideNoteForm, cpiSchedule, bookingId, queryClient, closeDrawer]);

  const handleSubmitWriteDown = useCallback(() => {
    const payload = {
      balance_type: writeDownForm.balance_type,
      booking_id: bookingId,
      comments: writeDownForm.comments,
    };

    createWriteDownMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["calculus-detail", bookingId],
        });
        setWriteDownForm({
          balance_type: calculusData?.payment_select_type?.[0]?.value || "",
          comments: "",
        });
        setPostSubView("menu");
      },
    });
  }, [createWriteDownMutation, writeDownForm, bookingId, queryClient, calculusData]);

  const handleSubmitLateFeeAccrual = useCallback(() => {
    if (
      lateFeeAccrualForm.is_waive_late_fee &&
      !lateFeeAccrualForm.is_waive_late_fee_date
    ) {
      return;
    }
    updateCalculusMutation.mutate(
      {
        id: bookingId,
        data: {
          waive_fee: {
            is_waive_late_fee: !!lateFeeAccrualForm.is_waive_late_fee,
            is_waive_late_fee_date: lateFeeAccrualForm.is_waive_late_fee
              ? lateFeeAccrualForm.is_waive_late_fee_date || null
              : null,
          },
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["calculus-detail", bookingId],
          });
          setLateFeeAccrualForm(
            createLateFeeAccrualFormDefaults(calculusData)
          );
          setPostSubView("menu");
        },
      }
    );
  }, [
    lateFeeAccrualForm,
    bookingId,
    updateCalculusMutation,
    queryClient,
    calculusData,
  ]);

  const isPostPaymentDisabled = useMemo(() => {
    const amountValue = toNumber(paymentForm.amount);
    const rowsToValidate = selectedPaymentPostType
      ? postPaymentTableRows
      : remainingPaymentsList.map((item, originalIdx) => ({ item, originalIdx }));

    let hasAllocationError = false;
    const totalApplied = rowsToValidate.reduce((sum, { item, originalIdx: idx }) => {
      const schedLateFee = getRemainingLateFeeDue(item);
      const schedAmt = getSchedulePaymentAmount(item);
      const lateFeeVal = getEnteredLateFeeAppld(
        idx,
        item,
        lateFeeAppldValues,
        waiveLateFeeValues
      );
      const pmtVal = getEnteredPmtAppld(idx, item, pmtAppldValues);

      if (lateFeeVal > schedLateFee + 0.009 || pmtVal > schedAmt + 0.009) {
        hasAllocationError = true;
      }

      return sum + lateFeeVal + pmtVal;
    }, 0);

    const amountsMatch =
      amountValue > 0 && Math.abs(totalApplied - amountValue) < 0.01;

    return (
      createPaymentMutation.isPending ||
      amountValue <= 0 ||
      !paymentForm.delivery_method ||
      !paymentForm.payment_form ||
      !amountsMatch ||
      hasAllocationError
    );
  }, [
    createPaymentMutation.isPending,
    paymentForm.amount,
    paymentForm.delivery_method,
    paymentForm.payment_form,
    pmtAppldValues,
    lateFeeAppldValues,
    waiveLateFeeValues,
    selectedPaymentPostType,
    postPaymentTableRows,
    remainingPaymentsList,
  ]);

  const handleSavePromises = useCallback(() => {
    if (promiseRows.length === 0 || isSavePromisesDisabled) {
      return;
    }

    // For simplicity, send each promise row as a separate call with type 'promise_pay'
    promiseRows.forEach((row) => {
      const payload = {
        booking_id: bookingId,
        promise_type: row.promiseType || null,
        date: formatDateForAPI(row.date) || null,
        amount: toNumber(row.amount),
        delivery_method: row.deliveryMethod || null,
        payment_form: row.paymentForm || null,
        note: row.account || "",
      };
      createPromisePayMutation.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["calculus-detail", bookingId],
          });
          queryClient.invalidateQueries({
            queryKey: ["promise-payments", bookingId],
          });
        }
      });
    });

    // Clear rows to hide the form
    setPromiseRows([]);
  }, [
    createPromisePayMutation,
    promiseRows,
    bookingId,
    isSavePromisesDisabled,
    queryClient,
  ]);

  const handleInsuranceFormChange = useCallback(
    (field, value) => {
      setInsuranceForm((prev) => {
        const newForm = { ...prev, [field]: value };

        // If insurance company is selected, update company name and phone
        if (field === "insurance_company" && value) {
          const selectedCompany = companies?.results?.find(
            (company) => company.id === Number(value)
          );

          if (selectedCompany) {
            newForm.insurance_company_name = selectedCompany.name || "";
            newForm.insurance_policy_number = selectedCompany.channel_id || "";
            newForm.insurance_company_phone = selectedCompany.phone_1 || "";
          }
        }

        // If insurance agent is selected, update agent name and phone
        if (field === "insurance_agent" && value) {
          const selectedAgent = agents?.results?.find(
            (agent) => agent.id === value
          );
          if (selectedAgent) {
            newForm.insurance_agent_name = selectedAgent.name || "";
            newForm.insurance_agent_phone = selectedAgent.phone_1 || "";
            newForm.insurance_agent_policy_number =
              selectedAgent.channel_id || "";
          }
        }

        return newForm;
      });
    },
    [companies?.results, agents?.results]
  );

  const handleRepoFormChange = useCallback((field, value) => {
    setRepoForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCancelRepoFormChange = useCallback((field, value) => {
    setCancelRepoForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleRedeemRepoFormChange = useCallback((field, value) => {
    setRedeemRepoForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleRecordRepoFormChange = useCallback((field, value) => {
    setRecordRepoForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAlertFormChange = useCallback((field, value) => {
    setAlertForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveInsurance = useCallback(() => {
    const insuranceHistory = {
      action: insuranceForm.action,
      insurance_company: insuranceForm.insurance_company,
      insurance_company_name: insuranceForm.insurance_company_name,
      insurance_company_phone: insuranceForm.insurance_company_phone,
      insurance_policy_number: insuranceForm.insurance_policy_number,
      policy_number: insuranceForm.policy_number,
      insurance_agent: insuranceForm.insurance_agent,
      insurance_agent_name: insuranceForm.insurance_agent_name,
      insurance_agent_phone: insuranceForm.insurance_agent_phone,
      insurance_agent_policy_number:
        insuranceForm.insurance_agent_policy_number,
      effective_date: formatDateForAPI(insuranceForm.effective_date),
      expire_date: formatDateForAPI(insuranceForm.expire_date),
      collision_deductible: toNumber(insuranceForm.collision_deductible),
      comprehensive_deductible: toNumber(
        insuranceForm.comprehensive_deductible
      ),
    };

    updateCalculusMutation.mutate({
      id: bookingId,
      data: {
        insurance_history: insuranceHistory,
      },
    });
    setShowInformationName("");
  }, [insuranceForm, bookingId, updateCalculusMutation]);

  const renderDay = useCallback((props, currentDate, selectedDate) => {
    let customStyle = { ...props.style };
    let customClassName = props.className || "";

    if (customClassName.includes("rdtDisabled")) {
      customStyle = {
        ...customStyle,
        opacity: 0.3,
        cursor: "not-allowed",
        textDecoration: "line-through",
      };
    }

    if (customClassName.includes("rdtActive")) {
      customStyle = {
        ...customStyle,
        backgroundColor: '#1890ff',
        color: '#ffffff',
        fontWeight: 'bold',
        display: 'inline-block',
        lineHeight: '30px',
        width: '30px',
        height: '30px',
        textAlign: 'center'
      };
    } else if (moment().isSame(currentDate, 'day')) {
      customStyle = {
        ...customStyle,
        backgroundColor: isLightTheme ? '#e6f7ff' : 'rgba(24, 144, 255, 0.2)',
        fontWeight: 'bold',
        border: '1px solid #1890ff',
        color: isLightTheme ? '#1890ff' : '#ffffff'
      };
    }

    // React-datetime handles clicks on the td element based on the `data-value` prop
    // which it passes in via `props`. Modifying the children or structure can break this.
    return (
      <td {...props} style={customStyle}>
        {currentDate.date()}
      </td>
    );
  }, [isLightTheme]);
  const handleSendAuthorization = useCallback(() => {
    sendAccountSMSMutation.mutate(bookingId);
  }, [sendAccountSMSMutation, bookingId]);

  const handleVerifyApprovalCode = useCallback(() => {
    if (!approvalCode.trim()) return;
    updateCalculusMutation.mutate({
      id: bookingId,
      data: {
        sms_approval_code: approvalCode.trim()
      }
    }, {
      onSuccess: () => {
        setApprovalCode("");
      }
    });
  }, [updateCalculusMutation, bookingId, approvalCode]);

  const renderSendContent = () => {
    const customer = accountData?.customer || {};
    if (sendView === "text_auth") {
      const applicantName = `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim().toUpperCase() + " [PRIMARY]";
      const rawCellPhone = customer?.cell_phone || customer?.phone;
      const hasPhone = rawCellPhone && rawCellPhone !== "-" && rawCellPhone.trim() !== "";
      const cellPhone = hasPhone ? rawCellPhone : "Phone not available";
      const allowAccounting = customer?.allow_accounting;
      const allowMarketing = customer?.allow_marketing;
      const smsStatus = customer?.sms_status || "not_send";

      // Helper function to get status badge
      const getStatusBadge = (status) => {
        const statusConfig = {
          not_send: { color: "#6c757d", text: "Not Sent", bgColor: "rgba(108, 117, 125, 0.15)" },
          waiting: { color: "#ffc107", text: "Waiting", bgColor: "rgba(255, 193, 7, 0.15)" },
          approved: { color: "#00f2c3", text: "Approved", bgColor: "rgba(0, 242, 195, 0.15)" },
          rejected: { color: "#fd5d93", text: "Rejected", bgColor: "rgba(253, 93, 147, 0.15)" }
        };
        const config = statusConfig[status] || statusConfig.not_send;
        return (
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "6px 16px",
            backgroundColor: config.bgColor,
            fontSize: "11px",
            fontWeight: "700",
            color: config.color,
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            borderLeft: `3px solid ${config.color}`
          }}>
            {config.text}
          </div>
        );
      };

      return (
        <div className="pt-0">
          {/* Header with back button */}
          <div className="d-flex align-items-center mb-0 pb-0">
            <h5 className="mb-1 mt-1 font-weight-bold text-uppercase" style={{ fontSize: "13px", fontWeight: 700 }}>TEXT AUTHORIZATION</h5>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <h5 className="mb-0 font-weight-bold text-uppercase mr-2">SEND TEXT</h5>
              <MdInfo size={18} color="#1f8ef1" style={{ cursor: "pointer" }} />
            </div>
            {getStatusBadge(smsStatus)}
          </div>

          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: isLightTheme ? "#f8f9fa" : "#2b2b3d",
              borderLeft: `4px solid ${activeColorStyle.borderColor}`
            }}
          >
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <span className="d-block text-uppercase mb-1" style={{ fontSize: "10px", fontWeight: 700, color: isLightTheme ? "#888" : "#ccc", letterSpacing: "0.5px" }}>Customer</span>
                <h6 className="mb-1 font-weight-bold">{applicantName}</h6>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px", fontSize: "12px" }}>
                  {hasPhone && <span>Cell Phone</span>}
                  <span className={`font-weight-bold ${!hasPhone ? "text-danger" : ""}`}>{cellPhone}</span>

                  {hasPhone && (
                    <>
                      <span className={`d-flex align-items-center ${allowAccounting ? "text-success" : "text-danger"}`}>
                        {allowAccounting ? <MdCheckCircle className="mr-1" /> : <IoMdCloseCircle className="mr-1" />} Account
                      </span>

                      <span className={`d-flex align-items-center ${allowMarketing ? "text-success" : "text-danger"}`}>
                        {allowMarketing ? <MdCheckCircle className="mr-1" /> : <IoMdCloseCircle className="mr-1" />} Marketing
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {
            hasPhone && (
              <div
                className="p-0 overflow-hidden mb-3"
                style={{
                  backgroundColor: isLightTheme ? "#f8f9fa" : "#2b2b3d",
                  borderLeft: `4px solid #1f8ef1`
                }}
              >
                <div
                  className="p-2 d-flex justify-content-between align-items-center"
                  style={{ backgroundColor: "#1f8ef1", color: "white" }}
                >
                  <h6 className="mb-0 font-weight-bold text-uppercase" style={{ color: "white" }}>TEXTING AUTHORIZATION</h6>
                </div>

                <div className="p-3">
                  {!allowAccounting && !allowMarketing && (
                    <>
                      <p className="small mb-3">
                        In order to receive texts, you must first perform the required authorization steps below.
                      </p>
                      <ul className="small mb-3 pl-3">
                        <li>Click the Authorize button.</li>
                        <li>Once clicked, the customer will receive a text with an approval #.</li>
                        <li>Ask for and enter this approval #, then click the Verify button.</li>
                      </ul>
                      <p className="small font-italic mb-4">
                        Note: The customer can also reply "Agree" to the authorization text.
                      </p>
                    </>
                  )}

                  {/* Authorization Status */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      {allowAccounting ? (
                        <MdCheckCircle className="text-success mr-2" size={16} />
                      ) : (
                        <IoMdCloseCircle className="text-danger mr-2" size={16} />
                      )}
                      <span className="small font-weight-bold">Allow Accounting Texting</span>
                    </div>
                    <div className="d-flex align-items-center">
                      {allowMarketing ? (
                        <MdCheckCircle className="text-success mr-2" size={16} />
                      ) : (
                        <IoMdCloseCircle className="text-danger mr-2" size={16} />
                      )}
                      <span className="small font-weight-bold">Allow Marketing Texting</span>
                    </div>
                  </div>

                  {/* Single Authorization Section */}
                  {(!allowAccounting || !allowMarketing) && (
                    <div className="border-top pt-3 mt-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <span className="font-weight-bold">Authorization Required</span>
                        <Button size="sm" color="info" className="mb-0" onClick={handleSendAuthorization} disabled={sendAccountSMSMutation.isPending}>
                          {sendAccountSMSMutation.isPending ? "SENDING..." : "AUTHORIZE"}
                        </Button>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="small mr-2">Approval #</span>
                        <Input
                          type="text"
                          bsSize="sm"
                          style={{ width: "100px", marginRight: "10px" }}
                          value={approvalCode}
                          maxLength={5}
                          onChange={(e) => setApprovalCode(e.target.value)}
                          placeholder="Enter code"
                        />
                        <Button
                          size="sm"
                          color="primary"
                          className="mb-0"
                          onClick={handleVerifyApprovalCode}
                          disabled={updateCalculusMutation.isPending || !approvalCode.trim()}
                        >
                          {updateCalculusMutation.isPending ? "VERIFYING..." : "VERIFY"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Both Approved */}
                  {allowAccounting && allowMarketing && (
                    <div className="d-flex align-items-center p-3 mt-3" style={{
                      backgroundColor: isLightTheme ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.05)",
                      borderLeft: "4px solid #00f2c3"
                    }}>
                      <MdCheckCircle className="mr-2" size={20} style={{ color: "#00f2c3" }} />
                      <span className="font-weight-bold" style={{ color: isLightTheme ? "#1e1e2f" : "#fff", fontSize: "13px", letterSpacing: "0.3px" }}>Texting Fully Authorized</span>
                    </div>
                  )}

                </div>
              </div>
            )
          }
        </div >
      );
    }

    if (sendView === "email") {
      return (
        <SendEmail
          key={sendEmailData?.transaction_id || "email-send"}
          isLightTheme={isLightTheme}
          activeColor={activeColor}
          isLoading={sendMailMutation.isPending}
          customerEmail={accountData?.customer?.email}
          initialSubject={sendEmailData?.subject}
          initialMessage={sendEmailData?.message}
          dataSource="accounts"
          librarySource="accounts"
          recordId={bookingId || accountData?.booking_id}
          onCancel={() => {
            setSendView("options");
            setSendEmailData(null);
          }}
          onSend={(data) => {
            const isReceipt = Boolean(sendEmailData?.transaction_id);
            sendMailMutation.mutate({
              type: isReceipt ? "account_receipt" : "accounts",
              id: isReceipt ? sendEmailData?.transaction_id : accountData?.booking_id,
              account_id: accountData?.id,
              subject: data?.subject,
              message: data?.message,
              template: data?.template?.value,
              transaction_id: sendEmailData?.transaction_id,
              attachments: data?.attachments,
            }, {
              onSuccess: () => {
                setSendView("options");
                setSendEmailData(null);
                closeDrawer();
              }
            });
          }}
        />
      );
    }

    return (
      <div className="d-flex flex-column gap-3 pt-3">
        {[
          {
            id: "email",
            label: "Send an Email",
            icon: <MdEmail size={20} />,
            disabled: !accountData?.customer?.email,
            disabledText: t('transactions.missingEmail')
          },
          { id: "text", label: "Send a Text", icon: <MdMessage size={20} /> },
        ].map((item, index) => (
          <div
            key={index}
            className={`p-3 mb-2 rounded d-flex align-items-center gap-3 ${item.disabled ? "opacity-5" : "cursor-pointer"}`}
            style={{
              backgroundColor: isLightTheme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)",
              border: "1px solid transparent",
              transition: "all 0.2s ease",
              cursor: item.disabled ? "not-allowed" : "pointer",
              opacity: item.disabled ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (item.disabled) return;
              e.currentTarget.style.borderColor = activeColorStyle.borderColor;
              e.currentTarget.style.backgroundColor = isLightTheme ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)";
            }}
            onMouseLeave={(e) => {
              if (item.disabled) return;
              e.currentTarget.style.borderColor = "transparent";
              e.currentTarget.style.backgroundColor = isLightTheme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)";
            }}
            onClick={() => {
              if (item.disabled) return;
              if (item.id === "text") {
                setSendView("text_auth");
              } else if (item.id === "email") {
                setSendView("email");
              }
            }}
          >
            <div style={{ color: item.disabled ? "#ccc" : activeColorStyle.borderColor, marginRight: "12px" }}>
              {item.icon}
            </div>
            <div className="d-flex flex-column">
              <h6 className="mb-0" style={{
                fontWeight: 600,
                color: item.disabled ? "#999" : (isLightTheme ? "#1e1e2f" : "#fff")
              }}>
                {item.label}
              </h6>
              {item.disabled && (
                <span style={{ fontSize: '11px', color: '#ff3d3d', marginTop: '2px' }}>
                  {item.disabledText}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleAccountRecordReportClick = useCallback((report) => {
    if (!bookingId || !report?.id) return;
    const url = `/reports/${report.id}?acct_id=${encodeURIComponent(bookingId)}&view=true`;
    window.open(url, "_blank", "noopener,noreferrer");
    closeDrawer();
  }, [bookingId, closeDrawer]);

  const renderPrintContent = () => {
    if (printSubView === "viewer" && pdfUrl) {
      return (
        <div className="pt-3 h-100 d-flex flex-column" style={{ height: 'calc(100vh - 100px)' }}>

          <div className="flex-1" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <iframe
              key={pdfUrl}
              id="pdf-iframe"
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px'
              }}
              title={t("accountsDrawer.print.documentViewer")}
            />
          </div>
        </div>
      );
    }

    if (printSubView === "documents") {
      const allDocs = documentsData?.data || [];
      const allIds = allDocs.map((d) => d.id);
      const isAllSelected =
        allIds.length > 0 && selectedDocuments.length === allIds.length;

      return (
        <div className="pt-3 h-100 d-flex flex-column">
          <div className="d-flex align-items-center mb-3">
            <h6 className="mb-0 font-weight-bold text-uppercase">
              {t("accountsDrawer.print.printDocuments")}
            </h6>
          </div>

          <div className="d-flex align-items-center mb-3">
            <div
              className="custom-control custom-checkbox pl-0 cursor-pointer d-flex align-items-center"
              onClick={() => {
                if (isAllSelected) setSelectedDocuments([]);
                else setSelectedDocuments(allIds);
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  border: `2px solid ${isAllSelected
                    ? activeColorStyle.borderColor
                    : isLightTheme
                      ? "#ccc"
                      : "#666"
                    }`,
                  backgroundColor: isAllSelected
                    ? activeColorStyle.borderColor
                    : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 10,
                  transition: "all 0.2s ease",
                }}
              >
                {isAllSelected && <MdCheckCircle size={16} color="#fff" />}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {t("accountsDrawer.print.selectDeselectAll")}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-auto" style={{ minHeight: "300px" }}>
            {isDocumentsLoading ? (
              <div className="text-center py-4">{t("accountsDrawer.print.loadingDocuments")}</div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  width: "100%",
                }}
              >
                {allDocs.map((doc) => {
                  const isSelected = selectedDocuments.includes(doc.id);
                  const docName =
                    doc.attributes?.name || doc.name || "Untitled Document";
                  return (
                    <div
                      key={doc.id}
                      className="d-flex align-items-center p-2 rounded cursor-pointer"
                      title={docName}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isLightTheme
                          ? "rgba(0,0,0,0.02)"
                          : "rgba(255,255,255,0.02)",
                        border: `1px solid ${isSelected
                          ? activeColorStyle.borderColor
                          : "transparent"
                          }`,
                        transition: "all 0.2s ease",
                        minWidth: 0,
                        width: "100%",
                      }}
                      onClick={() => {
                        setSelectedDocuments((prev) =>
                          prev.includes(doc.id)
                            ? prev.filter((id) => id !== doc.id)
                            : [...prev, doc.id]
                        );
                      }}
                    >
                      <div
                        style={{
                          minWidth: 20,
                          width: 20,
                          height: 20,
                          border: `2px solid ${isSelected
                            ? activeColorStyle.borderColor
                            : isLightTheme
                              ? "#ccc"
                              : "#666"
                            }`,
                          backgroundColor: isSelected
                            ? activeColorStyle.borderColor
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 8,
                          transition: "all 0.2s ease",
                        }}
                      >
                        {isSelected && (
                          <MdCheckCircle size={16} color="#fff" />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {docName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {!isDocumentsLoading && allDocs.length === 0 && (
              <div className="text-center py-4 text-muted">
                {t("accountsDrawer.print.noDocumentsFound")}
              </div>
            )}
          </div>

          <div className="mt-3 d-flex justify-content-end">
            <Button
              style={{ ...activeColorStyle, color: "white" }}
              disabled={
                selectedDocuments.length === 0 ||
                printCalculusMutation.isPending
              }
              onClick={() => {
                printCalculusMutation.mutate({
                  id: bookingId,
                  documents: selectedDocuments,
                }, {
                  onSuccess: (response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);
                    setPdfUrl(blobUrl);
                    setPrintSubView("viewer");
                    setDrawerFullSize(true);
                  }
                });
              }}
            >
              {printCalculusMutation.isPending
                ? t("accountsDrawer.print.printing")
                : t("accountsDrawer.print.title")}
            </Button>
          </div>
        </div>
      );
    }

    if (printSubView === "reports") {
      return (
        <div className="h-100 d-flex flex-column">
          <div
            className="d-flex align-items-center justify-content-center font-weight-bold text-uppercase"
            style={{
              ...activeColorStyle,
              color: "white",
              fontSize: "13px",
              letterSpacing: "0.04em",
              marginTop: -8,
              marginLeft: -16,
              marginRight: -16,
              marginBottom: 12,
              padding: "8px 12px",
            }}
          >
            {t("accountsDrawer.print.recordSpecificReports")}
          </div>

          <div className="flex-1 overflow-auto" style={{ minHeight: "300px" }}>
            {isRecordReportsLoading ? (
              <div className="text-center py-4">{t("common.loading")}</div>
            ) : accountRecordReports.length === 0 ? (
              <div className="text-center py-4 text-muted">
                {t("accountsDrawer.print.noRecordSpecificReports")}
              </div>
            ) : (
              <div className="d-flex flex-column">
                {accountRecordReports.map((report) => (
                  <div
                    key={report.value || report.id}
                    className="d-flex align-items-center px-3 py-2 cursor-pointer border-bottom"
                    style={{
                      cursor: "pointer",
                      backgroundColor: isLightTheme ? "#fff" : "rgba(255,255,255,0.02)",
                      borderColor: isLightTheme ? "#e9ecef" : "rgba(255,255,255,0.08)",
                      transition: "background-color 0.2s ease",
                    }}
                    onClick={() => handleAccountRecordReportClick(report)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isLightTheme
                        ? "rgba(29, 140, 248, 0.08)"
                        : "rgba(255,255,255,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isLightTheme
                        ? "#fff"
                        : "rgba(255,255,255,0.02)";
                    }}
                  >
                    <MdPrint
                      size={20}
                      style={{
                        color: activeColorStyle.borderColor,
                        marginRight: 12,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: isLightTheme ? "#1e1e2f" : "#fff",
                      }}
                    >
                      {report.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-3 pt-3">
        {[
          { label: t("accountsDrawer.print.menu.documents"), icon: <MdInsertDriveFile size={22} />, id: "documents" },
          { label: t("accountsDrawer.print.menu.reports"), icon: <MdAssessment size={22} />, id: "reports" },
        ].map((item, index) => (
          <div
            key={index}
            className="p-3 mb-2 cursor-pointer d-flex align-items-center"
            onClick={() => {
              if (item.id === "documents") {
                setPrintSubView("documents");
              } else if (item.id === "reports") {
                setPrintSubView("reports");
              }
            }}
            style={{
              backgroundColor: isLightTheme
                ? "rgba(0,0,0,0.05)"
                : "rgba(255,255,255,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              border: "1px solid transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isLightTheme
                ? "rgba(0,0,0,0.08)"
                : "rgba(255,255,255,0.08)";
              e.currentTarget.style.borderColor = activeColorStyle.borderColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isLightTheme
                ? "rgba(0,0,0,0.05)"
                : "rgba(255,255,255,0.05)";
              e.currentTarget.style.borderColor = "transparent";
            }}
          >
            <div
              style={{
                marginRight: "15px",
                display: "flex",
                alignItems: "center",
                opacity: 0.8
              }}
            >
              {item.icon}
            </div>
            <h6
              className="mb-0"
              style={{
                fontWeight: 600,
                color: isLightTheme ? "#1e1e2f" : "#fff",
              }}
            >
              {item.label}
            </h6>
            <MdChevronRight
              size={20}
              style={{ marginLeft: "auto", opacity: 0.5 }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {isDrawerVisible && (
        <div
          style={{
            ...drawerStyles.overlay,
            opacity: drawerOpen ? 1 : 0,
            pointerEvents: drawerOpen ? "auto" : "none",
          }}
          onClick={closeDrawer}
        >
          <div
            className={
              isTheme2
                ? "accounts-right-drawer accounts-right-drawer--theme2"
                : "accounts-right-drawer"
            }
            style={{
              ...drawerStyles.panel,
              transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
            }}
            onClick={(e) => e.stopPropagation()}
            onTransitionEnd={() => {
              if (!drawerOpen) {
                setIsDrawerVisible(false);
                setPostSubView("menu");
                setPmtAppldValues({});
                setLateFeeAppldValues({});
                setWaiveLateFeeValues({});
              }
            }}
          >
            {drawerFormFieldsCss ? <style>{drawerFormFieldsCss}</style> : null}
            {!isMobile && (
              <div
                style={drawerStyles.drawerResizeHandle}
                onPointerDown={onDrawerWidthHandlePointerDown}
                title={t("accountsDrawer.drawerResizeTitle")}
                role="separator"
                aria-orientation="vertical"
                aria-label={t("accountsDrawer.drawerResizeAria")}
              >
                <div
                  style={{
                    width: 3,
                    height: 40,
                    backgroundColor: activeColorStyle.borderColor,
                    opacity: 0.55,
                  }}
                />
              </div>
            )}
            <div style={drawerStyles.mainColumn}>
            <div style={drawerStyles.header}>
              {isMobile ? (
              <>
              <button
                type="button"
                onClick={closeDrawer}
                style={drawerStyles.closeBtnMobile}
                aria-label={t("dealsDrawer.close")}
              >
                <i className="tim-icons icon-simple-remove" style={{ fontSize: 20 }} />
              </button>
              <div
                ref={drawerTabScrollRef}
                className="channel-tabs-scroll"
                style={drawerStyles.headerTabsScroll}
              >
                {(
                  (drawerActiveTab === "action" && showInformationName !== "") ||
                  (drawerActiveTab === "post" && postSubView !== "menu") ||
                  (drawerActiveTab === "send" && sendView !== "options") ||
                  (drawerActiveTab === "print" && printSubView !== "menu") ||
                  drawerActiveTab === "flags" ||
                  drawerSubView !== null
                ) && (
                    <button
                      type="button"
                      className="btn btn-link p-0 mr-2"
                      style={{
                        color: activeColorStyle.borderColor,
                        fontWeight: 600,
                        fontSize: 13,
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => {
                          if (drawerSubView) {
                            setDrawerSubView(null);
                            setDrawerFullSize(false);
                            return;
                          }
                          if (drawerActiveTab === "flags") setDrawerActiveTab("more");
                          else if (drawerActiveTab === "action") setShowInformationName("");
                          else if (drawerActiveTab === "post") setPostSubView("menu");
                          else if (drawerActiveTab === "send") setSendView("options");
                          else if (drawerActiveTab === "print") {
                            if (printSubView === "viewer") {
                              if (viewerSource) {
                                setDrawerActiveTab(viewerSource.tab);
                                if (viewerSource.tab === "post" && viewerSource.subView) {
                                  setPostSubView(viewerSource.subView);
                                }
                                setViewerSource(null);
                              } else {
                                setPrintSubView("documents");
                              }
                              if (pdfUrl) {
                                URL.revokeObjectURL(pdfUrl);
                                setPdfUrl(null);
                              }
                            } else {
                              setPrintSubView("menu");
                            }
                          }
                        }}
                      >
                        &lt; {t('common.back')}
                      </button>
                    )}
                <Nav
                  tabs
                  className="border-0"
                  style={{
                    marginTop: "4px",
                    marginBottom: 0,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    minWidth: "max-content",
                    width: "auto",
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {[
                    "action",
                    "send",
                    "promiseToPay",
                    "post",
                    "print",
                    "notes",
                    "more",
                  ].map((key) => (
                    <NavItem key={key} style={{ flex: "0 0 auto" }}>
                      <NavLink
                        href="#"
                        className={
                          drawerActiveTab === key
                            ? "active font-weight-bold"
                            : ""
                        }
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: 12,
                          cursor: "pointer",
                          color:
                            drawerActiveTab === key
                              ? activeColorStyle.borderColor
                              : isLightTheme
                                ? "#1e1e2f"
                                : "white",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          position: "relative",
                          paddingBottom: 10,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setDrawerActiveTab(key);

                          // Reset sub-views
                          setShowInformationName("");
                          setPostSubView("menu");
                          setPrintSubView("menu");
                          setViewerSource(null);

                          if (scrollableRef.current) {
                            scrollableRef.current.scrollTop = 0;
                          }
                        }}
                      >
                        {t(`accountsDrawer.tabs.${key}`)}
                        {drawerActiveTab === key && (
                          <div
                            style={{
                              height: 3,
                              background: activeColorStyle.borderColor,
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: 0,
                            }}
                          />
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </div>
              {canScrollDrawerTabsRight && (
                <button
                  type="button"
                  onClick={handleDrawerTabScrollRight}
                  style={drawerStyles.tabScrollNextBtn}
                  aria-label={t("common.scrollTabs")}
                >
                  <MdChevronRight size={24} />
                </button>
              )}
              </>
              ) : (
              <>
              <h5 style={{ margin: 0 }}>
                <Nav tabs className="border-0" style={{ marginTop: "4px" }}>
                {(
                  (drawerActiveTab === "action" && showInformationName !== "") ||
                  (drawerActiveTab === "post" && postSubView !== "menu") ||
                  (drawerActiveTab === "send" && sendView !== "options") ||
                  (drawerActiveTab === "print" && printSubView !== "menu") ||
                  drawerActiveTab === "flags" ||
                  drawerSubView !== null
                ) && (
                    <button
                      className="btn btn-link p-0 mr-2"
                      style={{ color: activeColorStyle.borderColor, fontWeight: 600, fontSize: 13 }}
                      onClick={() => {
                          if (drawerSubView) {
                            setDrawerSubView(null);
                            setDrawerFullSize(false);
                            return;
                          }
                          if (drawerActiveTab === "flags") setDrawerActiveTab("more");
                          else if (drawerActiveTab === "action") setShowInformationName("");
                          else if (drawerActiveTab === "post") setPostSubView("menu");
                          else if (drawerActiveTab === "send") setSendView("options");
                          else if (drawerActiveTab === "print") {
                            if (printSubView === "viewer") {
                              if (viewerSource) {
                                setDrawerActiveTab(viewerSource.tab);
                                if (viewerSource.tab === "post" && viewerSource.subView) {
                                  setPostSubView(viewerSource.subView);
                                }
                                setViewerSource(null);
                              } else {
                                setPrintSubView("documents");
                              }
                              if (pdfUrl) {
                                URL.revokeObjectURL(pdfUrl);
                                setPdfUrl(null);
                              }
                            } else {
                              setPrintSubView("menu");
                            }
                          }
                        }}
                      >
                        &lt; {t('common.back')}
                      </button>
                    )}
                  {[
                    "action",
                    "send",
                    "promiseToPay",
                    "post",
                    "print",
                    "notes",
                    "more",
                  ].map((key) => (
                    <NavItem key={key}>
                      <NavLink
                        href="#"
                        className={
                          drawerActiveTab === key
                            ? "active font-weight-bold"
                            : ""
                        }
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: 12,
                          cursor: "pointer",
                          color:
                            drawerActiveTab === key
                              ? activeColorStyle.borderColor
                              : isLightTheme
                                ? "#1e1e2f"
                                : "white",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setDrawerActiveTab(key);
                          setShowInformationName("");
                          setPostSubView("menu");
                          setPrintSubView("menu");
                          setViewerSource(null);
                          if (scrollableRef.current) {
                            scrollableRef.current.scrollTop = 0;
                          }
                        }}
                      >
                        {t(`accountsDrawer.tabs.${key}`)}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </h5>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
                <button type="button" onClick={closeDrawer} style={drawerStyles.closeBtn}>
                  {t("dealsDrawer.close")}
                </button>
              </div>
              </>
              )}
            </div>
            <div
              style={drawerStyles.body}
              ref={scrollableRef}
              className={isTheme2 ? "accounts-right-drawer-form-fields" : undefined}
            >
              {drawerActiveTab === "action" && (
                <div>
                  {showInformationName === "" ? (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                      {[
                        {
                          label: calculusData?.is_lock ? "Unlock Account" : "Lock Account",
                          action: () => {
                            updateCalculusMutation.mutate({
                              id: bookingId,
                              data: {
                                is_lock: !calculusData?.is_lock,
                              },
                            });
                          },
                          icon: calculusData?.is_lock ? (
                            <MdLockOpen size={20} />
                          ) : (
                            <MdLock size={20} />
                          ),
                          visible: true,
                        },
                        {
                          label: "Out for Repo",
                          action: () => { if (status !== "closed") setShowInformationName("out-for-repo") },
                          icon: <MdReportProblem size={20} />,
                          visible: status !== "out_for_repo",
                          disabled: status === "closed"
                        },
                        {
                          label: "Cancel Repo",
                          action: () => { if (status !== "closed") setShowInformationName("cancel-repo") },
                          icon: <MdRestore size={20} />,
                          visible: status === "out_for_repo",
                          disabled: status === "closed"
                        },
                        {
                          label:
                            status === "repossessed"
                              ? "Update Repo"
                              : "Record Repo",
                          action: () => {
                            if (status !== "closed") {
                              setShowInformationName("record-repo");
                            }
                          },
                          icon: <MdSave size={20} />,
                          visible: true,
                          disabled: status === "closed"
                        },
                        {
                          label: "Redeem Repo",
                          action: () => {
                            if (status !== "closed") {
                              setShowInformationName("redeem-repo");
                            }
                          },
                          icon: <MdRestore size={20} />,
                          visible: status === "repossessed",
                          disabled: status === "closed"
                        },
                        {
                          label: "Update Insurance",
                          action: () => setShowInformationName("update-insurance"),
                          icon: <MdSecurity size={20} />,
                          visible: true
                        },
                        {
                          label: "Update Alert",
                          action: () => setShowInformationName("update-alert"),
                          icon: <MdAddAlert size={20} />,
                          visible: true
                        },
                      ].filter(item => item.visible).map((item, index) => (
                        <div
                          key={index}
                          onClick={item.disabled ? undefined : item.action}
                          className="d-flex align-items-center justify-content-between"
                          style={{
                            padding: "16px 20px",
                            cursor: item.disabled ? "not-allowed" : "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            border: isLightTheme
                              ? "1px solid #e0e0e0"
                              : "1px solid rgba(255, 255, 255, 0.1)",
                            backgroundColor: isLightTheme
                              ? item.disabled ? "#f8f9fa" : "#fff"
                              : item.disabled ? "rgba(255, 255, 255, 0.02)" : "rgba(255, 255, 255, 0.05)",
                            color: isLightTheme ? (item.disabled ? "#999" : "#333") : (item.disabled ? "#666" : "#fff"),
                            transition: "all 0.2s ease",
                            boxShadow: isLightTheme && !item.disabled ? "0 2px 4px rgba(0,0,0,0.02)" : "none",
                            opacity: item.disabled ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!item.disabled) {
                              e.currentTarget.style.backgroundColor = isLightTheme
                                ? "#f8f9fa"
                                : "rgba(255, 255, 255, 0.1)";
                              e.currentTarget.style.transform = "translateY(-1px)";
                              e.currentTarget.style.boxShadow = isLightTheme ? "0 4px 8px rgba(0,0,0,0.05)" : "0 4px 8px rgba(0,0,0,0.2)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!item.disabled) {
                              e.currentTarget.style.backgroundColor = isLightTheme
                                ? "#fff"
                                : "rgba(255, 255, 255, 0.05)";
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none";
                            }
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <span style={{ marginRight: '16px', opacity: 0.8 }}>{item.icon}</span>
                            <span>{item.label}</span>
                          </div>
                          <MdChevronRight size={20} style={{ opacity: 0.5 }} />
                        </div>
                      ))}
                    </div>
                  ) : showInformationName === "update-insurance" ? (
                    <div>
                      <h5
                        className="mb-1 mt-1"
                        style={{
                          color: isLightTheme ? "#1e1e2f" : "#fff",
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        Update Insurance
                      </h5>
                      {/* Insurance Action Select */}
                      <div className="mb-3">
                        <FormGroup>
                          <Label
                            className="mb-1"
                            style={{ fontSize: 12, color: "#a0a0b2" }}
                          >
                            Action
                          </Label>
                          <DrawerSelect
                            activeColor={activeColor}
                            className="react-select"
                            placeholder="Select Action"
                            name="insurance_action"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={
                              insuranceForm.action
                                ? {
                                  value: insuranceForm.action,
                                  label:
                                    insuranceForm.action === "update"
                                      ? "Update"
                                      : insuranceForm.action === "cancel"
                                        ? "Cancel"
                                        : insuranceForm.action === "renew"
                                          ? "Renew Policy"
                                          : "New Policy",
                                }
                                : null
                            }
                            onChange={(option) =>
                              handleInsuranceFormChange(
                                "action",
                                option?.value || ""
                              )
                            }
                            options={[
                              { value: "update", label: "Update" },
                              { value: "cancel", label: "Cancel" },
                              { value: "renew", label: "Renew Policy" },
                              { value: "new", label: "New Policy" },
                            ]}
                          />
                        </FormGroup>
                      </div>

                      {/* Insurance Form */}
                      {insuranceForm.action !== 'cancel' && (
                        <Row>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Insurance Company {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select Company"
                                name="insurance_company"
                                isSearchable
                                isLoading={isCompaniesLoading}
                                closeMenuOnSelect={true}
                                value={
                                  insuranceForm.insurance_company
                                    ? {
                                      value: Number(
                                        insuranceForm.insurance_company
                                      ),
                                      label:
                                        companies?.results?.find(
                                          (c) =>
                                            c.id ===
                                            Number(
                                              insuranceForm.insurance_company
                                            )
                                        )?.name ||
                                        String(insuranceForm.insurance_company),
                                    }
                                    : null
                                }
                                onChange={(option) =>
                                  handleInsuranceFormChange(
                                    "insurance_company",
                                    option?.value || ""
                                  )
                                }
                                onInputChange={(newValue) => setCompanySearch(newValue)}
                                options={(companies?.results || []).map(
                                  (company) => ({
                                    value: company.id,
                                    label: company.name,
                                  })
                                )}
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Policy Number {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <Input
                                type="text"
                                value={insuranceForm.insurance_policy_number}
                                onChange={(e) =>
                                  handleInsuranceFormChange(
                                    "insurance_policy_number",
                                    e.target.value
                                  )
                                }
                                placeholder="Policy Number"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Insurance Agent {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select Agent"
                                name="insurance_agent"
                                isSearchable
                                isLoading={isAgentsLoading}
                                closeMenuOnSelect={true}
                                value={
                                  insuranceForm.insurance_agent
                                    ? {
                                      value: Number(
                                        insuranceForm.insurance_agent
                                      ),
                                      label:
                                        agents?.results?.find(
                                          (a) =>
                                            a.id ===
                                            Number(
                                              insuranceForm.insurance_agent
                                            )
                                        )?.name ||
                                        String(insuranceForm.insurance_agent),
                                    }
                                    : null
                                }
                                onChange={(option) =>
                                  handleInsuranceFormChange(
                                    "insurance_agent",
                                    option?.value || ""
                                  )
                                }
                                onInputChange={(newValue) => setAgentSearch(newValue)}
                                options={(agents?.results || []).map((agent) => ({
                                  value: agent.id,
                                  label: agent.name,
                                  displayLabel: agent.name + ' ' + (agent.channel_id || '')
                                }))}
                                getOptionLabel={option => option.displayLabel || option.label}
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Effective Date {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <ReactDatetime
                                inputProps={{
                                  className: "form-control",
                                  placeholder: "Select Date",
                                  style: { borderRadius: 0 },
                                }}
                                value={
                                  insuranceForm.effective_date
                                    ? formatDateForPicker(
                                      insuranceForm.effective_date
                                    )
                                    : insuranceForm.effective_date
                                }
                                onChange={(date) =>
                                  handleInsuranceFormChange(
                                    "effective_date",
                                    date
                                  )
                                }
                                renderDay={renderDay}
                                dateFormat="MM-DD-YYYY"
                                timeFormat={false}
                                closeOnSelect={true}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Expire Date {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <ReactDatetime
                                inputProps={{
                                  className: "form-control",
                                  placeholder: "Select Date",
                                  style: { borderRadius: 0 },
                                }}
                                value={
                                  insuranceForm.expire_date
                                    ? formatDateForPicker(
                                      insuranceForm.expire_date
                                    )
                                    : insuranceForm.expire_date
                                }
                                onChange={(date) =>
                                  handleInsuranceFormChange("expire_date", date)
                                }
                                renderDay={renderDay}
                                dateFormat="MM-DD-YYYY"
                                timeFormat={false}
                                closeOnSelect={true}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Collision Deductible {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <Input
                                style={{ borderRadius: 0, fontSize: "14px" }}
                                type="text"
                                value={insuranceForm.collision_deductible}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(
                                    /[^\d.]/g,
                                    ""
                                  );
                                  handleInsuranceFormChange(
                                    "collision_deductible",
                                    raw
                                  );
                                }}
                                onBlur={() => {
                                  handleInsuranceFormChange(
                                    "collision_deductible",
                                    formatPriceWithDollar(
                                      insuranceForm.collision_deductible
                                    )
                                  );
                                }}
                                onFocus={() => {
                                  const raw = String(
                                    insuranceForm.collision_deductible || ""
                                  ).replace(/[^\d.]/g, "");
                                  handleInsuranceFormChange(
                                    "collision_deductible",
                                    raw
                                  );
                                }}
                                placeholder="$0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label
                                className="mb-1"
                                style={{ fontSize: 12, color: "#a0a0b2" }}
                              >
                                Comprehensive Deductible {(insuranceForm.action === 'new' || insuranceForm.action === 'renew') && <span style={{ color: "red" }}>*</span>}
                              </Label>
                              <Input
                                style={{ borderRadius: 0, fontSize: "14px" }}
                                type="text"
                                value={insuranceForm.comprehensive_deductible}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(
                                    /[^\d.]/g,
                                    ""
                                  );
                                  handleInsuranceFormChange(
                                    "comprehensive_deductible",
                                    raw
                                  );
                                }}
                                onBlur={() => {
                                  handleInsuranceFormChange(
                                    "comprehensive_deductible",
                                    formatPriceWithDollar(
                                      insuranceForm.comprehensive_deductible
                                    )
                                  );
                                }}
                                onFocus={() => {
                                  const raw = String(
                                    insuranceForm.comprehensive_deductible || ""
                                  ).replace(/[^\d.]/g, "");
                                  handleInsuranceFormChange(
                                    "comprehensive_deductible",
                                    raw
                                  );
                                }}
                                placeholder="$0.00"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      )}

                      {/* Save Button */}
                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          onClick={() => setShowInformationName("")}
                          size="sm"
                        >
                          Cancel
                        </Button>
                        <Button
                          style={{ ...activeColorStyle, color: "white" }}
                          size="sm"
                          onClick={handleSaveInsurance}
                          disabled={updateCalculusMutation.isPending || (
                            (insuranceForm.action === 'new' || insuranceForm.action === 'renew') && (
                              !insuranceForm.insurance_company ||
                              !insuranceForm.insurance_policy_number ||
                              !insuranceForm.insurance_agent ||
                              !insuranceForm.effective_date ||
                              !insuranceForm.expire_date ||
                              !insuranceForm.collision_deductible ||
                              !insuranceForm.comprehensive_deductible
                            )
                          )}
                        >
                          {updateCalculusMutation.isPending ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm mr-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Saving...
                            </>
                          ) : (
                            "Save"
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : showInformationName === "out-for-repo" ? (
                    <div>
                      <h5
                        className="mb-1 mt-1"
                        style={{
                          color: isLightTheme ? "#1e1e2f" : "#fff",
                          fontSize: 13,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        Place Out for Repossession
                      </h5>
                      <Row>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label
                              className="mb-1"
                              style={{
                                fontSize: 12,
                                color: isLightTheme ? "#666" : "#a0a0b2",
                              }}
                            >
                              Agent:
                            </Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Select Agent"
                              name="agent"
                              isSearchable
                              closeMenuOnSelect={true}
                              value={
                                repoForm.agent
                                  ? {
                                    value: repoForm.agent,
                                    label:
                                      repoAgents?.results?.find(
                                        (agent) => agent.id === repoForm.agent
                                      )?.name || repoForm.agent,
                                  }
                                  : null
                              }
                              onChange={(option) =>
                                handleRepoFormChange(
                                  "agent",
                                  option?.value || ""
                                )
                              }
                              options={
                                repoAgents?.results?.map((agent) => ({
                                  value: agent.id,
                                  label: agent.name,
                                })) || []
                              }
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          {/* Reason Select */}
                          <FormGroup className="mb-3">
                            <Label
                              className="mb-1"
                              style={{
                                fontSize: 12,
                                color: isLightTheme ? "#666" : "#a0a0b2",
                              }}
                            >
                              Reason:
                            </Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Reason"
                              name="reason"
                              isSearchable
                              closeMenuOnSelect={true}
                              value={
                                repoForm.reason
                                  ? {
                                    value: repoForm.reason,
                                    label: repoForm.reason,
                                  }
                                  : null
                              }
                              onChange={(option) =>
                                handleRepoFormChange(
                                  "reason",
                                  option?.value || ""
                                )
                              }
                              options={[
                                {
                                  value: "Past Due Payment",
                                  label: "Past Due Payment",
                                },
                                { value: "Insurance", label: "Insurance" },
                                {
                                  value: "Other/See Notes",
                                  label: "Other/See Notes",
                                },
                              ]}
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {/* Notes Textarea */}
                      <FormGroup className="mb-3">
                        <Label
                          className="mb-1"
                          style={{
                            fontSize: 12,
                            color: isLightTheme ? "#666" : "#a0a0b2",
                          }}
                        >
                          Notes:
                        </Label>
                        <Input
                          type="textarea"
                          rows="4"
                          value={repoForm.notes}
                          onChange={(e) =>
                            handleRepoFormChange("notes", e.target.value)
                          }
                          placeholder="Enter notes..."
                        />
                      </FormGroup>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          onClick={() => setShowInformationName("")}
                          size="sm"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            updateCalculusMutation.mutate({
                              id: bookingId,
                              data: {
                                repo: {
                                  is_out_for_repo: true,
                                  repo_agent: repoForm.agent || null,
                                  reason: repoForm.reason || "",
                                  notes: repoForm.notes || "",
                                }
                              },
                            });
                            setShowInformationName("");
                          }}
                        >
                          Confirm
                        </Button>
                      </div>
                    </div>
                  ) : showInformationName === "cancel-repo" ? (
                    <div>
                      <h5
                        className="mb-3"
                        style={{
                          color: isLightTheme ? "#1e1e2f" : "#fff",
                          textTransform: "uppercase",
                        }}
                      >
                        Cancel Repossession
                      </h5>

                      {/* Notes Textarea */}
                      <FormGroup className="mb-3">
                        <Label
                          className="mb-1"
                          style={{
                            fontSize: 12,
                            color: isLightTheme ? "#666" : "#a0a0b2",
                          }}
                        >
                          Notes:
                        </Label>
                        <Input
                          type="textarea"
                          rows="4"
                          value={cancelRepoForm.notes}
                          onChange={(e) =>
                            handleCancelRepoFormChange("notes", e.target.value)
                          }
                          placeholder="Enter notes..."
                          style={{
                            backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                            color: isLightTheme ? "#1e1e2f" : "#fff",
                          }}
                        />
                      </FormGroup>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          onClick={() => {
                            setRedeemRepoForm({ notes: "" });
                            setShowInformationName("");
                          }}
                          size="sm"
                          style={{ marginRight: 8 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          style={{ ...activeColorStyle, color: "white" }}
                          size="sm"
                          onClick={() => {
                            updateCalculusMutation.mutate({
                              id: bookingId,
                              data: {
                                repo: {
                                  is_out_for_repo: false,
                                  notes: cancelRepoForm.notes || "",
                                }
                              },
                            });
                            setShowInformationName("");
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : showInformationName === "redeem-repo" ? (
                    <div>
                      <h5
                        className="mb-1 mt-1"
                        style={{
                          color: isLightTheme ? "#1e1e2f" : "#fff",
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        Redeem Repo
                      </h5>

                      <FormGroup className="mb-3">
                        <Label
                          className="mb-1"
                          style={{
                            fontSize: 12,
                            color: isLightTheme ? "#666" : "#a0a0b2",
                          }}
                        >
                          Notes
                        </Label>
                        <Input
                          type="textarea"
                          rows="4"
                          value={redeemRepoForm.notes}
                          onChange={(e) =>
                            handleRedeemRepoFormChange("notes", e.target.value)
                          }
                          placeholder="Enter notes..."
                          style={{
                            backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                            color: isLightTheme ? "#1e1e2f" : "#fff",
                          }}
                        />
                      </FormGroup>

                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          onClick={() => setShowInformationName("")}
                          size="sm"
                          style={{ marginRight: 8 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          style={{ ...activeColorStyle, color: "white" }}
                          size="sm"
                          onClick={() => {
                            updateCalculusMutation.mutate({
                              id: bookingId,
                              data: {
                                repo: {
                                  is_repossession: false,
                                  notes: redeemRepoForm.notes || "",
                                },
                              },
                            });
                            setRedeemRepoForm({ notes: "" });
                            setShowInformationName("");
                          }}
                        >
                          Confirm
                        </Button>
                      </div>
                    </div>
                  ) : showInformationName === "record-repo" ? (
                    <div>
                      {/*
                        Repossessed durumunda ekran "Update Repo" gibi davranır:
                        alanlar salt-okunur kalsın.
                      */}
                      {(() => {
                        const isRepoUpdateReadOnly = status === "repossessed";
                        const isRecoveredAtOther =
                          recordRepoForm.recovered_at === "Other";
                        const disabledInputBg = "rgba(29, 37, 59, 0.05)";
                        return (
                          <>
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                        <h5
                          className="mb-0"
                          style={{
                            color: isLightTheme ? "#1e1e2f" : "#fff",
                            fontSize: 13,
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          {status === "repossessed"
                            ? "Update Repo"
                            : "Record Repo"}
                        </h5>
                        <div className="d-flex flex-shrink-0">
                          <Button
                            outline
                            color="primary"
                            size="sm"
                            onClick={() => setShowInformationName("")}
                            style={{ marginRight: 8 }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            style={{ ...activeColorStyle, color: "white" }}
                            disabled={isRepoUpdateReadOnly}
                            onClick={() => {
                              updateCalculusMutation.mutate({
                                id: bookingId,
                                data: {
                                  repo: {
                                    is_repossession: true,
                                    effective_date:
                                      formatDateForAPI(
                                        recordRepoForm.repo_effective_date
                                      ) || getCurrentDateForAPI(),
                                    was_obtained:
                                      recordRepoForm.how_repo_obtained || "",
                                    vehicle_location:
                                      recordRepoForm.vehicle_location ?? null,
                                    recovered_repo:
                                      recordRepoForm.recovered_by || null,
                                    recovered_at:
                                      recordRepoForm.recovered_at ?? null,
                                    repo_charge: toNumber(
                                      recordRepoForm.repo_charge
                                    ),
                                    notes: recordRepoForm.condition_notes || "",
                                    ...(isRecoveredAtOther
                                      ? {
                                        address_1: recordRepoForm.address_1 || "",
                                        address_2: recordRepoForm.address_2 || "",
                                        city: recordRepoForm.city || "",
                                        state: recordRepoForm.state?.id || null,
                                        zip_code: recordRepoForm.zip_code || "",
                                      }
                                      : {}),
                                  },
                                },
                              });
                              setShowInformationName("");
                            }}
                          >
                            Confirm
                          </Button>
                        </div>
                      </div>

                      <fieldset
                        disabled={isRepoUpdateReadOnly}
                        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
                      >
                      <Row>
                        <Col xs="12">
                          <Row className="g-2">
                            <Col md="6"  className="mb-2 ">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Repo Effective Date
                                </Label>
                                <ReactDatetime
                                  inputProps={{
                                    className: "form-control",
                                    placeholder: "Select Date",
                                  }}
                                  value={
                                    recordRepoForm.repo_effective_date
                                      ? formatDateForPicker(
                                        recordRepoForm.repo_effective_date
                                      )
                                      : ""
                                  }
                                  onChange={(date) =>
                                    handleRecordRepoFormChange(
                                      "repo_effective_date",
                                      date
                                    )
                                  }
                                  renderDay={renderDay}
                                  dateFormat="MM-DD-YYYY"
                                  timeFormat={false}
                                  closeOnSelect={true}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2 ">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  How Repo Was Obtained
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="how_repo_obtained"
                                  isSearchable
                                  isDisabled={isRepoUpdateReadOnly}
                                  closeMenuOnSelect={true}
                                  value={
                                    recordRepoForm.how_repo_obtained
                                      ? {
                                        value: recordRepoForm.how_repo_obtained,
                                        label:
                                          recordRepoHowObtainedOptions.find(
                                            (o) =>
                                              o.value ===
                                              recordRepoForm.how_repo_obtained
                                          )?.label ||
                                          recordRepoForm.how_repo_obtained,
                                      }
                                      : null
                                  }
                                  onChange={(option) =>
                                    handleRecordRepoFormChange(
                                      "how_repo_obtained",
                                      option?.value || ""
                                    )
                                  }
                                  options={recordRepoHowObtainedOptions}
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2 ">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Repo Charge
                                </Label>
                                <DollarInput
                                  name="repo_charge"
                                  value={recordRepoForm.repo_charge}
                                  onChange={(value) =>
                                    handleRecordRepoFormChange("repo_charge", value)
                                  }
                                  placeholder="$0.00"
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2 ">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Vehicle Location
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="vehicle_location"
                                  isSearchable
                                  isDisabled={isRepoUpdateReadOnly}
                                  closeMenuOnSelect={true}
                                  value={
                                    recordRepoForm.vehicle_location
                                      ? {
                                        value: recordRepoForm.vehicle_location,
                                        label:
                                          recordRepoLocationSelectOptions.find(
                                            (o) =>
                                              o.value ===
                                              recordRepoForm.vehicle_location
                                          )?.label ||
                                          recordRepoForm.vehicle_location,
                                      }
                                      : null
                                  }
                                  onChange={(option) =>
                                    handleRecordRepoFormChange(
                                      "vehicle_location",
                                      option?.value || ""
                                    )
                                  }
                                  options={recordRepoLocationSelectOptions}
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Recovered By
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="recovered_by"
                                  isSearchable
                                  isDisabled={isRepoUpdateReadOnly}
                                  closeMenuOnSelect={true}
                                  value={
                                    recordRepoForm.recovered_by
                                      ? {
                                        value: recordRepoForm.recovered_by,
                                        label:
                                          recordRepoAgentSelectOptions.find(
                                            (o) =>
                                              o.value ===
                                              recordRepoForm.recovered_by
                                          )?.label ||
                                          (typeof recordRepoForm.recovered_by ===
                                            "string" ||
                                          typeof recordRepoForm.recovered_by ===
                                            "number"
                                            ? String(recordRepoForm.recovered_by)
                                            : ""),
                                      }
                                      : null
                                  }
                                  onChange={(option) =>
                                    handleRecordRepoFormChange(
                                      "recovered_by",
                                      option?.value || ""
                                    )
                                  }
                                  options={recordRepoAgentSelectOptions}
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Recovered At
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="recovered_at"
                                  isSearchable={false}
                                  isDisabled={isRepoUpdateReadOnly}
                                  closeMenuOnSelect={true}
                                  value={
                                    recordRepoForm.recovered_at
                                      ? {
                                        value: recordRepoForm.recovered_at,
                                        label:
                                          recordRepoRecoveredAtOptions.find(
                                            (o) =>
                                              o.value ===
                                              recordRepoForm.recovered_at
                                          )?.label || recordRepoForm.recovered_at,
                                      }
                                      : null
                                  }
                                  onChange={(option) =>
                                    handleRecordRepoFormChange(
                                      "recovered_at",
                                      option?.value || ""
                                    )
                                  }
                                  options={recordRepoRecoveredAtOptions}
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </Col>
                        <Col xs="12" className="mt-2 mb-2">
                              <div
                                style={{
                                  borderTop: isLightTheme
                                    ? "1px solid #e0e0e0"
                                    : "1px solid rgba(255,255,255,0.12)",
                                }}
                              />
                            </Col>
                        <Col xs="12">
                          <Row className="g-2">
                            <Col xs="12" className="mb-2 " style={{ paddingLeft: 5, paddingRight: 5 }}>
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Condition Notes
                                </Label>
                                <Input
                                  type="textarea"
                                  rows={4}
                                  value={recordRepoForm.condition_notes}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "condition_notes",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter condition notes..."
                                  style={{
                                    backgroundColor: isLightTheme
                                      ? "#fff"
                                      : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Personal Property
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="personal_property"
                                  isSearchable={false}
                                  isDisabled={isRepoUpdateReadOnly}
                                  closeMenuOnSelect={true}
                                  value={recordRepoYesNoOptions.find(
                                    (o) =>
                                      o.value ===
                                      recordRepoForm.personal_property
                                  )}
                                  onChange={(option) =>
                                    handleRecordRepoFormChange(
                                      "personal_property",
                                      option?.value ?? false
                                    )
                                  }
                                  options={recordRepoYesNoOptions}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Notified
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="notified"
                                  isSearchable={false}
                                  isDisabled={isRepoUpdateReadOnly}
                                  closeMenuOnSelect={true}
                                  value={recordRepoYesNoOptions.find(
                                    (o) => o.value === recordRepoForm.notified
                                  )}
                                  onChange={(option) =>
                                    handleRecordRepoFormChange(
                                      "notified",
                                      option?.value ?? false
                                    )
                                  }
                                  options={recordRepoYesNoOptions}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Notified On
                                </Label>
                                <ReactDatetime
                                  inputProps={{
                                    className: "form-control",
                                    placeholder: "Select Date",
                                  }}
                                  value={
                                    recordRepoForm.notified_on
                                      ? formatDateForPicker(
                                        recordRepoForm.notified_on
                                      )
                                      : ""
                                  }
                                  onChange={(date) =>
                                    handleRecordRepoFormChange(
                                      "notified_on",
                                      date
                                    )
                                  }
                                  renderDay={renderDay}
                                  dateFormat="MM-DD-YYYY"
                                  timeFormat={false}
                                  closeOnSelect={true}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Days to Claim
                                </Label>
                                <Input
                                  type="text"
                                  value={recordRepoForm.days_to_claim}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "days_to_claim",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    backgroundColor: isRepoUpdateReadOnly
                                      ? disabledInputBg
                                      : isLightTheme
                                        ? "#fff"
                                        : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Claim Deadline
                                </Label>
                                <ReactDatetime
                                  inputProps={{
                                    className: "form-control",
                                    placeholder: "Select Date",
                                  }}
                                  value={
                                    recordRepoForm.claim_deadline
                                      ? formatDateForPicker(
                                        recordRepoForm.claim_deadline
                                      )
                                      : ""
                                  }
                                  onChange={(date) =>
                                    handleRecordRepoFormChange(
                                      "claim_deadline",
                                      date
                                    )
                                  }
                                  renderDay={renderDay}
                                  dateFormat="MM-DD-YYYY"
                                  timeFormat={false}
                                  closeOnSelect={true}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Description
                                </Label>
                                <Input
                                  type="text"
                                  value={recordRepoForm.description}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "description",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    backgroundColor: isRepoUpdateReadOnly
                                      ? disabledInputBg
                                      : isLightTheme
                                        ? "#fff"
                                        : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </Col>

                        <Col xs="12">
                          <Row className="g-2">
                          

                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Address 1
                                </Label>
                                <Input
                                  type="text"
                                  value={recordRepoForm.address_1}
                                  disabled={!isRecoveredAtOther}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "address_1",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    backgroundColor:
                                      isRepoUpdateReadOnly || !isRecoveredAtOther
                                        ? disabledInputBg
                                      : isLightTheme
                                        ? "#fff"
                                        : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  Address 2
                                </Label>
                                <Input
                                  type="text"
                                  value={recordRepoForm.address_2}
                                  disabled={!isRecoveredAtOther}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "address_2",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    backgroundColor:
                                      isRepoUpdateReadOnly || !isRecoveredAtOther
                                        ? disabledInputBg
                                      : isLightTheme
                                        ? "#fff"
                                        : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  City
                                </Label>
                                <Input
                                  type="text"
                                  value={recordRepoForm.city}
                                  disabled={!isRecoveredAtOther}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "city",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    backgroundColor:
                                      isRepoUpdateReadOnly || !isRecoveredAtOther
                                        ? disabledInputBg
                                        : isLightTheme
                                          ? "#fff"
                                          : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  State
                                </Label>
                                <StatesInput
                                  name="record_repo_state"
                                  value={recordRepoForm.state}
                                  activeColor={activeColor}
                                  isDisabled={
                                    isRepoUpdateReadOnly || !isRecoveredAtOther
                                  }
                                  onChange={(stateObject) =>
                                    handleRecordRepoFormChange(
                                      "state",
                                      stateObject
                                        ? {
                                          id: stateObject.id,
                                          abbreviation:
                                            stateObject.abbreviation,
                                        }
                                        : null
                                    )
                                  }
                                  placeholder="Select State"
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4" className="mb-2">
                              <FormGroup className="mb-0">
                                <Label
                                  className="mb-1"
                                  style={{
                                    fontSize: 12,
                                    color: isLightTheme ? "#666" : "#a0a0b2",
                                  }}
                                >
                                  ZIP Code
                                </Label>
                                <Input
                                  type="text"
                                  value={recordRepoForm.zip_code}
                                  disabled={!isRecoveredAtOther}
                                  onChange={(e) =>
                                    handleRecordRepoFormChange(
                                      "zip_code",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    backgroundColor:
                                      isRepoUpdateReadOnly || !isRecoveredAtOther
                                        ? disabledInputBg
                                      : isLightTheme
                                        ? "#fff"
                                        : "#2b2b3d",
                                    color: isLightTheme ? "#1e1e2f" : "#fff",
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      </fieldset>
                          </>
                        );
                      })()}
                    </div>
                  ) : showInformationName === "update-alert" ? (
                    <div>
                      <h5
                        className="mb-1 mt-1"
                        style={{
                          color: isLightTheme ? "#1e1e2f" : "#fff",
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        Update Alert
                      </h5>

                      <FormGroup check className="mb-3">
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={alertForm.useDefaultStyle}
                            onChange={(e) => handleAlertFormChange("useDefaultStyle", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          Use Default Style
                        </Label>
                      </FormGroup>

                      <FormGroup className="mb-3">
                        <Label
                          className="mb-1"
                          style={{
                            fontSize: 12,
                            color: isLightTheme ? "#666" : "#a0a0b2",
                          }}
                        >
                          Message
                        </Label>
                        <Input
                          type="text"
                          value={alertForm.content}
                          onChange={(e) =>
                            handleAlertFormChange("content", e.target.value)
                          }
                          placeholder=""
                        />
                      </FormGroup>

                      <Row>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label
                              className="mb-1"
                              style={{
                                fontSize: 12,
                                color: isLightTheme ? "#666" : "#a0a0b2",
                              }}
                            >
                              Font Color
                            </Label>
                            <div style={{ position: "relative" }}>
                              <Input
                                type="text"
                                style={{
                                  backgroundColor: alertForm.font_color,
                                  color: "#fff",
                                  textShadow: "0 0 2px #000",
                                  paddingRight: "40px"
                                }}
                                value={alertForm.font_color}
                                onChange={(e) =>
                                  handleAlertFormChange("font_color", e.target.value)
                                }
                                onClick={(e) => {
                                  // Removed auto-click to allow typing
                                }}
                                onDoubleClick={(e) => {
                                  if (fontColorRef.current) fontColorRef.current.click();
                                }}
                                disabled={alertForm.useDefaultStyle}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 10,
                                  cursor: "pointer"
                                }}
                                onClick={() => fontColorRef.current?.click()}
                              >
                                <MdEdit size={16} style={{ color: "#fff", filter: "drop-shadow(0 0 2px #000)" }} />
                              </div>
                              <input
                                type="color"
                                ref={fontColorRef}
                                value={alertForm.font_color || "#000000"}
                                onChange={(e) =>
                                  handleAlertFormChange("font_color", e.target.value)
                                }
                                style={{
                                  visibility: "hidden",
                                  position: "absolute",
                                  width: 0,
                                  height: 0
                                }}
                                disabled={alertForm.useDefaultStyle}
                              />
                            </div>
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label
                              className="mb-1"
                              style={{
                                fontSize: 12,
                                color: isLightTheme ? "#666" : "#a0a0b2",
                              }}
                            >
                              Background Color
                            </Label>
                            <div style={{ position: "relative" }}>
                              <Input
                                type="text"
                                style={{
                                  backgroundColor: alertForm.background_color,
                                  color: "#fff",
                                  textShadow: "0 0 2px #000",
                                  paddingRight: "40px"
                                }}
                                value={alertForm.background_color}
                                onChange={(e) =>
                                  handleAlertFormChange("background_color", e.target.value)
                                }
                                onClick={(e) => {
                                  // Removed auto-click to allow typing
                                }}
                                onDoubleClick={(e) => {
                                  if (backgroundColorRef.current) backgroundColorRef.current.click();
                                }}
                                disabled={alertForm.useDefaultStyle}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 10,
                                  cursor: "pointer"
                                }}
                                onClick={() => backgroundColorRef.current?.click()}
                              >
                                <MdEdit size={16} style={{ color: "#fff", filter: "drop-shadow(0 0 2px #000)" }} />
                              </div>
                              <input
                                type="color"
                                ref={backgroundColorRef}
                                value={alertForm.background_color || "#ffffff"}
                                onChange={(e) =>
                                  handleAlertFormChange("background_color", e.target.value)
                                }
                                style={{
                                  visibility: "hidden",
                                  position: "absolute",
                                  width: 0,
                                  height: 0
                                }}
                                disabled={alertForm.useDefaultStyle}
                              />
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          onClick={() => setShowInformationName("")}
                          size="sm"
                          className="mr-2"
                        >
                          Cancel
                        </Button>
                        <Button
                          style={{ ...activeColorStyle, color: "white" }}
                          size="sm"
                          onClick={() => {
                            updateCalculusMutation.mutate({
                              id: bookingId,
                              data: {
                                alert_default_style: alertForm.useDefaultStyle,
                                alert_message: alertForm.content,
                                alert_font_color: alertForm.font_color,
                                alert_background_color: alertForm.background_color
                              },
                            });
                            setShowInformationName("");
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {drawerActiveTab === "promiseToPay" && (
                <div>
                  <div className="d-flex align-items-center width-100 justify-content-end mb-3">
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: 12 }}
                    >
                      <Button
                        className="btn btn-sm"
                        onClick={handleAddPromiseRow}
                      >
                        {t("common.add")}
                      </Button>
                      <Button
                        style={{
                          ...activeColorStyle,
                          color: "white",
                          opacity: isSavePromisesDisabled ? 0.6 : 1,
                          cursor: isSavePromisesDisabled
                            ? "not-allowed"
                            : "pointer",
                        }}
                        className="btn btn-sm"
                        onClick={handleSavePromises}
                        disabled={isSavePromisesDisabled}
                      >
                        {t("common.save")}
                      </Button>
                    </div>
                  </div>
                  <div
                    className="d-flex flex-wrap align-items-center justify-content-between mb-3"
                    style={{ fontSize: 12 }}
                  >
                    <div>
                      <div>
                        Summary: <b>{promiseSummary?.kept} </b>Kept / <b>{promiseSummary?.broken}</b> Broken / <b>{promiseSummary?.pending}</b> Pending
                        / <b>{promiseSummary?.total}</b> Total / <b>{promiseSummary?.total_amount}</b>
                      </div>
                      <div>
                        Last Paid Date: <b>{promiseSummary?.last_promise_date ? formatDateForDisplay(promiseSummary?.last_promise_date) : "-"}</b> for <b>{formatPriceWithDollar(promiseSummary?.last_promise_amount)}</b>{" "}
                        &nbsp; Payment: <b>{promiseSummary?.payment_frequency} Bi-Weekly</b>
                      </div>
                    </div>
                    <div>
                      Past Due:{" "}
                      <span style={{ color: "#ff5c5c", fontWeight: 700 }}>
                        $0.00
                      </span>{" "}
                      &nbsp; Promised:{" "}
                      <span style={{ color: "#00d97e", fontWeight: 700 }}>
                        $0.00
                      </span>{" "}
                      &nbsp; Need:{" "}
                      <span style={{ color: "#1890ff", fontWeight: 700 }}>
                        $0.00
                      </span>
                    </div>
                  </div>
                  {promisePayments.length === 0 && (
                    <div className="text-muted mb-3">
                      Click Add to create a new promise.
                    </div>
                  )}

                  {promiseRows.map((row) => (
                    <div
                      key={row.id}
                      className="mb-3"
                      style={{
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <div style={{ padding: 12 }}>
                        <Row form>
                          <Col md="4" >
                            <FormGroup>
                              <Label className="mb-1">Promise Type</Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select"
                                name={`promiseType-${row.id}`}
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  promiseTypeOptions.find(
                                    (option) => option.value === row.promiseType
                                  ) || null
                                }
                                onChange={(option) =>
                                  handlePromiseFieldChange(
                                    row.id,
                                    "promiseType",
                                    option?.value || ""
                                  )
                                }
                                options={promiseTypeOptions}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="4" >
                            <FormGroup>
                              <Label className="mb-1">Date</Label>
                              <ReactDatetime
                                inputProps={{
                                  className: "form-control",
                                  placeholder: "Select Date",
                                  style: { borderRadius: 0 },
                                }}
                                value={
                                  row.date
                                    ? formatDateForPicker(row.date)
                                    : ""
                                }
                                isValidDate={(currentDate) => currentDate.isSameOrAfter(moment().startOf("day"))}
                                onChange={(date) => {
                                  const formattedDate = formatDateForAPI(date) || "";
                                  handlePromiseFieldChange(row.id, "date", formattedDate);
                                }}
                                renderDay={renderDay}
                                dateFormat="MM-DD-YYYY"
                                timeFormat={false}
                                closeOnSelect={true}
                              />
                            </FormGroup>
                          </Col>
                          {/* <Col md="4" className="mb-2">
                            <FormGroup>
                              <Label className="mb-1">Balance Type</Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select"
                                name={`balanceType-${row.id}`}
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  balanceTypeOptions.find(
                                    (option) => option.value === row.balanceType
                                  ) || null
                                }
                                onChange={(option) =>
                                  handlePromiseFieldChange(
                                    row.id,
                                    "balanceType",
                                    option?.value || ""
                                  )
                                }
                                options={balanceTypeOptions}
                              />
                            </FormGroup>
                          </Col> */}
                          <Col md="4">
                            <FormGroup>
                              <Label className="mb-1">Amount</Label>
                              <Input
                                style={{ borderRadius: 0, fontSize: "14px" }}
                                type="text"
                                value={row.amount}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(
                                    /[^\d.]/g,
                                    ""
                                  );
                                  handlePromiseFieldChange(
                                    row.id,
                                    "amount",
                                    raw
                                  );
                                }}
                                onBlur={() => {
                                  handlePromiseFieldChange(
                                    row.id,
                                    "amount",
                                    formatPriceWithDollar(row.amount)
                                  );
                                }}
                                onFocus={() => {
                                  const raw = String(row.amount || "").replace(
                                    /[^\d.]/g,
                                    ""
                                  );
                                  handlePromiseFieldChange(
                                    row.id,
                                    "amount",
                                    raw
                                  );
                                }}
                                placeholder="$0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="4">
                            <FormGroup>
                              <Label className="mb-1">Delivery Method</Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select"
                                name={`deliveryMethod-${row.id}`}
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  deliveryMethodOptions.find(
                                    (option) =>
                                      option.value === row.deliveryMethod
                                  ) || null
                                }
                                onChange={(option) =>
                                  handlePromiseFieldChange(
                                    row.id,
                                    "deliveryMethod",
                                    option?.value || ""
                                  )
                                }
                                options={deliveryMethodOptions}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="4" >
                            <FormGroup>
                              <Label className="mb-1">Payment Form</Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select"
                                name={`paymentForm-${row.id}`}
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  paymentFormOptions.find(
                                    (option) => option.value === row.paymentForm
                                  ) || null
                                }
                                onChange={(option) =>
                                  handlePromiseFieldChange(
                                    row.id,
                                    "paymentForm",
                                    option?.value || ""
                                  )
                                }
                                options={paymentFormOptions}
                              />
                            </FormGroup>
                          </Col>
                          <Col
                            md="12"
                            className="d-flex align-items-end justify-content-end mb-2"
                          >
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleRemovePromiseRow(row.id)}
                            >
                              Remove
                            </button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  ))}
                  <div className="mb-3">
                    {isPromisePaymentsLoading ? (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" className="mr-2" /> Loading
                        promises...
                      </div>
                    ) : promisePayments.length === 0 ? (
                      <div className="text-muted">
                        No promise payments recorded yet.
                      </div>
                    ) : (
                      promisePayments.map((promise) => {
                        const statusColor = getPromiseStatusColor(
                          promise.promise_status
                        );

                        const promiseDetails = [
                          {
                            label: "Promise Type",
                            value: promise.promise_type || "-",
                          },
                          {
                            label: "Date",
                            value: promise.date
                              ? formatDateForDisplay(promise.date)
                              : "-",
                          },
                          {
                            label: "Amount",
                            value: formatPriceWithDollar(promise.amount),
                          },
                          {
                            label: "Delivery Method",
                            value: promise.delivery_method || "-",
                          },
                          {
                            label: "Payment Form",
                            value: promise.payment_form || "-",
                          },
                        ];

                        const noteValue = promise.note?.trim();
                        const showNote = Boolean(noteValue);

                        return (
                          <div
                            key={promise.id}
                            className="mb-2"
                            style={{
                              border: "1px solid rgba(255,255,255,0.12)",
                              borderLeft: `6px solid ${statusColor}`,
                              backgroundColor: isLightTheme
                                ? "#f4f5f7"
                                : "rgba(255,255,255,0.04)",
                            }}
                          >
                            <div style={{ padding: 8 }}>
                              <div
                                className="d-flex flex-column"
                                style={{ gap: 8 }}
                              >
                                <div
                                  className="d-flex align-items-center flex-wrap"
                                  style={{ gap: 10 }}
                                >
                                  <div
                                    style={{
                                      width: 33,
                                      height: 33,
                                      border: `2px solid ${statusColor}`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: 700,
                                      fontSize: 14,
                                      color: statusColor,
                                      backgroundColor: isLightTheme
                                        ? "#ffffff"
                                        : "rgba(255,255,255,0.08)",
                                    }}
                                  >
                                    P
                                  </div>
                                  <div
                                    className="d-flex flex-wrap align-items-center"
                                    style={{ gap: 8, flex: 1 }}
                                  >
                                    {promiseDetails.map((detail) => (
                                      <div
                                        key={`${promise.id}-${detail.label}`}
                                        style={{
                                          minWidth: 140,
                                        }}
                                      >
                                        <CardTitle
                                          className="mb-1 font-weight-bold"
                                          style={{ fontSize: 12 }}
                                        >
                                          {detail.label}
                                        </CardTitle>
                                        <div style={{ fontSize: 11 }}>
                                          {detail.value}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {promise.promise_status === "pending" && (
                                    <Button
                                      color="link"
                                      className="p-0"
                                      disabled={
                                        deletingPromiseId === promise.id &&
                                        deletePromisePayMutation.isPending
                                      }
                                      onClick={() =>
                                        handleDeletePromise(promise.id)
                                      }
                                      style={{
                                        color: "#ff5c5c",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <RiDeleteBinLine size={20} />
                                    </Button>
                                  )}
                                </div>
                                {showNote && (
                                  <div
                                    className="d-flex flex-column"
                                    style={{ paddingLeft: 60 }}
                                  >
                                    <CardTitle
                                      className="mb-1 font-weight-bold"
                                      style={{ fontSize: 13 }}
                                    >
                                      Note
                                    </CardTitle>
                                    <div style={{ fontSize: 12 }}>
                                      {noteValue}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              {drawerActiveTab === "post" && status === "closed" && postSubView !== "payoff-receipt" && (
                <div style={{ textAlign: "center", marginTop: "40px" }}>
                  <h4 style={{ color: "red", fontWeight: "bold", fontSize: "16px" }}>This balance is paid off/closed!</h4>
                </div>
              )}
              {drawerActiveTab === "post" && (status !== "closed" || postSubView === "payoff-receipt") && (
                <div>
                  {postSubView === "menu" && (
                    <div className="d-flex flex-column" style={{ gap: '10px' }}>
                      {[
                        ...(canPostPayments && !isChargeOffAccount
                          ? [{ label: "Post a Payment", action: () => setPostSubView("payment"), icon: <MdPayment size={20} /> }]
                          : []),
                          ...(canPostPayoff && !isChargeOffAccount
                            ? [{ label: "Post a Payoff", action: () => setPostSubView("payoff"), icon: <MdCheckCircle size={20} /> }]
                            : []),
                            ...(canPostChargeOff && !isChargeOffAccount
                              ? [{ label: "Post a Charge Off", action: () => setPostSubView("charge-off"), icon: <MdMoneyOff size={20} /> }]
                              : []),
                            ...(canPostChargeOff && isChargeOffAccount
                              ? [{
                                label: "Post Charge Off Reversal",
                                action: () => setPostSubView("charge-off-reversal"),
                                icon: <MdRestore size={20} />,
                              }]
                              : []),
                        ...(canPostExtensions && !isChargeOffAccount
                          ? [{ label: "Post an Extension", action: () => setPostSubView("extension"), icon: <MdDateRange size={20} /> }]
                          : []),
                        ...(!isChargeOffAccount
                          ? [{ label: "Post Account Write Down", action: () => setPostSubView("write-down"), icon: <MdTrendingDown size={20} /> }]
                          : []),
                        ...(canCreateSideNote && !isChargeOffAccount
                          ? [{ label: "Create a Side Note", action: () => setPostSubView("side-note"), icon: <MdNoteAdd size={20} /> }]
                          : []),
                        ...(!isChargeOffAccount
                          ? [{
                          label: "Late Fee Accrual",
                          action: () => {
                            setLateFeeAccrualForm(
                              createLateFeeAccrualFormDefaults(calculusData)
                            );
                            setPostSubView("late-fee-accrual");
                          },
                          icon: <MdAttachMoney size={20} />,
                        }]
                          : []),
                        // { label: "Skip AutoPay", action: () => { }, icon: <MdNextPlan size={20} /> },
                      ].map((item, index) => (
                        <div
                          key={index}
                          onClick={item.action}
                          className="d-flex align-items-center justify-content-between"
                          style={{
                            padding: "13px 15px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            border: isLightTheme
                              ? "1px solid #e0e0e0"
                              : "1px solid rgba(255, 255, 255, 0.1)",
                            backgroundColor: isLightTheme
                              ? "#fff"
                              : "rgba(255, 255, 255, 0.05)",
                            color: isLightTheme ? "#333" : "#fff",
                            transition: "all 0.2s ease",
                            boxShadow: isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = isLightTheme
                              ? "#f8f9fa"
                              : "rgba(255, 255, 255, 0.1)";
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = isLightTheme ? "0 4px 8px rgba(0,0,0,0.05)" : "0 4px 8px rgba(0,0,0,0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isLightTheme
                              ? "#fff"
                              : "rgba(255, 255, 255, 0.05)";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none";
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <span style={{ marginRight: '16px', opacity: 0.8 }}>{item.icon}</span>
                            <span>{item.label}</span>
                          </div>
                          <MdChevronRight size={20} style={{ opacity: 0.5 }} />
                        </div>
                      ))}
                      {!isChargeOffAccount && canPostReversal && (
                        <>
                      <hr style={{ margin: '5px 0', borderColor: isLightTheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }} />
                      <div
                        onClick={() => setPostSubView("reversal")}
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          padding: "13px 15px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: isLightTheme
                            ? "1px solid #e0e0e0"
                            : "1px solid rgba(255, 255, 255, 0.1)",
                          backgroundColor: isLightTheme
                            ? "#fff"
                            : "rgba(255, 255, 255, 0.05)",
                          color: isLightTheme ? "#333" : "#fff",
                          transition: "all 0.2s ease",
                          boxShadow: isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isLightTheme
                            ? "#f8f9fa"
                            : "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow = isLightTheme ? "0 4px 8px rgba(0,0,0,0.05)" : "0 4px 8px rgba(0,0,0,0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isLightTheme
                            ? "#fff"
                            : "rgba(255, 255, 255, 0.05)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none";
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <span style={{ marginRight: '16px' }}><MdRestore size={20} /></span>
                          <span>Post Reversal</span>
                        </div>
                        <MdChevronRight size={20} style={{ opacity: 0.5 }} />
                      </div>
                        </>
                      )}
                      {!isChargeOffAccount && (
                      <>
                      <hr style={{ margin: '5px 0', borderColor: isLightTheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }} />
                      <div
                        onClick={() => {
                          setMiscFeeAdjustmentForm(createMiscFeeAdjustmentFormDefaults(calculusData));
                          setPostSubView("misc-fee-adjustment");
                        }}
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          padding: "13px 15px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: isLightTheme
                            ? "1px solid #e0e0e0"
                            : "1px solid rgba(255, 255, 255, 0.1)",
                          backgroundColor: isLightTheme
                            ? "#fff"
                            : "rgba(255, 255, 255, 0.05)",
                          color: isLightTheme ? "#333" : "#fff",
                          transition: "all 0.2s ease",
                          boxShadow: isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isLightTheme
                            ? "#f8f9fa"
                            : "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow = isLightTheme ? "0 4px 8px rgba(0,0,0,0.05)" : "0 4px 8px rgba(0,0,0,0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isLightTheme
                            ? "#fff"
                            : "rgba(255, 255, 255, 0.05)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none";
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <span style={{ marginRight: '16px' }}><MdReceipt size={20} /></span>
                          <span>Post Misc Fee Adjustment</span>
                        </div>
                        <MdChevronRight size={20} style={{ opacity: 0.5 }} />
                      </div>
                      </>
                      )}
                    </div>
                  )}

                  {postSubView === "payment-success" && (
                    <div className="pt-2">
                      <div className="text-center py-4">
                        {lastPostedPayment?.receipt_id && (
                          <p className="text-muted" style={{ fontSize: 13 }}>Receipt # {lastPostedPayment.receipt_id}</p>
                        )}
                        <MdCheckCircle size={48} color="#2dce89" />
                        <h5 className="mt-3 font-weight-bold" style={{ color: "#2dce89" }}>Payment Posted Successfully!</h5>
                      </div>

                      <div className="d-flex mt-3" >
                        <Button
                          outline
                          color="info"
                          style={{ padding: "6px 12px", fontSize: "13px", fontWeight: "600", backgroundColor: "#f8fdfd", width: "33%" }}
                          size="sm"
                          onClick={() => setPaymentReceiptOpen(true)}
                        >
                          VIEW/PRINT RECEIPT
                        </Button>
                        <Button
                          outline
                          color="info"
                          style={{ padding: "6px 12px", fontSize: "13px", fontWeight: "600", backgroundColor: "#f8fdfd", width: "33%" }}
                          onClick={() => {
                            setSendEmailData({
                              transaction_id: lastPostedPayment?.id || lastPostedPayment?.documentId,
                              transaction: lastPostedPayment,
                              accountData,
                            });
                            setSendView("email");
                            setDrawerActiveTab("send");
                          }}
                        >
                          EMAIL RECEIPT
                        </Button>
                        <Button
                          outline
                          color="info"
                          size="sm"
                          style={{ padding: "6px 12px", fontSize: "13px", fontWeight: "600", backgroundColor: "#f8fdfd", width: "33%" }}
                          onClick={() => {
                            setLastPostedPayment(null);
                            setPostSubView("payment");
                            setPaymentForm(createPaymentFormDefaults());
                            setSelectedPaymentPostType("");
                          }}
                        >
                          POST ANOTHER PAYMENT
                        </Button>
                      </div>

                      {paymentReceiptOpen && (
                        <ReceiptDrawer
                          isOpen={paymentReceiptOpen}
                          onClose={() => setPaymentReceiptOpen(false)}
                          transaction={lastPostedPayment}
                          accountData={accountData}
                          isLightTheme={isLightTheme}
                        />
                      )}
                    </div>
                  )}

                  {postSubView === "payment" && !isChargeOffAccount && (
                    <div>
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>Post Payment</h5>
                      <Row>
                        {/* 1. Date (Disabled) */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Date</Label>
                            <ReactDatetime
                              inputProps={{
                                className: "form-control",
                                placeholder: "Select Date",
                                style: { borderRadius: 0 },
                                disabled: true
                              }}
                              value={
                                paymentForm.date
                                  ? formatDateForPicker(paymentForm.date)
                                  : formatDateForPicker(getCurrentDateForAPI())
                              }
                              dateFormat="MM-DD-YYYY"
                              timeFormat={false}
                              closeOnSelect={true}
                            />
                          </FormGroup>
                        </Col>
                        {/* 2. Action Type */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Action Type</Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Select Action"
                              name="action_type"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={
                                [
                                  { value: "take_payment", label: "Take a Payment" },
                                  { value: "waive_late_fees", label: "Waive Late Fees Only" },
                                ].find((o) => o.value === paymentForm.action_type) || null
                              }
                              onChange={(option) =>
                                handlePaymentInputChange("action_type", option?.value || "")
                              }
                              options={[
                                { value: "take_payment", label: "Take a Payment" },
                                { value: "waive_late_fees", label: "Waive Late Fees Only" },
                              ]}
                            />
                          </FormGroup>
                        </Col>
                        {/* 3. Delivery Method */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Delivery Method</Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Select"
                              name="delivery_method"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={
                                deliveryMethodOptions.find(
                                  (option) =>
                                    option.value === paymentForm.delivery_method
                                ) || null
                              }
                              onChange={(option) => {
                                handlePaymentInputChange("delivery_method", option?.value || "");
                                handlePaymentInputChange("payment_form", "");
                              }}
                              options={deliveryMethodOptions}
                            />
                          </FormGroup>
                        </Col>
                        {/* 4. Payment Form */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Payment Form</Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Select"
                              name="payment_form"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              isDisabled={!paymentForm.delivery_method}
                              value={
                                filteredPostPaymentOptions.find(
                                  (option) =>
                                    option.value === paymentForm.payment_form
                                ) || null
                              }
                              onChange={(option) =>
                                handlePaymentInputChange("payment_form", option?.value || "")
                              }
                              options={filteredPostPaymentOptions}
                            />
                          </FormGroup>
                        </Col>
                        {/* 5. Effect Date */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Effect Date</Label>
                            <ReactDatetime
                              inputProps={{
                                className: "form-control",
                                placeholder: "Select Date",
                                style: { borderRadius: 0 },
                              }}
                              value={
                                paymentForm.effect_date
                                  ? formatDateForPicker(paymentForm.effect_date)
                                  : formatDateForPicker(getCurrentDateForAPI())
                              }
                              onChange={(date) => {
                                const formattedDate = formatDateForAPI(date) || "";
                                handlePaymentInputChange("effect_date", formattedDate);
                              }}
                              renderDay={renderDay}
                              dateFormat="MM-DD-YYYY"
                              timeFormat={false}
                              closeOnSelect={true}
                              isValidDate={(current) =>
                                current.isSameOrBefore(moment().endOf("day"))
                              }
                            />
                          </FormGroup>
                        </Col>
                        {/* 6. Amount */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Amount</Label>
                            <DollarInput
                              name="amount"
                              className="form-control"
                              value={paymentForm.amount}
                              onChange={(value) =>
                                handlePaymentInputChange("amount", value)
                              }
                              placeholder="$0.00"
                              style={{ borderRadius: 0, fontSize: "14px" }}
                            />
                          </FormGroup>
                        </Col>
                        {/* 7. Amount Received */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Amount Received</Label>
                            <DollarInput
                              name="amount_received"
                              className="form-control"
                              value={paymentForm.amount_received}
                              onChange={(value) =>
                                handlePaymentInputChange("amount_received", value)
                              }
                              placeholder="$0.00"
                              style={{ borderRadius: 0, fontSize: "14px" }}
                            />
                          </FormGroup>
                        </Col>
                        {/* 8. Change Due (readonly, calculated) */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Change Due</Label>
                            <Input
                              type="text"
                              readOnly
                              className="form-control"
                              value={(() => {
                                const received = parseFloat(String(paymentForm.amount_received).replace(/[^0-9.]/g, "")) || 0;
                                const amount = parseFloat(String(paymentForm.amount).replace(/[^0-9.]/g, "")) || 0;
                                const change = received - amount;
                                return change >= 0 ? formatPriceWithDollar(change) : "$0.00";
                              })()}
                              style={{ borderRadius: 0, fontSize: "14px", backgroundColor: "transparent", cursor: "default" }}
                            />
                          </FormGroup>
                        </Col>
                        {/* 9. Ref Number */}
                        <Col md="4" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Reference Number</Label>
                            <Input
                              type="text"
                              className="form-control"
                              value={paymentForm.ref_number}
                              onChange={(e) =>
                                handlePaymentInputChange("ref_number", e.target.value)
                              }
                              placeholder="Ref #"
                            />
                          </FormGroup>
                        </Col>
                        {/* 9. Comments */}
                        <Col md="8" className="mb-1">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>Comments</Label>
                            <div style={{ position: "relative" }}>
                              {commentSuggestionsOpen &&
                                filteredCommentSuggestions.length > 0 && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      left: 0,
                                      right: 0,
                                      top: "100%",
                                      marginTop: 2,
                                      maxHeight: 160,
                                      overflowY: "auto",
                                      zIndex: 20,
                                      background: isLightTheme ? "#fff" : "#27293d",
                                      border: isLightTheme
                                        ? "1px solid #ced4da"
                                        : "1px solid rgba(255,255,255,0.2)",
                                      boxShadow: isLightTheme
                                        ? "0 2px 8px rgba(0,0,0,0.12)"
                                        : "0 2px 8px rgba(0,0,0,0.4)",
                                    }}
                                  >
                                    {filteredCommentSuggestions.map((item, idx) => (
                                      <button
                                        key={`${item}-${idx}`}
                                        type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          selectCommentSuggestion(item);
                                        }}
                                        onMouseEnter={() =>
                                          setCommentSuggestionHighlight(idx)
                                        }
                                        style={{
                                          display: "block",
                                          width: "100%",
                                          textAlign: "left",
                                          padding: "6px 10px",
                                          border: "none",
                                          background:
                                            commentSuggestionHighlight === idx
                                              ? activeColorStyle?.borderColor ||
                                                "#1d8cf8"
                                              : "transparent",
                                          color:
                                            commentSuggestionHighlight === idx
                                              ? "#fff"
                                              : isLightTheme
                                                ? "#333"
                                                : "#eee",
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: "pointer",
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {item}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              <Input
                                type="text"
                                className="form-control"
                                value={paymentForm.note}
                                onChange={(e) => {
                                  handlePaymentInputChange("note", e.target.value);
                                  setCommentSuggestionsOpen(true);
                                  setCommentSuggestionHighlight(-1);
                                }}
                                onFocus={openCommentSuggestions}
                                onBlur={() => {
                                  commentBlurTimerRef.current = setTimeout(() => {
                                    closeCommentSuggestions();
                                  }, 150);
                                }}
                                onKeyDown={handleCommentSuggestionKeyDown}
                                placeholder="Optional comments"
                                autoComplete="off"
                              />
                            </div>
                          </FormGroup>
                        </Col>
                        <Col md="12" className="d-flex justify-content-end">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleSubmitPayment}
                            disabled={isPostPaymentDisabled}
                            style={{
                              ...(activeColorStyle || {}),
                              opacity: isPostPaymentDisabled ? 0.6 : 1,
                              border: "none",
                            }}
                          >
                            {createPaymentMutation.isPending
                              ? "Posting..."
                              : "Post Payment"}
                          </Button>
                        </Col>
                      </Row>

                      {/* Remaining Payments Table */}
                      {(() => {
                        const tableRows = postPaymentTableRows;
                        return (
                          <div style={{ marginTop: 16 }}>
                            <Row className="mb-2">
                              <Col md="4">
                                <FormGroup className="mb-0">
                                  <Label className="mb-1" style={{ fontSize: 12 }}>Payment Type</Label>
                                  <DrawerSelect
                                    activeColor={activeColor}
                                    className="react-select"
                                    placeholder="All Payments"
                                    name="selected_payment_post_type"
                                    isSearchable={false}
                                    closeMenuOnSelect={true}
                                    value={
                                      PAYMENT_POST_TYPE_OPTIONS.find(
                                        (option) => option.value === selectedPaymentPostType
                                      ) || PAYMENT_POST_TYPE_OPTIONS[0]
                                    }
                                    onChange={(option) =>
                                      setSelectedPaymentPostType(option?.value ?? "")
                                    }
                                    options={PAYMENT_POST_TYPE_OPTIONS}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            {remainingPaymentsList.length === 0 ? (
                              <div className="text-muted" style={{ fontSize: 13, textAlign: "center", padding: "12px 0" }}>
                                No remaining payments.
                              </div>
                            ) : tableRows.length === 0 ? (
                              <div className="text-muted" style={{ fontSize: 13, textAlign: "center", padding: "12px 0" }}>
                                No payments match the selected type.
                              </div>
                            ) : (
                              <div style={{ maxHeight: 250, overflowY: "auto", position: "relative" }}>
                                <table className="table table-sm mb-0" style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                                  <thead>
                                    <tr>
                                      {[
                                        "Due Date",
                                        "Pmt Type",
                                        "Sched Amt Due",
                                        "Sched Late Fee Due",
                                        "Waive Late Fee",
                                        "Sched Total Due",
                                        "Sched Late Fee Appld",
                                        "Sched Pmt Appld",
                                        "Sched Total Appld",
                                        "Sched Remain Due",
                                      ].map((h) => (
                                        <th key={h} style={{
                                          backgroundColor: isLightTheme ? "#f8f9fa" : "#2a2a3a",
                                          color: isLightTheme ? "#444" : "#ccc",
                                          fontWeight: 700,
                                          padding: "6px 8px",
                                          fontSize: 11,
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 1,
                                          borderBottom: isLightTheme ? "2px solid #dee2e6" : "2px solid #444",
                                          whiteSpace: "normal",
                                          textAlign: "center",
                                          verticalAlign: "bottom",
                                          lineHeight: 1.2,
                                        }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tableRows.map(({ item, originalIdx: idx }) => {
                                      const dueDate = item.due_date || item.date || null;
                                      const pmtType = item.description || item.note || "";
                                      const daysPastDue = item.day_past_due ?? 0;
                                      const schedAmt = item.pmt_amount ?? item.amount ?? 0;
                                      const schedLateFee = getRemainingLateFeeDue(item);
                                      const schedTotal = item.total_due ?? (parseFloat(schedAmt) + parseFloat(schedLateFee));
                                      const defaultLateFeeAppld = item.late_fee_applied ?? 0;
                                      const pmtAppld = item.payment_applied ?? 0;
                                      const enteredLateFeeAppld = lateFeeAppldValues[idx] !== undefined
                                        ? parseAppldInputValue(lateFeeAppldValues[idx])
                                        : parseAppldInputValue(defaultLateFeeAppld);
                                      const enteredPmtAppld = pmtAppldValues[idx] !== undefined
                                        ? parseAppldInputValue(pmtAppldValues[idx])
                                        : parseFloat(pmtAppld);
                                      const canEditLateFeeAppld = schedLateFee > 0.009;
                                      const canWaiveLateFee =
                                        WAIVE_LATE_FEE_ROW_CHECKBOX_ENABLED && schedLateFee > 0.009;
                                      const isWaiveLateFeeChecked =
                                        waiveLateFeeValues[idx] ?? !!item.waive_late_fee;
                                      const canEditLateFeeAppldInput =
                                        canEditLateFeeAppld && !isWaiveLateFeeChecked;
                                      const effectiveLateFeeDue = isWaiveLateFeeChecked ? 0 : schedLateFee;
                                      const effectiveSchedTotal =
                                        parseFloat(schedAmt) + parseFloat(effectiveLateFeeDue);
                                      const totalAppld = enteredLateFeeAppld + enteredPmtAppld;
                                      const remainDue =
                                        effectiveSchedTotal - enteredPmtAppld - enteredLateFeeAppld;

                                      const lateDay = Number(daysPastDue) || 0;
                                      let dateBg = "#28a745";
                                      if (lateDay > 0) dateBg = "#f6ad55";
                                      if (lateDay > 15) dateBg = "#f6a04d";
                                      if (lateDay > 30) dateBg = "#d97706";
                                      if (lateDay > 60) dateBg = "#bf6d00";
                                      if (lateDay >= 90) dateBg = "#d66d75";
                                      if (lateDay >= 120) dateBg = "#ef5350";

                                      const formatDate = (d) => {
                                        if (!d) return "-";
                                        const dt = new Date(d);
                                        return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}/${dt.getUTCFullYear()}`;
                                      };

                                      const rowStyle = { borderBottom: isLightTheme ? "1px solid #dee2e6" : "1px solid #444" };
                                      const td = { padding: "6px 8px", fontSize: 11, color: isLightTheme ? "#333" : "#ddd", textAlign: "center", verticalAlign: "middle" };
                                      const tdInput = { ...td, padding: "4px 6px" };

                                      return (
                                        <tr key={idx} style={rowStyle}>
                                          <td style={{ ...td, padding: "4px 6px" }}>
                                            <span style={{
                                              display: "inline-block",
                                              backgroundColor: dateBg,
                                              color: "#fff",
                                              fontWeight: 700,
                                              fontSize: 11,
                                              padding: "3px 7px",
                                              whiteSpace: "nowrap",
                                            }}>
                                              {formatDate(dueDate)}
                                            </span>
                                          </td>
                                          <td style={{ ...td, textAlign: "left", color: isLightTheme ? "#2f80ed" : "#0092e0", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            title={pmtType}>
                                            {pmtType.length > 12 ? pmtType.slice(0, 11) + "\u2026" : pmtType || "-"}
                                          </td>
                                          <td style={td}>{formatPriceWithDollar(schedAmt)}</td>
                                          <td style={td}>
                                            {isWaiveLateFeeChecked ? (
                                              <span style={{ textDecoration: "line-through", opacity: 0.6 }}>
                                                {formatPriceWithDollar(schedLateFee)}
                                              </span>
                                            ) : (
                                              formatPriceWithDollar(schedLateFee)
                                            )}
                                          </td>
                                          <td style={td}>
                                            {canWaiveLateFee ? (
                                              <input
                                                type="checkbox"
                                                checked={isWaiveLateFeeChecked}
                                                onChange={(e) => {
                                                  const checked = e.target.checked;
                                                  setWaiveLateFeeValues((prev) => ({
                                                    ...prev,
                                                    [idx]: checked,
                                                  }));
                                                  if (checked) {
                                                    setLateFeeAppldValues((prev) => {
                                                      const next = { ...prev };
                                                      delete next[idx];
                                                      return next;
                                                    });
                                                  }
                                                }}
                                                style={{ cursor: "pointer" }}
                                              />
                                            ) : (
                                              <input
                                                type="checkbox"
                                                disabled
                                                checked={false}
                                                style={{ cursor: "not-allowed", opacity: 0.7 }}
                                              />
                                            )}
                                          </td>
                                          <td style={td}>{formatPriceWithDollar(effectiveSchedTotal)}</td>
                                          <td style={tdInput}>
                                            {canEditLateFeeAppldInput ? (
                                              <input
                                                type="text"
                                                value={
                                                  lateFeeAppldValues[idx] !== undefined
                                                    ? lateFeeAppldValues[idx]
                                                    : formatPriceWithDollar(defaultLateFeeAppld)
                                                }
                                                onChange={(e) => {
                                                  const raw = e.target.value.replace(/[^0-9.]/g, "");
                                                  setLateFeeAppldValues((prev) => ({
                                                    ...prev,
                                                    [idx]: raw ? "$" + raw : "",
                                                  }));
                                                }}
                                                onBlur={(e) => {
                                                  const raw = parseAppldInputValue(e.target.value);
                                                  const capped = Math.min(raw, schedLateFee);
                                                  setLateFeeAppldValues((prev) => ({
                                                    ...prev,
                                                    [idx]: formatPriceWithDollar(capped),
                                                  }));
                                                }}
                                                style={{
                                                  width: 65,
                                                  textAlign: "center",
                                                  fontSize: 11,
                                                  border: isLightTheme ? "1px solid #ccc" : "1px solid #555",
                                                  padding: "2px 4px",
                                                  background: isLightTheme ? "#fff" : "#1e1e2e",
                                                  color: isLightTheme ? "#333" : "#ddd",
                                                  outline: "none",
                                                }}
                                              />
                                            ) : (
                                              <input
                                                type="text"
                                                readOnly
                                                disabled
                                                value={formatPriceWithDollar(enteredLateFeeAppld)}
                                                style={{
                                                  width: 65,
                                                  textAlign: "center",
                                                  fontSize: 11,
                                                  border: isLightTheme ? "1px solid #ccc" : "1px solid #555",
                                                  padding: "2px 4px",
                                                  background: isLightTheme ? "#f8f9fa" : "#1e1e2e",
                                                  color: isLightTheme ? "#333" : "#ddd",
                                                  cursor: "not-allowed",
                                                  opacity: 0.7,
                                                }}
                                              />
                                            )}
                                          </td>
                                          <td style={tdInput}>
                                            <input
                                              type="text"
                                              value={pmtAppldValues[idx] !== undefined ? pmtAppldValues[idx] : formatPriceWithDollar(pmtAppld)}
                                              onChange={(e) => {
                                                const raw = e.target.value.replace(/[^0-9.]/g, "");
                                                setPmtAppldValues(prev => ({ ...prev, [idx]: raw ? "$" + raw : "" }));
                                              }}
                                              onBlur={(e) => {
                                                const raw = parseFloat(e.target.value.replace(/[^0-9.]/g, "")) || 0;
                                                setPmtAppldValues(prev => ({ ...prev, [idx]: formatPriceWithDollar(raw) }));
                                              }}
                                              style={{ width: 70, textAlign: "center", fontSize: 11, border: isLightTheme ? "1px solid #ccc" : "1px solid #555", padding: "2px 4px", background: isLightTheme ? "#fff" : "#1e1e2e", color: isLightTheme ? "#333" : "#ddd", outline: "none" }}
                                            />
                                          </td>
                                          <td style={td}>{formatPriceWithDollar(totalAppld)}</td>
                                          <td style={{
                                            ...td,
                                            backgroundColor: remainDue <= 0.001 ? (isLightTheme ? "#d4edda" : "rgba(40, 167, 69, 0.2)") : "inherit",
                                            color: remainDue <= 0.001 ? (isLightTheme ? "#155724" : "#28a745") : td.color,
                                            fontWeight: remainDue <= 0.001 ? 700 : (td.fontWeight || "normal")
                                          }}>
                                            {formatPriceWithDollar(remainDue)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {postSubView === "payoff" && !isChargeOffAccount && (
                    <div>
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>Post Payoff</h5>
                      <Row>
                        <Col md={isPayoffPosting ? "6" : "12"}>
                          <Row>
                            <Col md={isPayoffPosting ? "12" : "6"} className="mb-2">
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Delivery Method</Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="delivery_method"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  isDisabled={isPayoffPosting}
                                  value={
                                    deliveryMethodOptions.find(
                                      (option) =>
                                        option.value === payoffForm.delivery_method
                                    ) || null
                                  }
                                  onChange={(option) => {
                                    handlePayoffInputChange("delivery_method", option?.value || "");
                                    handlePayoffInputChange("payment_form", "");
                                  }}
                                  options={deliveryMethodOptions}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={isPayoffPosting ? "12" : "6"} className="mb-2">
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Payment Form</Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="payment_form"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  isDisabled={isPayoffPosting || !payoffForm.delivery_method}
                                  value={
                                    filteredPaymentFormOptions.find(
                                      (option) =>
                                        option.value === payoffForm.payment_form
                                    ) || null
                                  }
                                  onChange={(option) =>
                                    handlePayoffInputChange(
                                      "payment_form",
                                      option?.value || ""
                                    )
                                  }
                                  options={filteredPaymentFormOptions}
                                />
                              </FormGroup>
                            </Col>

                            {/* Payoff Detail Section */}
                            {payoffForm.payment_form !== "" && (
                              <>
                                <Col md={isPayoffPosting ? "12" : "6"} className="mb-2">
                                  <FormGroup>
                                    <Label className="mb-1" style={{ fontSize: 12 }}>Balance</Label>
                                    <DrawerSelect
                                      activeColor={activeColor}
                                      className="react-select"
                                      placeholder="Select Balance"
                                      name="balance_type"
                                      isSearchable={false}
                                      closeMenuOnSelect={true}
                                      isDisabled={isPayoffPosting}
                                      value={
                                        calculusData?.payment_select_type?.find(
                                          (option) => option.value === payoffForm.balance_type
                                        )
                                          ? {
                                            value: payoffForm.balance_type,
                                            label: payoffForm.balance_type,
                                          }
                                          : null
                                      }
                                      onChange={(option) =>
                                        handlePayoffInputChange(
                                          "balance_type",
                                          option?.value || ""
                                        )
                                      }
                                      options={
                                        calculusData?.payment_select_type?.map((item) => ({
                                          value: item.value,
                                          label: item.value,
                                        })) || []
                                      }
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={isPayoffPosting ? "12" : "6"} className="mb-2">
                                  <FormGroup>
                                    <Label className="mb-1" style={{ fontSize: 12 }}>Effective Date</Label>
                                    <ReactDatetime
                                      inputProps={{
                                        className: "form-control",
                                        placeholder: "Select Date",
                                        style: { borderRadius: 0 },
                                        disabled: isPayoffPosting
                                      }}
                                      value={
                                        payoffForm.date
                                          ? formatDateForPicker(payoffForm.date)
                                          : getCurrentDateForAPI()
                                      }
                                      onChange={(date) => {
                                        const formattedDate = formatDateForAPI(date) || "";
                                        handlePayoffInputChange("date", formattedDate);
                                      }}
                                      renderDay={renderDay}
                                      dateFormat="MM-DD-YYYY"
                                      timeFormat={false}
                                      closeOnSelect={!isPayoffPosting}
                                    />
                                  </FormGroup>
                                </Col>

                                <Col md="12" className="mt-2" style={{ opacity: isPayoffPosting ? 0.6 : 1 }}>
                                  <h6 className="font-weight-bold mb-3 text-uppercase" style={{ fontSize: "14px", borderBottom: isLightTheme ? "1px solid #eee" : "1px solid rgba(255,255,255,0.1)", paddingBottom: "10px" }}>
                                    PAYOFF DETAIL
                                  </h6>
                                  <div style={{ fontSize: "13px" }}>
                                    {(() => {
                                      const selectedItem = calculusData?.payment_select_type?.find(
                                        (item) => item.value === payoffForm.balance_type
                                      );
                                      const selectedAmount = selectedItem?.amount || 0;
                                      const isPrimaryLoanSelected =
                                        payoffForm.balance_type === "principal" ||
                                        payoffForm.balance_type === "Primary Loan" ||
                                        payoffForm.balance_type === "Primary loan" ||
                                        selectedItem?.value === "principal" ||
                                        selectedItem?.value === "Primary Loan" ||
                                        selectedItem?.value === "Primary loan" ||
                                        String(selectedItem?.value || "")
                                          .toLowerCase()
                                          .includes("principal");

                                      const isAllBalancesSelected =
                                        payoffForm.balance_type === "All Balances" ||
                                        selectedItem?.value === "All Balances";

                                      return [
                                        {
                                          label: "Down Payment Balance:",
                                          value: isPrimaryLoanSelected
                                            ? 0
                                            : selectedItem?.def_down_amount || 0,
                                        },
                                        {
                                          label: "Principal Balance:",
                                          value: selectedAmount,
                                        },
                                        {
                                          label: "Sales Tax Balance:",
                                          value: selectedItem?.sales_tax_amount || 0,
                                        },
                                        {
                                          label: "Non Interest Earning Balance:",
                                          value:
                                            selectedItem?.current_non_earning_principal ??
                                            (isPrimaryLoanSelected || isAllBalancesSelected
                                              ? calculusData?.balances?.current_non_earning_principal ?? 0
                                              : 0),
                                        },
                                        {
                                          label: "Accrued Interest Balance:",
                                          value: selectedItem?.current_interest ?? selectedItem?.accured_interest ?? 0,
                                        },
                                        {
                                          label: "Late Fees Balance:",
                                          value: selectedItem?.late_fee || 0,
                                        },
                                        {
                                          label: "Misc Fees Balance:",
                                          value: selectedItem?.misc_fee || 0,
                                        },
                                      ].map((item, idx) => (
                                        <div key={idx} className="d-flex justify-content-between mb-2">
                                          <span className="font-weight-bold text-right" style={{ width: "60%" }}>{item.label}</span>
                                          <span style={{ width: "40%", textAlign: "right" }}>{formatPriceWithDollar(item.value)}</span>
                                        </div>
                                      ));
                                    })()}
                                    <div style={{ borderTop: isLightTheme ? "2px solid #333" : "2px solid #fff", margin: "5px 0" }}></div>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="font-weight-bold text-right" style={{ width: "60%" }}>Payoff:</span>
                                      <span style={{ width: "40%", textAlign: "right", fontWeight: "bold" }}>
                                        {(() => {
                                          const selectedItem = calculusData?.payment_select_type?.find(
                                            (item) => item.value === payoffForm.balance_type
                                          );
                                          return formatPriceWithDollar(selectedItem?.total || 0);
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="mt-4 font-italic font-weight-bold">
                                    Per Diem: <span className="ml-4">$8.30</span>
                                  </div>
                                </Col>

                                <Col md="12" className="mt-4">
                                  <Button
                                    block
                                    disabled={isPayoffPosting}
                                    onClick={handlePostPayoffClick}
                                    style={{
                                      backgroundColor: isPayoffPosting ? "#9ecbde" : "#8abed7",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "0",
                                      padding: "10px",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      marginBottom: "10px"
                                    }}
                                  >
                                    POST THIS PAYOFF
                                  </Button>
                                  <Button
                                    block
                                    disabled={isPayoffPosting || printPayoffMutation.isPending}
                                    style={{
                                      backgroundColor: isPayoffPosting ? "#0288d1" : "#0288d1",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "0",
                                      padding: "10px",
                                      fontSize: "14px",
                                      fontWeight: "600"
                                    }}
                                    onClick={() => {
                                      let payoffAmount = 0;
                                      const selectedItem = calculusData?.payment_select_type?.find(
                                        (item) => item.value === payoffForm.balance_type
                                      );
                                      payoffAmount = selectedItem?.total || 0;

                                      const payload = {
                                        booking_id: bookingId,
                                        date: parseDateFromAPI(payoffForm.date) || null,
                                        amount: payoffAmount,
                                        collected: toNumber(payoffForm.amount_received),
                                        delivery_method: payoffForm.delivery_method || null,
                                        payment_form: payoffForm.payment_form || null,
                                        amount_received: toNumber(payoffForm.amount_received),
                                        ref_number: payoffForm.ref_number || null,
                                        note: payoffForm.note || "",
                                        payment_type: "payoff",
                                        applied_payments: [],
                                        write_off: payoffForm.write_off === "Yes",
                                        balance: payoffForm.balance_type,
                                      };

                                      printPayoffMutation.mutate(payload, {
                                        onSuccess: (response) => {
                                          const blob = new Blob([response.data], { type: 'application/pdf' });
                                          const blobUrl = URL.createObjectURL(blob);
                                          setPdfUrl(blobUrl);
                                          setViewerSource({ tab: drawerActiveTab, subView: postSubView });
                                          setPrintSubView("viewer");
                                          setDrawerActiveTab("print");
                                          setDrawerFullSize(true);
                                        }
                                      });
                                    }}
                                  >
                                    {printPayoffMutation.isPending ? "PRINTING..." : "PRINT PAYOFF QUOTE"}
                                  </Button>
                                </Col>
                              </>
                            )}
                          </Row>
                        </Col>

                        {/* Right Area for Posting */}
                        {isPayoffPosting && (
                          <Col md="6">
                            <div style={{ backgroundColor: isLightTheme ? "#f8f9fa" : "rgba(255,255,255,0.05)", padding: "16px" }}>
                              <h6 className="font-weight-bold mb-4 text-uppercase" style={{ fontSize: "14px" }}>
                                POST THE PAYOFF FOR THIS ACCOUNT
                              </h6>
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Amount Paying:</Label>
                                <DollarInput
                                  name="amount"
                                  value={payoffForm.amount}
                                  onChange={(value) => handlePayoffInputChange("amount", value)}
                                  placeholder="$0.00"
                                />
                              </FormGroup>
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Amount Received:</Label>
                                <DollarInput
                                  name="amount_received"
                                  value={payoffForm.amount_received}
                                  onChange={(value) => handlePayoffInputChange("amount_received", value)}
                                  placeholder="$0.00"
                                />
                              </FormGroup>
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Change Due:</Label>
                                <Input
                                  type="text"
                                  readOnly
                                  value={(() => {
                                    const received = toNumber(payoffForm.amount_received);
                                    const amountVal = toNumber(payoffForm.amount);
                                    return formatPriceWithDollar(Math.abs(received - amountVal));
                                  })()}
                                  style={{ fontSize: "14px", backgroundColor: "transparent", cursor: "default" }}
                                />
                              </FormGroup>
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Ref Number:</Label>
                                <Input
                                  type="text"
                                  value={payoffForm.ref_number}
                                  onChange={(e) => handlePayoffInputChange("ref_number", e.target.value)}
                                />
                              </FormGroup>
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Comments: (50 Max)</Label>
                                <Input
                                  type="text"
                                  maxLength="50"
                                  value={payoffForm.note}
                                  onChange={(e) => handlePayoffInputChange("note", e.target.value)}
                                />
                              </FormGroup>
                              <FormGroup>
                                <Label className="mb-1" style={{ fontSize: 12 }}>Write Off Uncollected?</Label>
                                <Input
                                  type="select"
                                  value={payoffForm.write_off || "No"}
                                  onChange={(e) => handlePayoffInputChange("write_off", e.target.value)}
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </Input>
                              </FormGroup>

                              <Row className="mt-4">
                                <Col md="6">
                                  <Button
                                    block
                                    style={{
                                      backgroundColor: "#0288d1",
                                      color: "#fff",
                                      border: "none",
                                      padding: "10px",
                                      fontSize: "14px",
                                      fontWeight: "600"
                                    }}
                                    onClick={() => setIsPayoffPosting(false)}
                                  >
                                    &lt; RE-QUOTE
                                  </Button>
                                </Col>
                                <Col md="6">
                                  {(() => {
                                    const amountPayingValue = toNumber(payoffForm.amount);
                                    const amountReceivedValue = toNumber(payoffForm.amount_received);
                                    const amountsMatch =
                                      Math.abs(amountPayingValue - amountReceivedValue) < 0.01;
                                    const writeOffYes = payoffForm.write_off === "Yes";
                                    const canPostPayoffAction =
                                      amountsMatch || writeOffYes;

                                    return (
                                      <Button
                                        block
                                        disabled={
                                          !canPostPayoffAction ||
                                          createPayoffMutation.isPending
                                        }
                                        style={{
                                          backgroundColor: canPostPayoffAction
                                            ? "#28a745"
                                            : "#6c757d",
                                          color: "#fff",
                                          border: "none",
                                          padding: "10px",
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          opacity:
                                            !canPostPayoffAction ||
                                            createPayoffMutation.isPending
                                              ? 0.65
                                              : 1,
                                          cursor:
                                            !canPostPayoffAction ||
                                            createPayoffMutation.isPending
                                              ? "not-allowed"
                                              : "pointer",
                                        }}
                                        onClick={handleSubmitPayoff}
                                      >
                                        {createPayoffMutation.isPending
                                          ? "POSTING..."
                                          : "POST PAYOFF"}
                                      </Button>
                                    );
                                  })()}
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}

                  {postSubView === "payoff-receipt" && (
                    <div className="p-3">

                      <div className="text-white text-center py-2 mb-3 font-weight-bold no-print" style={{ backgroundColor: "#28a745" }}>
                        Payoff was posted successfully!
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2 no-print">
                        <span>{moment().format("M/D/YYYY")}</span>
                        <div>
                          <Button className="mr-2 border-0" style={{ borderRadius: 0, ...(activeColorStyle || {}), color: "white" }} onClick={() => {
                            const printContents = document.getElementById("payoff-receipt-print-area").innerHTML;
                            const iframe = document.createElement("iframe");
                            iframe.style.position = "absolute";
                            iframe.style.width = "0px";
                            iframe.style.height = "0px";
                            iframe.style.border = "none";
                            document.body.appendChild(iframe);

                            const doc = iframe.contentWindow.document;
                            doc.open();
                            doc.write(`
                              <html>
                                <head>
                                  <title>Payoff Receipt</title>
                                  <link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css">
                                  <style>
                                    @media print {
                                      @page { margin: 20px; }
                                      body { 
                                        padding: 20px;
                                        background: white;
                                        color: black;
                                        -webkit-print-color-adjust: exact;
                                      }
                                    }
                                  </style>
                                </head>
                                <body>
                                  ${printContents}
                                </body>
                              </html>
                            `);
                            doc.close();

                            iframe.contentWindow.focus();
                            setTimeout(() => {
                              iframe.contentWindow.print();
                              setTimeout(() => {
                                document.body.removeChild(iframe);
                              }, 1000);
                            }, 500);
                          }}>PRINT THIS RECEIPT</Button>
                          <Button className="border-0" style={{ borderRadius: 0, ...(activeColorStyle || {}), color: "white" }} onClick={() => closeDrawer()}>CLOSE</Button>
                        </div>
                      </div>

                      <div className="border p-0 mb-3" id="payoff-receipt-web-area" style={{ backgroundColor: isLightTheme ? "#fff" : "#2b2b36", fontSize: "12px", color: isLightTheme ? "#000" : "#fff" }}>
                        <Row className="m-0 border-bottom p-2">
                          <Col md="6">
                            <strong>{payoffReceiptData?.portfolio?.name}</strong><br />
                            {payoffReceiptData?.portfolio?.address}<br />
                            {payoffReceiptData?.portfolio?.city}, {payoffReceiptData?.portfolio?.state} {payoffReceiptData?.portfolio?.zip}<br />
                            {payoffReceiptData?.portfolio?.phone} phone<br />
                            {payoffReceiptData?.portfolio?.fax} fax<br />
                            {payoffReceiptData?.portfolio?.website}<br />
                            <strong className="mt-2 d-inline-block">Thanks for your business!</strong>
                          </Col>
                          <Col md="6">
                            <table className="table table-bordered table-sm mb-0" style={{ color: "border-color" }}>
                              <tbody>
                                <tr>
                                  <td style={{ width: "40%", backgroundColor: "rgba(0,0,0,0.05)" }}>Receipt ID</td>
                                  <td>{payoffReceiptData?.receipt_id || ""}</td>
                                </tr>
                                <tr>
                                  <td style={{ backgroundColor: "rgba(0,0,0,0.05)" }}>Transaction Date</td>
                                  <td>{moment(payoffReceiptData?.created_payment_date).format("M/D/YYYY h:mm:ss A")}</td>
                                </tr>
                                <tr>
                                  <td style={{ backgroundColor: "rgba(0,0,0,0.05)" }}>Effective Date</td>
                                  <td>{moment(payoffReceiptData?.date).format("M/D/YYYY")}</td>
                                </tr>
                              </tbody>
                            </table>
                          </Col>
                        </Row>

                        <Row className="m-0 border-bottom p-2">
                          <Col md="6" className="border-right" style={{ borderColor: "rgba(0,0,0,0.1) !important" }}>
                            <strong>Customer</strong><br />
                            {calculusData?.customer?.name || "test test"}<br />
                            {calculusData?.customer?.address || "123 FAKE ST"}<br /><br />
                            {calculusData?.customer?.city_state_zip || "Pasadena, TX 77506"}<br />
                            Co-Borrower: {calculusData?.customer?.co_borrower || ""}
                          </Col>
                          <Col md="6">
                            <strong>Account Information</strong><br />
                            Loan: {calculusData?.inventory?.stock_number || ""}<br />
                            Account: {calculusData?.booking_id || ""}<br />
                            Stock #/VIN: {calculusData?.inventory?.stock_number || ""} / {calculusData?.inventory?.vin || ""}<br />
                            Description: {calculusData?.inventory?.vehicle_description || ""}
                          </Col>
                        </Row>

                        <Row className="m-0 border-bottom p-2">
                          <Col md="6" className="border-right" style={{ borderColor: "rgba(0,0,0,0.1) !important" }}>
                            <strong>Transaction Posted By</strong><br />
                            {payoffReceiptData?.posted_by || "KA110269"}
                          </Col>
                          <Col md="6">
                            <strong>Notes</strong><br />
                            [ Balance Payoff: {calculusData?.balance_payoff_id || "13210658"} ]
                          </Col>
                        </Row>

                        <div className="p-2 pt-3">
                          <table className="table table-sm mb-4" style={{ color: "inherit" }}>
                            <thead>
                              <tr className="border-bottom">
                                <th>Transaction Type</th>
                                <th>Balance Desc</th>
                                <th className="text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td></td>
                                <td>{payoffReceiptData?.balance === "all" ? "All Balances" : payoffReceiptData?.balance === "principal" ? "Primary Loan" : (payoffReceiptData?.balance || "Primary Loan")}</td>
                                <td className="text-right">{payoffReceiptData?.amount != null ? formatPriceWithDollar(payoffReceiptData?.amount) : "$3,056.67"}</td>
                              </tr>
                              {(!payoffReceiptData?.amount) && (
                                <tr>
                                  <td>Down Payment</td>
                                  <td>Primary Loan</td>
                                  <td className="text-right">$1,410.00</td>
                                </tr>
                              )}
                            </tbody>
                          </table>

                          <div className="d-flex justify-content-end pr-2 pb-2">
                            <div style={{ width: "350px" }}>
                              <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span>TOTAL TRANSACTION:</span>
                                <strong>{payoffReceiptData?.amount != null ? formatPriceWithDollar(payoffReceiptData?.amount) : "$4,466.67"}</strong>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <span>Amount Paid :</span>
                                <strong>{payoffReceiptData?.amount != null ? formatPriceWithDollar(payoffReceiptData?.amount) : "$4,466.67"}</strong>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <span>Amount Received :</span>
                                <strong>{payoffReceiptData?.amount_received != null ? formatPriceWithDollar(payoffReceiptData?.amount_received) : "$4,466.67"}</strong>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Change Due :</span>
                                <strong>{(() => {
                                  const received = Number(payoffReceiptData?.amount_received);
                                  const paid = Number(payoffReceiptData?.amount);
                                  if (!Number.isFinite(received) || !Number.isFinite(paid)) return "$0.00";
                                  return formatPriceWithDollar(Math.abs(received - paid));
                                })()}</strong>
                              </div>
                              <div className="mt-3">
                                <div className="d-flex justify-content-between mb-1">
                                  <span>Payment Method :</span>
                                  <strong>{payoffReceiptData?.payment_form || "Credit Card"}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-1">
                                  <span>Reference # :</span>
                                  <strong>{payoffReceiptData?.ref_number || ""}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span>Delivery Method :</span>
                                  <strong>{payoffReceiptData?.delivery_method || "Web"}</strong>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 mb-2 p-1" style={{ backgroundColor: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.1)", fontSize: "11px" }}>
                            CURRENT DUE BREAKDOWN - as of this transaction
                          </div>
                          <div className="d-flex justify-content-end pr-2 pb-2">
                            <div style={{ width: "350px" }}>
                              <div className="d-flex justify-content-between mb-1">
                                <span>Next Scheduled Payment:</span>
                                <div style={{ border: "1px solid rgba(0,0,0,0.1)", width: "150px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.next_schedule_payment_amount != null ? formatPriceWithDollar(payoffReceiptData?.next_schedule_payment_amount) : "$0.00"}</div>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Next Schedule Payment Date:</span>
                                <div style={{ border: "1px solid rgba(0,0,0,0.1)", width: "150px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.next_schedule_payment_date ? moment(payoffReceiptData?.next_schedule_payment_date).format("M/D/YYYY") : ""}</div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 mb-2 p-1" style={{ backgroundColor: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.1)", fontSize: "11px" }}>
                            BALANCE INFORMATION - as of this transaction
                          </div>
                          <Row className="m-0 border border-top-0" style={{ borderColor: "rgba(0,0,0,0.1) !important" }}>
                            <Col md="6" className="p-0 border-right" style={{ borderColor: "rgba(0,0,0,0.1) !important" }}>
                              <div className="p-1 border-top" style={{ backgroundColor: "#e8e6dd", color: '#000' }}>Primary Loan <span style={{ fontStyle: "italic" }}>Principal Only</span> Balance</div>
                              <div className="p-2 d-flex justify-content-end">
                                <div style={{ width: "250px" }}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="text-right lh-1" style={{ lineHeight: "1" }}>Before Transaction<br /><span style={{ fontSize: "9px" }}>(not including interest)</span></div>
                                    <div style={{ border: "1px dashed rgba(0,0,0,0.3)", width: "100px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.primary_loan_before != null ? formatPriceWithDollar(payoffReceiptData?.primary_loan_before) : "$0.00"}</div>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-right lh-1" style={{ lineHeight: "1" }}>After Transaction<br /><span style={{ fontSize: "9px" }}>(not including interest)</span></div>
                                    <div style={{ border: "1px solid rgba(0,0,0,0.3)", width: "100px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.primary_loan_after != null ? formatPriceWithDollar(payoffReceiptData?.primary_loan_after) : "$0.00"}</div>
                                  </div>
                                </div>
                              </div>
                            </Col>
                            <Col md="6" className="p-0">
                              <div className="p-1 border-top" style={{ backgroundColor: "#e8e6dd", color: '#000' }}>Down Payment(s) Balances</div>
                              <div className="p-2 d-flex justify-content-end">
                                <div style={{ width: "250px" }}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="text-right lh-1" style={{ lineHeight: "1" }}>Before Transaction</div>
                                    <div style={{ border: "1px dashed rgba(0,0,0,0.3)", width: "100px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.def_down_before != null ? formatPriceWithDollar(payoffReceiptData?.def_down_before) : "$0.00"}</div>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-right lh-1" style={{ lineHeight: "1" }}>After Transaction</div>
                                    <div style={{ border: "1px dashed rgba(0,0,0,0.3)", width: "100px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.def_down_after != null ? formatPriceWithDollar(payoffReceiptData?.def_down_after) : "$0.00"}</div>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>

                          {/* CPI Balance Row */}
                          {(payoffReceiptData?.cpi_before != null || payoffReceiptData?.cpi_after != null) && (
                            <Row className="m-0 border border-top-0" style={{ borderColor: "rgba(0,0,0,0.1) !important" }}>
                              <Col md="6" className="p-0 border-right" style={{ borderColor: "rgba(0,0,0,0.1) !important" }}>
                                <div className="p-1 border-top" style={{ backgroundColor: "#e8e6dd", color: '#000' }}>CPI Balance</div>
                                <div className="p-2 d-flex justify-content-end">
                                  <div style={{ width: "250px" }}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <div className="text-right lh-1" style={{ lineHeight: "1" }}>Before Transaction</div>
                                      <div style={{ border: "1px dashed rgba(0,0,0,0.3)", width: "100px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.cpi_before != null ? formatPriceWithDollar(payoffReceiptData?.cpi_before) : "$0.00"}</div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="text-right lh-1" style={{ lineHeight: "1" }}>After Transaction</div>
                                      <div style={{ border: "1px solid rgba(0,0,0,0.3)", width: "100px", textAlign: "center", padding: "2px" }}>{payoffReceiptData?.cpi_after != null ? formatPriceWithDollar(payoffReceiptData?.cpi_after) : "$0.00"}</div>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                              <Col md="6" className="p-0">
                              </Col>
                            </Row>
                          )}

                        </div>
                      </div>

                      <div style={{ display: 'none' }}>
                        <div id="payoff-receipt-print-area" className="p-4" style={{ backgroundColor: "#fff", fontSize: "14px", color: "#000", fontFamily: '"Times New Roman", Times, serif' }}>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <span style={{ fontSize: "12px" }}>{moment().format("M/D/YY, h:mm A")}</span>
                            <strong style={{ fontSize: "14px" }}>Transaction Receipt</strong>
                            <span style={{ visibility: "hidden", fontSize: "12px" }}>{moment().format("M/D/YY, h:mm A")}</span>
                          </div>

                          <Row className="m-0 mb-3">
                            <Col xs="6" className="p-0">
                              <strong>{payoffReceiptData?.portfolio?.name || "TEXAS AUTO CAPITAL"}</strong><br />
                              {payoffReceiptData?.portfolio?.address || "P.O.Box 550589,"}<br />
                              {payoffReceiptData?.portfolio?.city || "Houston"}, {payoffReceiptData?.portfolio?.state || "TX"} {payoffReceiptData?.portfolio?.zip || "77255"}<br />
                              {payoffReceiptData?.portfolio?.phone || "(346)355-7802"} phone<br />
                              {payoffReceiptData?.portfolio?.fax || ""} fax<br />
                              {payoffReceiptData?.portfolio?.website || "www.txautocapital.com"}<br />
                              <strong className="mt-3 d-inline-block">Thanks for your business!</strong>
                            </Col>
                            <Col xs="6" className="p-0 d-flex justify-content-end">
                              <table className="table table-bordered mb-0" style={{ color: "black", borderColor: "#000080", maxWidth: "400px", borderWidth: "2px" }}>
                                <tbody>
                                  <tr>
                                    <td className="p-1" style={{ width: "45%", fontSize: "16px", borderColor: "#000080" }}>Receipt ID</td>
                                    <td className="text-center p-1" style={{ fontSize: "16px", borderColor: "#000080" }}>{payoffReceiptData?.receipt_id || "140991506"}</td>
                                  </tr>
                                  <tr>
                                    <td className="p-1 align-middle" style={{ fontSize: "16px", borderColor: "#000080" }}>Transaction Date</td>
                                    <td className="text-center p-1" style={{ fontSize: "16px", borderColor: "#000080", lineHeight: "1.2" }}>
                                      {moment(payoffReceiptData?.created_payment_date || new Date()).format("M/D/YYYY h:mm:ss")}<br />
                                      {moment(payoffReceiptData?.created_payment_date || new Date()).format("A")}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="p-1" style={{ fontSize: "16px", borderColor: "#000080" }}>Effective Date</td>
                                    <td className="text-center p-1" style={{ fontSize: "16px", borderColor: "#000080" }}>{moment(payoffReceiptData?.date).format("M/D/YYYY")}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </Col>
                          </Row>

                          <div className="mb-3" style={{ border: "2px solid #000080" }}>
                            <Row className="m-0">
                              <Col xs="6" className="p-2" style={{ borderRight: "2px solid #000080" }}>
                                <strong>Customer</strong><br />
                                {calculusData?.customer?.name || "test test"}<br />
                                {calculusData?.customer?.address || "123 FAKE ST"}<br /><br />
                                {calculusData?.customer?.city_state_zip || "Pasadena, TX 77506"}<br />
                                Co-Borrower: {calculusData?.customer?.co_borrower || ""}
                              </Col>
                              <Col xs="6" className="p-2">
                                <strong>Account Information</strong><br />
                                Loan: {calculusData?.inventory?.stock_number || "S11574"}<br />
                                Account: {calculusData?.booking_id || "11116992"}<br />
                                Stock #/VIN: {calculusData?.inventory?.stock_number || "S11574"} / {calculusData?.inventory?.vin || "3VWD17AJ3GM292794"}<br />
                                Description: {calculusData?.inventory?.vehicle_description || "2016 Volkswagen Jetta"}
                              </Col>
                            </Row>
                          </div>

                          <Row className="m-0 mb-4 px-2">
                            <Col xs="6" className="p-0">
                              <strong>Transaction Posted By</strong><br />
                              {payoffReceiptData?.posted_by || "KA110269"}
                            </Col>
                            <Col xs="6" className="p-0">
                              <strong>Notes</strong><br />

                            </Col>
                          </Row>

                          <div>
                            <table className="table table-borderless table-sm mb-4" style={{ color: "black", borderBottom: "1px solid #000" }}>
                              <thead>
                                <tr style={{ borderBottom: "3px double #000" }}>
                                  <th className="p-0 pb-1 h6 font-weight-bold" style={{ color: "black", border: "none" }}>Transaction Type</th>
                                  <th className="p-0 pb-1 h6 font-weight-bold" style={{ color: "black", border: "none" }}>Balance Desc</th>
                                  <th className="p-0 pb-1 h6 font-weight-bold text-right" style={{ color: "black", border: "none" }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr style={{ borderBottom: "1px dotted #000" }}>
                                  <td className="p-0 py-1"></td>
                                  <td className="p-0 py-1">{payoffReceiptData?.balance === "all" ? "All Balances" : payoffReceiptData?.balance === "principal" ? "Primary Loan" : (payoffReceiptData?.balance || "Primary Loan")}</td>
                                  <td className="p-0 py-1 text-right">{payoffReceiptData?.amount != null ? formatPriceWithDollar(payoffReceiptData?.amount) : "$3,059.35"}</td>
                                </tr>
                                {(!payoffReceiptData?.amount) && (
                                  <tr style={{ borderBottom: "1px dotted #000" }}>
                                    <td className="p-0 py-1">Down Payment</td>
                                    <td className="p-0 py-1">Primary Loan</td>
                                    <td className="p-0 py-1 text-right">$1,410.00</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>

                            <div className="d-flex justify-content-end pr-0 pb-4">
                              <div style={{ width: "350px" }}>
                                <div className="d-flex justify-content-between align-items-end mb-2">
                                  <span className="h6 mb-0 font-weight-bold text-uppercase">TOTAL TRANSACTION:</span>
                                  <strong className="h6 mb-0 pl-3 pt-1" style={{ borderTop: "3px double #000" }}>{payoffReceiptData?.amount != null ? formatPriceWithDollar(payoffReceiptData?.amount) : "$4,469.35"}</strong>
                                </div>

                                <div className="border border-dark p-2 text-right">
                                  <div className="d-flex justify-content-between mb-0">
                                    <span className="font-weight-bold">Amount Paid :</span>
                                    <span>{payoffReceiptData?.amount != null ? formatPriceWithDollar(payoffReceiptData?.amount) : "$4,469.35"}</span>
                                  </div>
                                  <div className="d-flex justify-content-between mb-0">
                                    <span className="font-weight-bold">Amount Received :</span>
                                    <span>{payoffReceiptData?.amount_received != null ? formatPriceWithDollar(payoffReceiptData?.amount_received) : "$4,469.35"}</span>
                                  </div>
                                  <div className="d-flex justify-content-between pb-2 mb-2" style={{ borderBottom: "1px solid #777" }}>
                                    <span className="font-weight-bold">Change Due :</span>
                                    <span>{(() => {
                                      const received = Number(payoffReceiptData?.amount_received);
                                      const paid = Number(payoffReceiptData?.amount);
                                      if (!Number.isFinite(received) || !Number.isFinite(paid)) return "$0.00";
                                      return formatPriceWithDollar(Math.abs(received - paid));
                                    })()}</span>
                                  </div>

                                  <div className="d-flex justify-content-center pt-1">
                                    <table className="text-left w-auto mx-auto" style={{ fontSize: "14px" }}>
                                      <tbody>
                                        <tr>
                                          <td className="font-weight-bold pr-2 text-right">Payment Method :</td>
                                          <td>{payoffReceiptData?.payment_form || "Debit Card"}</td>
                                        </tr>
                                        <tr>
                                          <td className="font-weight-bold pr-2 text-right">Reference # :</td>
                                          <td>{payoffReceiptData?.ref_number || ""}</td>
                                        </tr>
                                        <tr>
                                          <td className="font-weight-bold pr-2 text-right">Delivery Method :</td>
                                          <td>{payoffReceiptData?.delivery_method || "Fax"}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mb-3 p-1 px-2 border" style={{ borderColor: "#ddd !important" }}>
                              <strong>CURRENT DUE BREAKDOWN</strong> - as of this transaction
                            </div>

                            <div className="d-flex mx-auto justify-content-center pb-4">
                              <table className="text-right w-auto" style={{ fontSize: "14px" }}>
                                <tbody>
                                  <tr>
                                    <td className="pr-2 py-1">Next Scheduled Payment:</td>
                                    <td className="px-2 py-1" style={{ borderBottom: "1px dotted #999", borderLeft: "1px dotted #999", borderRight: "1px dotted #999", borderTop: "1px dotted #999", width: "130px", textAlign: "center", backgroundColor: '#f8f8f8' }}>
                                      {payoffReceiptData?.next_schedule_payment_amount != null ? formatPriceWithDollar(payoffReceiptData?.next_schedule_payment_amount) : "$0.00"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="pr-2 py-1">Next Schedule Payment Date:</td>
                                    <td className="px-2 py-1" style={{ borderBottom: "1px dotted #999", borderLeft: "1px dotted #999", borderRight: "1px dotted #999", width: "130px", textAlign: "center", backgroundColor: '#f8f8f8' }}>
                                      {payoffReceiptData?.next_schedule_payment_date ? moment(payoffReceiptData?.next_schedule_payment_date).format("M/D/YYYY") : ""}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            <div className="mb-2 p-1 px-2 border" style={{ borderColor: "#ddd !important" }}>
                              <strong>BALANCE INFORMATION</strong> - as of this transaction
                            </div>

                            <Row className="m-0 mb-4">
                              <Col xs="6" className="p-0 border-right border-dark pr-3 mb-3">
                                <strong>Primary Loan <span style={{ fontStyle: "italic" }}>Principal Only</span> Balance</strong>
                                <table className="w-100 mt-2" style={{ fontSize: "14px" }}>
                                  <tbody>
                                    <tr style={{ borderBottom: "1px dotted #ccc" }}>
                                      <td className="text-right lh-1 py-1" style={{ lineHeight: "1.2", paddingRight: "12px" }}>Before Transaction<br /><span style={{ fontSize: "10px" }}>(not including interest)</span></td>
                                      <td className="text-center" style={{ width: "110px", border: "1px dotted #999", backgroundColor: "#f8f8f8" }}>{payoffReceiptData?.primary_loan_before != null ? formatPriceWithDollar(payoffReceiptData?.primary_loan_before) : "$3,044.61"}</td>
                                    </tr>
                                    <tr style={{ borderBottom: "1px dotted #ccc" }}>
                                      <td className="text-right lh-1 py-1" style={{ lineHeight: "1.2", paddingRight: "12px" }}>After Transaction<br /><span style={{ fontSize: "10px" }}>(not including interest)</span></td>
                                      <td className="text-center" style={{ width: "110px", border: "1px dotted #999", backgroundColor: "#f8f8f8" }}>{payoffReceiptData?.primary_loan_after != null ? formatPriceWithDollar(payoffReceiptData?.primary_loan_after) : "$0.00"}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </Col>
                              <Col xs="6" className="p-0 pl-3 mb-3">
                                <strong>Down Payment(s) Balances</strong>
                                <table className="w-100 mt-2" style={{ fontSize: "14px" }}>
                                  <tbody>
                                    <tr style={{ borderBottom: "1px dotted #ccc" }}>
                                      <td className="text-right lh-1 py-2" style={{ paddingRight: "12px" }}>Before Transaction</td>
                                      <td className="text-center" style={{ width: "110px", border: "1px dotted #999", backgroundColor: "#f8f8f8" }}>{payoffReceiptData?.def_down_before != null ? formatPriceWithDollar(payoffReceiptData?.def_down_before) : "$1,410.00"}</td>
                                    </tr>
                                    <tr style={{ borderBottom: "1px dotted #ccc" }}>
                                      <td className="text-right lh-1 py-2" style={{ paddingRight: "12px" }}>After Transaction</td>
                                      <td className="text-center" style={{ width: "110px", border: "1px dotted #999", backgroundColor: "#f8f8f8" }}>{payoffReceiptData?.def_down_after != null ? formatPriceWithDollar(payoffReceiptData?.def_down_after) : "$0.00"}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </Col>

                              {/* CPI Balance Row */}
                              {(payoffReceiptData?.cpi_before != null || payoffReceiptData?.cpi_after != null) && (
                                <Col xs="6" className="p-0 border-right border-dark pr-3 mb-3">
                                  <strong>CPI Balance</strong>
                                  <table className="w-100 mt-2" style={{ fontSize: "14px" }}>
                                    <tbody>
                                      <tr style={{ borderBottom: "1px dotted #ccc" }}>
                                        <td className="text-right lh-1 py-2" style={{ paddingRight: "12px" }}>Before Transaction</td>
                                        <td className="text-center" style={{ width: "110px", border: "1px dotted #999", backgroundColor: "#f8f8f8" }}>{payoffReceiptData?.cpi_before != null ? formatPriceWithDollar(payoffReceiptData?.cpi_before) : "$0.00"}</td>
                                      </tr>
                                      <tr style={{ borderBottom: "1px dotted #ccc" }}>
                                        <td className="text-right lh-1 py-2" style={{ paddingRight: "12px" }}>After Transaction</td>
                                        <td className="text-center" style={{ width: "110px", border: "1px dotted #999", backgroundColor: "#f8f8f8" }}>{payoffReceiptData?.cpi_after != null ? formatPriceWithDollar(payoffReceiptData?.cpi_after) : "$0.00"}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </Col>
                              )}
                            </Row>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {postSubView === "side-note" && !isChargeOffAccount && (
                    <div>
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>Side Note Information</h5>
                      <div style={{ backgroundColor: isLightTheme ? "#f8f9fa" : "rgba(255,255,255,0.05)", padding: 16 }}>
                        <Row>
                          <Col md="6" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1">Description: <span style={{ color: "red" }}>*</span></Label>
                              <Input
                                type="text"
                                style={{ borderRadius: 0, borderColor: showErrors && !sideNoteForm.description ? "red" : undefined }}
                                value={sideNoteForm.description}
                                onChange={(e) => setSideNoteForm(prev => ({ ...prev, description: e.target.value }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1">Side Note Type:</Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select"
                                name="side_note_type"
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={sideNoteForm.type ? { value: sideNoteForm.type, label: sideNoteForm.type } : null}
                                onChange={(opt) => setSideNoteForm(prev => ({ ...prev, type: opt?.value || '' }))}
                                options={[
                                  { value: "Recurring CPI", label: "Recurring CPI" },
                                  { value: "Side Note", label: "Side Note" },
                                  { value: "CPI Balance", label: "CPI Balance" }
                                ]}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1">{sideNoteForm.type === "Recurring CPI" ? "Amount:" : "Loan Amount:"} <span style={{ color: "red" }}>*</span></Label>
                              <DollarInput
                                name="loan_amount"
                                value={sideNoteForm.amount}
                                onChange={(val) => setSideNoteForm(prev => ({ ...prev, amount: val }))}
                                placeholder="$0.00"
                                style={{ borderRadius: 0, fontSize: "14px", borderColor: showErrors && !toNumber(sideNoteForm.amount) ? "red" : undefined }}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1">{sideNoteForm.type === "Recurring CPI" ? "First Due Date:" : "Loan Date:"}</Label>
                              <ReactDatetime
                                inputProps={{
                                  className: "form-control",
                                  placeholder: "Select Date",
                                  style: { borderRadius: 0 },
                                  maxLength: 10
                                }}
                                value={sideNoteForm.date ? formatDateForPicker(sideNoteForm.date) : ""}
                                onChange={(date) => {
                                  const formattedDate = formatDateForAPI(date) || "";
                                  setSideNoteForm(prev => ({ ...prev, date: formattedDate }));
                                }}
                                renderDay={renderDay}
                                dateFormat="MM/DD/YYYY"
                                timeFormat={false}
                                closeOnSelect={true}
                              />
                            </FormGroup>
                          </Col>
                          {sideNoteForm.type === "Recurring CPI" ? (
                            <Col md="6" className="mb-2">
                              <FormGroup>
                                <Label className="mb-1">Frequency:</Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="frequency"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  value={sideNoteForm.frequency ? { value: sideNoteForm.frequency, label: sideNoteForm.frequency } : null}
                                  onChange={(opt) => setSideNoteForm(prev => ({ ...prev, frequency: opt?.value || '' }))}
                                  options={[
                                    { value: "Weekly", label: "Weekly" },
                                    { value: "Bi-Weekly", label: "Bi-Weekly" },
                                    { value: "Semi-Monthly", label: "Semi-Monthly" },
                                    { value: "Monthly", label: "Monthly" }
                                  ]}
                                />
                              </FormGroup>
                            </Col>
                          ) : (
                            <Col md="12" className="mb-2">
                              <FormGroup>
                                <Label className="mb-1">Loan Rate:</Label>
                                <Input
                                  type="text"
                                  style={{ borderRadius: 0 }}
                                  value={sideNoteForm.rate}
                                  onChange={(e) => setSideNoteForm(prev => ({ ...prev, rate: e.target.value }))}
                                  placeholder="0.0000%"
                                />
                              </FormGroup>
                            </Col>
                          )}
                        </Row>
                      </div>

                      {sideNoteForm.type === "CPI Balance" && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-2 mt-2">
                            <style>{`
                              .rdt-right .rdtPicker {
                                left: auto !important;
                                right: 0 !important;
                              }
                            `}</style>
                            <h5 className="mb-0 text-uppercase">PAYMENT SCHEDULE</h5>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleCalculate}
                            >
                              CALCULATE
                            </Button>
                          </div>
                          <div style={{ backgroundColor: isLightTheme ? "#f8f9fa" : "rgba(255,255,255,0.05)", padding: 16 }}>
                            <Row className="mb-2 font-weight-bold" style={{ fontSize: 12, color: '#a0a0b2', marginLeft: -4, marginRight: -4 }}>
                              <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}># of Pmts</Col>
                              <Col xs="3" style={{ paddingLeft: 4, paddingRight: 4 }}>Desired Payment</Col>
                              <Col xs="3" style={{ paddingLeft: 4, paddingRight: 4 }}>Frequency</Col>
                              <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}>First Payment Date</Col>
                              <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}>Calculated Payment</Col>
                            </Row>
                            {cpiSchedule.map((row) => (
                              <Row key={row.id} className="mb-2 align-items-center" style={{ marginLeft: -4, marginRight: -4 }}>
                                <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}>
                                  <Input
                                    bsSize="sm"
                                    value={row.count}
                                    onChange={(e) => handleCpiScheduleChange(row.id, 'count', e.target.value)}
                                    placeholder="0"
                                    style={{ borderRadius: 0, height: 31, fontSize: "14px" }}
                                  />
                                </Col>
                                <Col xs="3" style={{ paddingLeft: 4, paddingRight: 4 }}>
                                  <DollarInput
                                    value={row.amount}
                                    onChange={(val) => handleCpiScheduleChange(row.id, 'amount', val)}
                                    style={{ borderRadius: 0, fontSize: "14px", height: 31, padding: '0.25rem 0.5rem', borderColor: showErrors && row.id === 1 && !toNumber(row.amount) ? "red" : undefined }}
                                    placeholder="$0.00"
                                  />
                                </Col>
                                <Col xs="3" style={{ paddingLeft: 4, paddingRight: 4 }}>
                                  <DrawerSelect
                                    activeColor={activeColor}
                                    className="react-select"
                                    placeholder="Select"
                                    name={`schedule-frequency-${row.id}`}
                                    isSearchable={false}
                                    closeMenuOnSelect={true}
                                    value={{ value: row.frequency, label: row.frequency }}
                                    onChange={(opt) => handleCpiScheduleChange(row.id, 'frequency', opt?.value)}
                                    options={[
                                      { value: "Weekly", label: "Weekly" },
                                      { value: "Bi-Weekly", label: "Bi-Weekly" },
                                      { value: "Semi-Monthly", label: "Semi-Monthly" },
                                      { value: "Monthly", label: "Monthly" }
                                    ]}
                                    menuPortalTarget={document.body}
                                    styles={{
                                      control: (base) => ({ ...base, minHeight: 31, height: 31, alignItems: 'center' }),
                                      valueContainer: (base) => ({ ...base, height: 31, padding: '0 8px', display: 'flex', alignItems: 'center' }),
                                      singleValue: (base) => ({ ...base, margin: 0 }),
                                      input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                      indicatorsContainer: (base) => ({ ...base, height: 31 }),
                                      dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                                      menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                  />
                                </Col>
                                <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}>
                                  <ReactDatetime
                                    className="rdt-right"
                                    inputProps={{
                                      className: "form-control form-control-sm",
                                      placeholder: "",
                                      style: { borderRadius: 0, height: 31 },
                                      maxLength: 10
                                    }}
                                    value={row.firstDate ? formatDateForPicker(row.firstDate) : ""}
                                    onChange={(date) => handleCpiScheduleChange(row.id, 'firstDate', formatDateForAPI(date))}
                                    renderDay={renderDay}
                                    dateFormat="MM/DD/YYYY"
                                    timeFormat={false}
                                    closeOnSelect={true}
                                  />
                                </Col>
                                <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}>
                                  <Input
                                    readOnly
                                    value={row.calculated || '$0.00'}
                                    style={{ borderRadius: 0, height: 31, textAlign: 'right', backgroundColor: '#e9ecef', fontSize: "12px", color: '#495057' }}
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Row className="justify-content-end align-items-center mt-2" style={{ marginLeft: -4, marginRight: -4 }}>
                              <Col xs="auto" className="font-weight-bold" style={{ fontSize: 12, paddingLeft: 4, paddingRight: 4 }}>Final Payment</Col>
                              <Col xs="2" style={{ paddingLeft: 4, paddingRight: 4 }}>
                                <Input
                                  readOnly
                                  value="$0.00"
                                  style={{ borderRadius: 0, height: 31, textAlign: 'right', backgroundColor: '#e9ecef', fontSize: "12px", color: '#495057' }}
                                />
                              </Col>
                            </Row>
                          </div>

                          <div className="d-flex align-items-center mb-2 mt-2">
                            <h5 className="mb-0 text-uppercase">DISCLOSURE RESULTS</h5>
                          </div>
                          <div style={{ backgroundColor: isLightTheme ? "#f8f9fa" : "rgba(255,255,255,0.05)", padding: 16 }}>
                            <Row className="text-center">
                              <Col>
                                <div style={{ fontSize: 12, marginBottom: 4 }}>APR</div>
                                <div style={{ backgroundColor: '#e9ecef', color: '#495057', padding: 8, fontSize: 12, border: '1px solid #dee2e6' }}>0.0000%</div>
                              </Col>
                              <Col>
                                <div style={{ fontSize: 12, marginBottom: 4 }}>Finance Charge</div>
                                <div style={{ backgroundColor: '#e9ecef', color: '#495057', padding: 8, fontSize: 12, border: '1px solid #dee2e6' }}>$0.00</div>
                              </Col>
                              <Col>
                                <div style={{ fontSize: 12, marginBottom: 4 }}>Amount Financed</div>
                                <div style={{ backgroundColor: '#e9ecef', color: '#495057', padding: 8, fontSize: 12, border: '1px solid #dee2e6' }}>{sideNoteForm.amount || '$11,800.00'}</div>
                              </Col>
                              <Col>
                                <div style={{ fontSize: 12, marginBottom: 4 }}>Total of Payments</div>
                                <div style={{ backgroundColor: '#e9ecef', color: '#495057', padding: 8, fontSize: 12, border: '1px solid #dee2e6' }}>{sideNoteForm.amount || '$11,800.00'}</div>
                              </Col>
                            </Row>
                          </div>
                        </>
                      )}

                      <div className="d-flex justify-content-end mt-3" style={{ gap: '10px' }}>
                        <Button
                          color="info"
                          outline
                          size="sm"
                          className="btn-simple"
                          onClick={() => setPostSubView("menu")}
                        >
                          CANCEL
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSubmitSideNote}
                          disabled={createPaymentMutation.isPending}
                        >
                          {createPaymentMutation.isPending ? "Creating..." : "CREATE SIDE NOTE"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {postSubView === "late-fee-accrual" && !isChargeOffAccount && (() => {
                    const showLateFeeDateError =
                      lateFeeAccrualForm.is_waive_late_fee &&
                      !lateFeeAccrualForm.is_waive_late_fee_date;
                    const borrowerName =
                      [
                        accountData?.customer?.full_name,
                      ]
                        .filter(Boolean)
                        .join(" ")
                        .trim() ||
                      accountData?.customer?.full_name ||
                      "—";
                    const stockNumber =
                      accountData?.inventory?.stock_number ||
                      accountData?.inventory_vehicle?.stock_number ||
                      "—";
                    const actStatusLabel = status
                      ? String(status)
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())
                      : "—";
                    const actStatusColor = getAccountStatusColor(status);
                    const flagsText =
                      Array.isArray(flags) && flags.length > 0
                        ? flags.map((f) => f.name).join(", ")
                        : "—";
                    const okDisabled =
                      updateCalculusMutation.isPending ||
                      (lateFeeAccrualForm.is_waive_late_fee &&
                        !lateFeeAccrualForm.is_waive_late_fee_date);
                    return (
                    <div className="pt-2">
                      <h5 className="mb-2 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>
                        Late Fee Accrual
                      </h5>
                      <div
                        style={{
                          backgroundColor: isLightTheme
                            ? "#f8f9fa"
                            : "rgba(255,255,255,0.05)",
                          padding: 16,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          className="font-weight-bold mb-2"
                          style={{ fontSize: 13 }}
                        >
                          {borrowerName}
                        </div>
                        <Row className="mb-1" style={{ fontSize: 12 }}>
                          <Col xs="6">
                            <span className="text-muted">Account:</span>{" "}
                            <span>{bookingId || accountData?.booking_id || "—"}</span>
                          </Col>
                          <Col xs="6">
                            <span className="text-muted">Stock #:</span>{" "}
                            <span>{stockNumber}</span>
                          </Col>
                        </Row>
                        <Row className="mb-1" style={{ fontSize: 12 }}>
                          <Col xs="6">
                            <span className="text-muted">Acct Status:</span>{" "}
                            <span style={{ color: actStatusColor }}>{actStatusLabel}</span>
                          </Col>
                          <Col xs="6">
                            <span className="text-muted">Loan:</span>{" "}
                            <span>{stockNumber}</span>
                          </Col>
                        </Row>
                        <Row style={{ fontSize: 12 }}>
                          <Col xs="12">
                            <span className="text-muted">Flags:</span>{" "}
                            <span>{flagsText}</span>
                          </Col>
                        </Row>
                      </div>

                      <Row className="mb-2 align-items-center justify-content-center">
                      <Col
                          xs="6"
                          md="5"
                          lg="4"
                          className="d-flex flex-column align-items-start justify-content-center "
                        >
                          <span
                            className="mb-2"
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: isLightTheme ? "#222" : "rgba(255,255,255,0.92)",
                            }}
                          >
                            Waive Late Fees:
                          </span>
                          <button
                            type="button"
                            className="late-fee-waive-toggle"
                            role="switch"
                            aria-checked={!!lateFeeAccrualForm.is_waive_late_fee}
                            aria-label="Waive late fees"
                            onClick={() =>
                              setLateFeeAccrualForm((prev) => {
                                const next = !prev.is_waive_late_fee;
                                return {
                                  ...prev,
                                  is_waive_late_fee: next,
                                  is_waive_late_fee_date: next
                                    ? prev.is_waive_late_fee_date
                                    : "",
                                };
                              })
                            }
                            style={{
                              position: "relative",
                              width: 52,
                              height: 28,
                              padding: 0,
                              flexShrink: 0,
                              border: isLightTheme
                                ? "1px solid rgba(0,0,0,0.14)"
                                : "1px solid rgba(255,255,255,0.2)",
                              backgroundColor: lateFeeAccrualForm.is_waive_late_fee
                                ? "#68B17A"
                                : isLightTheme
                                  ? "#d5d5d5"
                                  : "#4a4a4a",
                              cursor: "pointer",
                              boxShadow: isLightTheme
                                ? "0 1px 2px rgba(0,0,0,0.08)"
                                : "0 1px 2px rgba(0,0,0,0.25)",
                              transition: "background-color 0.15s ease",
                            }}
                          >
                            <span
                              aria-hidden
                              className="late-fee-waive-toggle-thumb"
                              style={{
                                position: "absolute",
                                top: 3,
                                left: lateFeeAccrualForm.is_waive_late_fee ? 29 : 3,
                                width: 20,
                                height: 20,
                                backgroundColor: "#fff",
                                border: "1px solid rgba(0,0,0,0.06)",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                                transition: "left 0.15s ease",
                              }}
                            />
                          </button>
                        </Col>
                        <Col xs="6" md="6" lg="6">
                          <FormGroup className="mb-0 mb-3 mb-md-0">
                            <Label className="mb-1" style={{ fontSize: 12 }}>
                              Waive late fees through:
                            </Label>
                            <ReactDatetime
                              inputProps={{
                                className: "form-control",
                                placeholder: "Select Date",
                                maxLength: 10,
                                disabled: !lateFeeAccrualForm.is_waive_late_fee,
                                style: showLateFeeDateError
                                  ? {
                                      border: "1px solid red",
                                    }
                                  : undefined,
                              }}
                              value={
                                lateFeeAccrualForm.is_waive_late_fee_date
                                  ? formatDateForPicker(
                                      lateFeeAccrualForm.is_waive_late_fee_date
                                    )
                                  : ""
                              }
                              onChange={(date) => {
                                if (!lateFeeAccrualForm.is_waive_late_fee) return;
                                const formattedDate = formatDateForAPI(date) || "";
                                setLateFeeAccrualForm((prev) => ({
                                  ...prev,
                                  is_waive_late_fee_date: formattedDate,
                                }));
                              }}
                              renderDay={renderDay}
                              dateFormat="MM/DD/YYYY"
                              timeFormat={false}
                              closeOnSelect={!!lateFeeAccrualForm.is_waive_late_fee}
                            />
                          </FormGroup>
                        </Col>
                        
                      </Row>

                      <p style={{ fontSize: 12, lineHeight: 1.45, opacity: 0.9 }}>
                        Turning on Late fee Waiver will waive the accrual of late fees
                        through the date you specify and will begin accruing on the next
                        due date following the date indicated above. This election
                        overrides any portfolio based configurations.
                      </p>

                      <div className="d-flex justify-content-end mt-3" style={{ gap: "10px" }}>
                        <Button
                          color="info"
                          outline
                          size="sm"
                          className="btn-simple"
                          onClick={() => {
                            setLateFeeAccrualForm(
                              createLateFeeAccrualFormDefaults(calculusData)
                            );
                            setPostSubView("menu");
                          }}
                        >
                          CANCEL
                        </Button>
                        <Button
                          size="sm"
                          color="primary"
                          onClick={handleSubmitLateFeeAccrual}
                          disabled={okDisabled}
                        >
                          {updateCalculusMutation.isPending ? "Saving..." : "OK"}
                        </Button>
                      </div>
                    </div>
                    );
                  })()}

                  {postSubView === "reversal" && (
                    <div className="pt-0">
                      {!selectedReversalId && (
                        <div
                          className="p-1 mb-3 text-center font-weight-bold"
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            color: isLightTheme ? "#1e1e2f" : "#fff",
                          }}
                        >
                          Select a transaction and enter a reason to post reversal.
                        </div>
                      )}

                      <FormGroup className="mb-2">
                        <Row className="align-items-center no-gutters">
                          <Col md="2">
                            <Label className="mb-0 font-weight-bold" style={{ fontSize: '12px', color: isLightTheme ? "#666" : "#aaa" }}>Comment:</Label>
                          </Col>
                          <Col md="10">
                            <Input
                              type="text"
                              value={reversalComment}
                              onChange={(e) => setReversalComment(e.target.value)}
                              placeholder="Enter reversal reason..."
                              autoFocus
                              style={{
                                borderRadius: 0,
                                height: '36px',
                                fontSize: '14px',
                                border: isLightTheme ? '1px solid #ddd' : '1px solid #444',
                                backgroundColor: isLightTheme ? '#fff' : 'rgba(255,255,255,0.05)',
                                color: isLightTheme ? '#333' : '#fff'
                              }}
                              maxLength={100}
                            />
                          </Col>
                        </Row>
                      </FormGroup>

                      <div className="mt-0" style={{ border: isLightTheme ? '1px solid #eee' : '1px solid rgba(255,255,255,0.1)', backgroundColor: isLightTheme ? '#fff' : 'transparent' }}>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <Table responsive className="table-sm mb-0 table-hover" style={{ fontSize: '12px', color: isLightTheme ? '#333' : '#fff' }}>
                            <thead style={{ backgroundColor: isLightTheme ? "#f8f9fa" : "rgba(255,255,255,0.05)", position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr style={{ borderBottom: isLightTheme ? '2px solid #eee' : '2px solid #444' }}>
                                <th className="py-2 font-weight-bold">ID</th>
                                <th className="py-2 font-weight-bold">Post Date</th>
                                <th className="py-2 font-weight-bold">Tran Code</th>
                                <th className="py-2 font-weight-bold">Amount</th>
                                <th className="py-2 font-weight-bold">Form</th>
                                <th className="py-2 font-weight-bold">Comment</th>
                                <th className="py-2 font-weight-bold text-center">Select</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isTransactionsLoading ? (
                                <tr>
                                  <td colSpan="7" className="text-center py-4">
                                    <Spinner size="sm" color="info" /> Loading payments...
                                  </td>
                                </tr>
                              ) : (Array.isArray(transactionsData?.payments) ? transactionsData.payments : [])?.filter(t => t.amount > 0 && t.payment_type !== "reversal" && t.payment_type !== "def_down_reversal" && !t.is_reversal)?.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="text-center py-4 text-muted">
                                    No reversible payments found.
                                  </td>
                                </tr>
                              ) : (
                                (() => {
                                  const reversalPayments = (Array.isArray(transactionsData?.payments) ? transactionsData.payments : [])
                                    ?.filter(t => t.amount > 0 && t.payment_type !== "reversal" && t.payment_type !== "def_down_reversal" && !t.is_reversal);
                                  return reversalPayments?.map((transaction, idx) => {
                                    const id = transaction.id || transaction.documentId;
                                    const isSelected = selectedReversalId === id;
                                    const isSelectable = idx < 5;
                                    return (
                                      <tr
                                        key={id}
                                        onClick={() => isSelectable && setSelectedReversalId(id)}
                                        style={{
                                          cursor: isSelectable ? 'pointer' : 'default',
                                          backgroundColor: isSelected ? (isLightTheme ? 'rgba(29, 140, 248, 0.08)' : 'rgba(29, 140, 248, 0.15)') : 'transparent',
                                          transition: 'background-color 0.2s ease',
                                          opacity: !isSelectable ? 0.7 : 1
                                        }}
                                      >
                                        <td className="py-2 align-middle">{transaction.receipt_id}</td>
                                        <td className="py-2 align-middle">{formatDateTimeForDisplay(transaction.date)}</td>
                                        <td className="py-2 align-middle">{transaction.action_type || '1-Payment'}</td>
                                        <td className="py-2 align-middle font-weight-bold" style={{ color: "#1d8cf8" }}>
                                          {formatPriceWithDollar(transaction.amount)}
                                        </td>
                                        <td className="py-2 align-middle">{transaction.payment_form || 'Cash'}</td>
                                        <td className="py-2 align-middle text-truncate" style={{ maxWidth: '120px' }}>
                                          {transaction.note || transaction.comment || '-'}
                                        </td>
                                        <td className="py-2 align-middle text-center">
                                          {isSelectable ? (
                                            isSelected ? (
                                              <MdCheckCircle size={22} color="#1d8cf8" />
                                            ) : (
                                              <MdRadioButtonUnchecked size={22} color={isLightTheme ? "#ccc" : "#444"} />
                                            )
                                          ) : (
                                            <span style={{ whiteSpace: 'nowrap' }}>
                                              reverse in order
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  });
                                })()
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end mt-4 pt-3" style={{ gap: '12px', borderTop: isLightTheme ? '1px solid #eee' : '1px solid rgba(255,255,255,0.1)' }}>
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          onClick={() => {
                            setPostSubView("menu");
                            setSelectedReversalId(null);
                            setReversalComment("");
                          }}
                          style={{ borderRadius: 0, fontWeight: 'bold', padding: '8px 24px' }}
                        >
                          CANCEL
                        </Button>
                        <Button
                          color="info"
                          size="sm"
                          onClick={handleSubmitReversal}
                          disabled={!selectedReversalId || !reversalComment || createPaymentMutation.isPending}
                          style={{ borderRadius: 0, fontWeight: 'bold', padding: '8px 24px', opacity: (!selectedReversalId || !reversalComment) ? 0.6 : 1 }}
                        >
                          {createPaymentMutation.isPending ? "PROCESSING..." : "POST REVERSAL"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {postSubView === "misc-fee-adjustment" && (
                    <div className="pt-2">
                      <h5 className="mb-2 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>Post Misc Fee Adjustment</h5>
                      <div style={{ backgroundColor: isLightTheme ? "#f8f9fa" : "rgba(255,255,255,0.05)", padding: 16 }}>
                        <Row>
                          <Col md="6" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1" style={{ fontSize: 12 }}>Date Due:</Label>
                              <ReactDatetime
                                inputProps={{
                                  className: "form-control",
                                  placeholder: "Select Date",
                                  maxLength: 10,
                                  disabled: miscFeeAdjustmentForm.adjustment_type === "Decrease",
                                }}
                                value={
                                  miscFeeAdjustmentForm.date_due
                                    ? formatDateForPicker(miscFeeAdjustmentForm.date_due)
                                    : ""
                                }
                                onChange={(date) => {
                                  if (miscFeeAdjustmentForm.adjustment_type === "Decrease") return;
                                  const formattedDate = formatDateForAPI(date) || "";
                                  setMiscFeeAdjustmentForm((prev) => ({
                                    ...prev,
                                    date_due: formattedDate,
                                  }));
                                }}
                                renderDay={renderDay}
                                dateFormat="MM/DD/YYYY"
                                timeFormat={false}
                                closeOnSelect={miscFeeAdjustmentForm.adjustment_type !== "Decrease"}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1" style={{ fontSize: 12 }}>Adjustment Type:</Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select type"
                                name="misc_fee_adjustment_type"
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  miscFeeAdjustmentForm.adjustment_type
                                    ? miscFeeAdjustmentTypeOptions.find(
                                        (o) => o.value === miscFeeAdjustmentForm.adjustment_type
                                      ) || {
                                        value: miscFeeAdjustmentForm.adjustment_type,
                                        label: miscFeeAdjustmentForm.adjustment_type,
                                      }
                                    : null
                                }
                                onChange={(opt) =>
                                  setMiscFeeAdjustmentForm((prev) => ({
                                    ...prev,
                                    adjustment_type: opt?.value || "",
                                  }))
                                }
                                options={miscFeeAdjustmentTypeOptions}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="12" className="mb-1">
                            <FormGroup>
                              <Label className="mb-1" style={{ fontSize: 12 }}>Description:</Label>
                              <Input
                                type="textarea"
                                rows={3}
                                value={miscFeeAdjustmentForm.description}
                                onChange={(e) =>
                                  setMiscFeeAdjustmentForm((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                  }))
                                }
                                placeholder="Enter description..."
                                style={{ fontSize: "14px" }}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6" className="mb-2">
                            <FormGroup>
                              <Label className="mb-1" style={{ fontSize: 12 }}>Adjustment Amount:</Label>
                              <DollarInput
                                name="misc_fee_adjustment_amount"
                                value={miscFeeAdjustmentForm.amount}
                                onChange={(val) =>
                                  setMiscFeeAdjustmentForm((prev) => ({ ...prev, amount: val }))
                                }
                                placeholder="$0.00"
                                updateWhileTyping
                              />
                              {miscFeeAdjustmentForm.adjustment_type === "Decrease" ? (
                                <div
                                  className="mt-1"
                                  style={{
                                    fontSize: 11,
                                    color: isMiscFeeDecreaseOverCap
                                      ? "#fd5d93"
                                      : isLightTheme
                                        ? "#666"
                                        : "#a0a0b2",
                                  }}
                                >
                                  {isMiscFeeDecreaseOverCap
                                    ? `Amount cannot exceed ${formatPriceWithDollar(
                                        miscFeeIncreaseCap
                                      )} (increase misc fees balance).`
                                    : `Maximum decrease: ${formatPriceWithDollar(
                                        miscFeeIncreaseCap
                                      )} (increase misc fees balance).`}
                                </div>
                              ) : null}
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                      <div className="d-flex justify-content-end mt-2" style={{ gap: '10px' }}>
                        <Button
                          color="info"
                          outline
                          size="sm"
                          className="btn-simple"
                          onClick={() => {
                            setMiscFeeAdjustmentForm(createMiscFeeAdjustmentFormDefaults());
                            setPostSubView("menu");
                          }}
                        >
                          CANCEL
                        </Button>
                        <Button
                          size="sm"
                          style={{ ...activeColorStyle, color: "white" }}
                          disabled={
                            updateCalculusMutation.isPending ||
                            !miscFeeAdjustmentForm.adjustment_type ||
                            toNumber(miscFeeAdjustmentForm.amount) === 0 ||
                            isMiscFeeDecreaseOverCap
                          }
                          onClick={handleSubmitMiscFeeAdjustment}
                        >
                          {updateCalculusMutation.isPending ? "SAVING..." : "POST"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {postSubView === "charge-off" && !isChargeOffAccount && (() => {
                    const showInventoryFields =
                      chargeOffForm.skip_or_charge_off === "Charge Off" &&
                      chargeOffForm.add_to_inventory === "Yes";
                    const showRecoveryFields =
                      chargeOffForm.skip_or_charge_off === "Charge Off" &&
                      chargeOffForm.add_to_inventory === "No";
                    const isChargeOffSubmitDisabled =
                      chargeOffMutation.isPending ||
                      (showInventoryFields &&
                        (!String(chargeOffForm.mileage ?? "").trim() ||
                          toNumber(chargeOffForm.collateral_acv) <= 0));

                    return (
                    <div className="pt-2">
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>
                        Post Charge Off
                      </h5>

                      <Row className="mx-0">
                        <Col md="12" className="mb-1 px-1">
                          <div
                            style={{
                              backgroundColor: isLightTheme
                                ? "#f8f9fa"
                                : "rgba(255,255,255,0.05)",
                              padding: 12,
                              border: isLightTheme
                                ? "1px solid #e0e0e0"
                                : "1px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <h6
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "600" }}
                            >
                              Charge Off Detail
                            </h6>
                            <div style={{ fontSize: "13px" }}>
                              {[
                                {
                                  label: "Down Payment Balance:",
                                  value: calculusData?.balances?.current_def_down || 0,
                                },
                                {
                                  label: "Principal Balance:",
                                  value: calculusData?.balances?.current_principal || 0,
                                },
                                {
                                  label: "Sales Tax Balance:",
                                  value: calculusData?.balances?.current_state_sales_tax || 0,
                                },
                                {
                                  label: "Non Interest Earning Balance:",
                                  value: calculusData?.balances?.current_non_earning_principal || 0,
                                },
                                {
                                  label: "Accrued Interest Balance:",
                                  value: (() => {
                                    const row =
                                      calculusData?.payment_select_type?.find((i) => i.value === "All Balances") ??
                                      calculusData?.payment_select_type?.[0];
                                    return row?.current_interest ?? row?.accured_interest ?? 0;
                                  })(),
                                },
                                {
                                  label: "Late Fees Balance:",
                                  value: calculusData?.balances?.current_late_fee || 0,
                                },
                                {
                                  label: "Misc Fees Balance:",
                                  value: 0,
                                },
                              ].map((item, idx) => (
                                <div
                                  key={idx}
                                  className="d-flex justify-content-between mb-1"
                                >
                                  <span className="text-right" style={{ width: "60%" }}>
                                    {item.label}
                                  </span>
                                  <span style={{ width: "40%", textAlign: "right" }}>
                                    {formatPriceWithDollar(item.value)}
                                  </span>
                                </div>
                              ))}
                              <div
                                style={{
                                  borderTop: isLightTheme
                                    ? "2px solid #333"
                                    : "2px solid #fff",
                                  margin: "5px 0",
                                }}
                              />
                              <div className="d-flex justify-content-between mb-0">
                                <span
                                  className="font-weight-bold text-right"
                                  style={{ width: "60%" }}
                                >
                                  Payoff:
                                </span>
                                <span
                                  style={{
                                    width: "40%",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {formatPriceWithDollar(calculusData?.balances?.current_total || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Col>

                        <Col md="6" className="mb-1 px-1">
                          <FormGroup className="mb-0">
                            <Label className="mb-0" style={{ fontSize: 12 }}>
                              Skip or Charge Off?
                            </Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Select"
                              name="skip_or_charge_off"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={
                                skipOrChargeOffOptions.find(
                                  (opt) => opt.value === chargeOffForm.skip_or_charge_off
                                ) || null
                              }
                              onChange={(option) =>
                                handleChargeOffInputChange(
                                  "skip_or_charge_off",
                                  option?.value || ""
                                )
                              }
                              options={skipOrChargeOffOptions}
                            />
                          </FormGroup>
                        </Col>

                        {chargeOffForm.skip_or_charge_off === "Charge Off" && (
                          <Col md="6" className="mb-1 px-1">
                            <FormGroup className="mb-0">
                              <Label className="mb-0" style={{ fontSize: 12 }}>
                                Add to Inventory?
                              </Label>
                              <DrawerSelect
                                activeColor={activeColor}
                                className="react-select"
                                placeholder="Select"
                                name="add_to_inventory"
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  yesNoOptions.find(
                                    (opt) => opt.value === chargeOffForm.add_to_inventory
                                  ) || null
                                }
                                onChange={(option) =>
                                  handleChargeOffInputChange(
                                    "add_to_inventory",
                                    option?.value || ""
                                  )
                                }
                                options={yesNoOptions}
                              />
                            </FormGroup>
                          </Col>
                        )}

                        {showInventoryFields && (
                          <>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Sales Location?
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="sales_location"
                                  isSearchable={true}
                                  closeMenuOnSelect={true}
                                  value={
                                    chargeOffForm.sales_location === "[No Change]"
                                      ? { value: "[No Change]", label: "[No Change]" }
                                      : [...(salesLocations?.results?.map(loc => ({ value: loc.id, label: loc.name })) || [])].find(opt => opt.value === chargeOffForm.sales_location) || null
                                  }
                                  onChange={(option) =>
                                    handleChargeOffInputChange("sales_location", option?.value || "[No Change]")
                                  }
                                  options={[
                                    { value: "[No Change]", label: "[No Change]" },
                                    ...(salesLocations?.results?.map(loc => ({ value: loc.id, label: loc.name })) || [])
                                  ]}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Mileage <span style={{ color: "red" }}>*</span>
                                </Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={chargeOffForm.mileage}
                                  onChange={(e) =>
                                    handleChargeOffInputChange(
                                      "mileage",
                                      e.target.value.replace(/\D/g, "")
                                    )
                                  }
                                  style={{ fontSize: "14px", height: "38px" }}
                                  className="text-right"
                                  placeholder="Enter mileage"
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Mileage Status?
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="mileage_status"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  value={mileageStatusOptions.find(opt => opt.value === chargeOffForm.mileage_status) || null}
                                  onChange={(option) => handleChargeOffInputChange("mileage_status", option?.value || "")}
                                  options={mileageStatusOptions}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Collateral ACV <span style={{ color: "red" }}>*</span>
                                </Label>
                                <DollarInput
                                  value={chargeOffForm.collateral_acv}
                                  onChange={(val) => handleChargeOffInputChange("collateral_acv", val)}
                                  placeholder="$0.00"
                                  inputProps={{
                                    style: { textAlign: "right", height: "38px" },
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Strict Foreclosure?
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="strict_foreclosure"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  value={yesNoOptions.find(opt => opt.value === chargeOffForm.strict_foreclosure) || null}
                                  onChange={(option) => handleChargeOffInputChange("strict_foreclosure", option?.value || "No")}
                                  options={yesNoOptions}
                                />
                              </FormGroup>
                            </Col>
                          </>
                        )}

                        {showRecoveryFields && (
                          <>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Do You Wish To Post A Recovery?
                                </Label>
                                <DollarInput
                                  value={chargeOffForm.post_recovery || formatPriceWithDollar(0)}
                                  onChange={(val) =>
                                    handleChargeOffInputChange("post_recovery", val)
                                  }
                                  inputProps={{
                                    style: { textAlign: "right", height: "38px" },
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="mb-1 px-1">
                              <FormGroup className="mb-0">
                                <Label className="mb-0" style={{ fontSize: 12 }}>
                                  Release customer from deficiency balance?
                                </Label>
                                <DrawerSelect
                                  activeColor={activeColor}
                                  className="react-select"
                                  placeholder="Select"
                                  name="release_customer"
                                  isSearchable={false}
                                  closeMenuOnSelect={true}
                                  value={
                                    yesNoOptions.find(
                                      (opt) => opt.value === chargeOffForm.release_customer
                                    ) || null
                                  }
                                  onChange={(option) =>
                                    handleChargeOffInputChange(
                                      "release_customer",
                                      option?.value || "No"
                                    )
                                  }
                                  options={yesNoOptions}
                                />
                              </FormGroup>
                            </Col>
                          </>
                        )}

                        <Col md="12" className="mb-1 px-1">
                          <FormGroup className="mb-0">
                            <Label className="mb-0" style={{ fontSize: 12 }}>
                              Comments:
                            </Label>
                            <Input
                              type="textarea"
                              rows="2"
                              value={chargeOffForm.comments}
                              onChange={(e) =>
                                handleChargeOffInputChange("comments", e.target.value)
                              }
                              placeholder="Enter comments..."
                              style={{ fontSize: "14px" }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end mt-1" style={{ gap: "10px" }}>
                        <Button
                          color="info"
                          outline
                          size="sm"
                          className="btn-simple"
                          onClick={() => {
                            setChargeOffForm({
                              skip_or_charge_off: "Charge Off",
                              add_to_inventory: "Yes",
                              sales_location: "[No Change]",
                              mileage: "",
                              mileage_status: "Actual Miles",
                              collateral_acv: formatPriceWithDollar(0),
                              strict_foreclosure: "No",
                              post_recovery: formatPriceWithDollar(0),
                              release_customer: "No",
                              comments: "",
                            });
                            setPostSubView("menu");
                          }}
                        >
                          CANCEL
                        </Button>
                        <Button
                          size="sm"
                          style={{ ...activeColorStyle, color: "white" }}
                          disabled={isChargeOffSubmitDisabled}
                          onClick={() => chargeOffMutation.mutate({
                            calculusId: bookingId,
                            data: {
                              ...chargeOffForm,
                              collateral_acv: toNumber(chargeOffForm.collateral_acv),
                              post_recovery: toNumber(chargeOffForm.post_recovery),
                              sales_location:
                                chargeOffForm.sales_location === "[No Change]"
                                  ? null
                                  : chargeOffForm.sales_location,
                            },
                          })}
                        >
                          {chargeOffMutation.isPending ? "CHARGING OFF..." : "CHARGE OFF"}
                        </Button>
                      </div>
                    </div>
                    );
                  })()}

                  {postSubView === "charge-off-reversal" && isChargeOffAccount && (
                    <div>
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>
                        Post Charge Off Reversal
                      </h5>

                      <Row>
                        <Col md="12" className="mb-1 px-1">
                          <FormGroup className="mb-0">
                            <Label className="mb-0" style={{ fontSize: 12 }}>
                              Notes:
                            </Label>
                            <Input
                              type="textarea"
                              rows="3"
                              value={chargeOffReversalComments}
                              onChange={(e) => setChargeOffReversalComments(e.target.value)}
                              placeholder="Enter notes..."
                              style={{ fontSize: "14px" }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end mt-1" style={{ gap: "10px" }}>
                        <Button
                          color="info"
                          outline
                          size="sm"
                          className="btn-simple"
                          onClick={() => {
                            setChargeOffReversalComments("");
                            setPostSubView("menu");
                          }}
                        >
                          CANCEL
                        </Button>
                        <Button
                          size="sm"
                          style={{ ...activeColorStyle, color: "white" }}
                          disabled={chargeOffReversalMutation.isPending}
                          onClick={() => chargeOffReversalMutation.mutate(
                            {
                              calculusId: bookingId,
                              data: { comments: chargeOffReversalComments },
                            },
                            {
                              onSuccess: () => {
                                setChargeOffReversalComments("");
                                setPostSubView("menu");
                                closeDrawer();
                              },
                            }
                          )}
                        >
                          {chargeOffReversalMutation.isPending ? "REVERSING..." : "POST CHARGE OFF REVERSAL"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {postSubView === "extension" && !isChargeOffAccount && (
                    <div>
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>Post Extension</h5>
                      {(() => {
                        const remainingPayments = Array.isArray(calculusData?.remaining_payments)
                          ? calculusData.remaining_payments
                          : [];
                        return (
                          <div style={{ marginTop: 16 }}>
                            {remainingPayments.length === 0 ? (
                              <div className="text-muted" style={{ fontSize: 13, textAlign: "center", padding: "12px 0" }}>
                                No remaining payments.
                              </div>
                            ) : (
                              <div style={{ maxHeight: 250, overflowY: "auto", position: "relative" }}>
                                <table className="table table-sm mb-0" style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                                  <thead>
                                    <tr>
                                      {[
                                        "Due Date",
                                        "Payment Type",
                                        "AutoPay",
                                        "Payment Amount",
                                        "Late Fee",
                                        "Total Due",
                                        "Select Payment To Extend",
                                      ].map((h) => (
                                        <th key={h} style={{
                                          backgroundColor: isLightTheme ? "#f8f9fa" : "#2a2a3a",
                                          color: isLightTheme ? "#444" : "#ccc",
                                          fontWeight: 700,
                                          padding: "6px 8px",
                                          fontSize: 11,
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 1,
                                          borderBottom: isLightTheme ? "2px solid #dee2e6" : "2px solid #444",
                                          whiteSpace: "normal",
                                          textAlign: "center",
                                          verticalAlign: "bottom",
                                          lineHeight: 1.2,
                                        }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {remainingPayments.map((item, idx) => {
                                      const dueDate = item.due_date || item.date || null;
                                      const pmtType = item.description || item.note || "";
                                      const daysPastDue = item.day_past_due ?? 0;
                                      const schedAmt = getSchedulePaymentAmount(item);
                                      const schedLateFee = getRemainingLateFeeDue(item);
                                      const schedTotal = getScheduleTotalDue(item);

                                      const lateDay = Number(daysPastDue) || 0;
                                      let dateBg = "#28a745";
                                      if (lateDay > 0) dateBg = "#f6ad55";
                                      if (lateDay > 15) dateBg = "#f6a04d";
                                      if (lateDay > 30) dateBg = "#d97706";
                                      if (lateDay > 60) dateBg = "#bf6d00";
                                      if (lateDay >= 90) dateBg = "#d66d75";
                                      if (lateDay >= 120) dateBg = "#ef5350";

                                      const formatDate = (d) => {
                                        if (!d) return "-";
                                        const dt = new Date(d);
                                        return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}/${dt.getUTCFullYear()}`;
                                      };

                                      const rowStyle = { borderBottom: isLightTheme ? "1px solid #dee2e6" : "1px solid #444", cursor: "pointer" };
                                      const td = { padding: "6px 8px", fontSize: 11, color: isLightTheme ? "#333" : "#ddd", textAlign: "center", verticalAlign: "middle" };

                                      return (
                                        <tr key={idx} style={rowStyle} onClick={() => setSelectedExtensionIdx(idx)}>
                                          <td style={{ ...td, padding: "4px 6px" }}>
                                            <span style={{
                                              display: "inline-block",
                                              backgroundColor: dateBg,
                                              color: "#fff",
                                              fontWeight: 700,
                                              fontSize: 11,
                                              padding: "3px 7px",
                                              whiteSpace: "nowrap",
                                            }}>
                                              {formatDate(dueDate)}
                                            </span>
                                          </td>
                                          <td style={{ ...td, textAlign: "left", color: isLightTheme ? "#2f80ed" : "#0092e0", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            title={pmtType}>
                                            {pmtType.length > 12 ? pmtType.slice(0, 11) + "\u2026" : pmtType || "-"}
                                          </td>
                                          <td style={td}>
                                            <input type="checkbox" disabled checked={!!item.autopay} style={{ cursor: "not-allowed", opacity: 0.7 }} />
                                          </td>
                                          <td style={td}>{formatPriceWithDollar(schedAmt)}</td>
                                          <td style={td}>{formatPriceWithDollar(schedLateFee)}</td>
                                          <td style={td}>{formatPriceWithDollar(schedTotal)}</td>
                                          <td style={td}>
                                            <input
                                              type="radio"
                                              name="extension_payment"
                                              checked={selectedExtensionIdx === idx}
                                              onChange={() => setSelectedExtensionIdx(idx)}
                                              style={{ cursor: "pointer" }}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <Row className="mt-3">
                        <Col md="12" className="d-flex justify-content-end">
                          <Button
                            color="info"
                            outline
                            size="sm"
                            className="btn-simple mr-2"
                            onClick={() => setPostSubView("menu")}
                            style={{ marginRight: '8px' }}
                          >
                            CANCEL
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleSubmitExtension}
                            disabled={selectedExtensionIdx === null || createPaymentMutation.isPending}
                            style={{
                              ...(activeColorStyle || {}),
                              opacity: selectedExtensionIdx === null || createPaymentMutation.isPending ? 0.6 : 1,
                              border: "none",
                            }}
                          >
                            {createPaymentMutation.isPending ? "Posting..." : "POST"}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {postSubView === "write-down" && !isChargeOffAccount && (
                    <div>
                      <h5 className="mb-1 mt-1" style={{ fontSize: "13px", fontWeight: 700 }}>Post Account Write Down</h5>

                      <Row>
                        <Col md="12" className="mb-2">
                          <FormGroup>
                            <Label className="mb-1" style={{ fontSize: 12 }}>
                              Select the Loan Balance to Write Down:
                            </Label>
                            <DrawerSelect
                              activeColor={activeColor}
                              className="react-select"
                              placeholder="Select Balance"
                              name="balance_type"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={
                                calculusData?.payment_select_type?.find(
                                  (option) => option.value === writeDownForm.balance_type
                                )
                                  ? {
                                    value: writeDownForm.balance_type,
                                    label: writeDownForm.balance_type,
                                  }
                                  : null
                              }
                              onChange={(option) =>
                                setWriteDownForm((prev) => ({
                                  ...prev,
                                  balance_type: option?.value || "",
                                }))
                              }
                              options={
                                calculusData?.payment_select_type?.map((item) => ({
                                  value: item.value,
                                  label: item.value,
                                })) || []
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="12">
                          <div
                            style={{
                              backgroundColor: isLightTheme
                                ? "#f8f9fa"
                                : "rgba(255,255,255,0.05)",
                              padding: 15,
                              border: isLightTheme
                                ? "1px solid #e0e0e0"
                                : "1px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <h6
                              className="mb-2"
                              style={{ fontSize: "13px", fontWeight: "600" }}
                            >
                              Write Down Detail
                            </h6>

                            <div style={{ fontSize: "13px" }}>
                              {(() => {
                                // Find selected item's amount
                                const selectedItem = calculusData?.payment_select_type?.find(
                                  (item) => item.value === writeDownForm.balance_type
                                );
                                const selectedAmount = selectedItem?.amount || 0;
                                const isAllBalancesSelected =
                                  writeDownForm.balance_type === "All Balances" ||
                                  selectedItem?.value === "All Balances";
                                const isPrimaryLoanSelected =
                                  writeDownForm.balance_type === "principal" ||
                                  writeDownForm.balance_type === "Primary Loan" ||
                                  writeDownForm.balance_type === "Primary loan" ||
                                  selectedItem?.value === "principal" ||
                                  selectedItem?.value === "Primary Loan" ||
                                  selectedItem?.value === "Primary loan" ||
                                  String(selectedItem?.value || "")
                                    .toLowerCase()
                                    .includes("principal");

                                return [
                                  {
                                    label: "Down Payment Balance:",
                                    value:
                                      selectedItem?.def_down_amount ||
                                      0,
                                  },
                                  {
                                    label: "Principal Balance:",
                                    value: selectedAmount,
                                  },
                                  {
                                    label: "Sales Tax Balance:",
                                    value: selectedItem?.sales_tax_amount ||
                                      0,
                                  },
                                  {
                                    label: "Non Interest Earning Balance:",
                                    value:
                                      selectedItem?.current_non_earning_principal ??
                                      (isPrimaryLoanSelected || isAllBalancesSelected
                                        ? calculusData?.balances?.current_non_earning_principal ?? 0
                                        : 0),
                                  },
                                  {
                                    label: "Accrued Interest Balance:",
                                    value: selectedItem?.current_interest ?? selectedItem?.accured_interest ?? 0,
                                  },
                                  {
                                    label: "Late Fees Balance:",
                                    value: selectedItem?.late_fee ?? 0,
                                  },
                                  {
                                    label: "Misc Fees Balance:",
                                    value: selectedItem?.misc_fee ?? 0,
                                  },
                                ].map((item, idx) => (
                                  <div
                                    key={idx}
                                    className="d-flex justify-content-between mb-1"
                                  >
                                    <span
                                      className="text-right"
                                      style={{ width: "60%" }}
                                    >
                                      {item.label}
                                    </span>
                                    <span
                                      style={{ width: "40%", textAlign: "right" }}
                                    >
                                      {formatPriceWithDollar(item.value)}
                                    </span>
                                  </div>
                                ));
                              })()}
                              <div
                                style={{
                                  borderTop: isLightTheme
                                    ? "2px solid #333"
                                    : "2px solid #fff",
                                  margin: "5px 0",
                                }}
                              ></div>
                              <div className="d-flex justify-content-between mb-2">
                                <span
                                  className="font-weight-bold text-right"
                                  style={{ width: "60%" }}
                                >
                                  Total Writedown:
                                </span>
                                <span
                                  style={{
                                    width: "40%",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {(() => {
                                    const selectedItem = calculusData?.payment_select_type?.find(
                                      (item) => item.value === writeDownForm.balance_type
                                    );
                                    return formatPriceWithDollar(selectedItem?.total || 0);
                                  })()}
                                </span>
                              </div>
                            </div>

                            <div className="mt-2">
                              <Label className="mb-1" style={{ fontSize: 13 }}>
                                Comments: <span style={{ fontSize: 11, color: '#888' }}>(50 Max)</span>
                              </Label>
                              <Input
                                type="textarea"
                                rows="3"
                                maxLength={50}
                                value={writeDownForm.comments}
                                onChange={(e) =>
                                  setWriteDownForm((prev) => ({
                                    ...prev,
                                    comments: e.target.value,
                                  }))
                                }
                                placeholder="Enter comments..."
                                style={{ borderRadius: 0, fontSize: "13px",padding: "5px" }}
                              />
                            </div>
                          </div>
                        </Col>

                        <Col md="12" className="d-flex justify-content-end mt-2">
                          <Button
                            size="lg"
                            onClick={handleSubmitWriteDown}
                            disabled={createWriteDownMutation.isPending}
                            style={{
                              fontWeight: "600",
                              padding: "10px 40px",
                            }}
                          >
                            {createWriteDownMutation.isPending ? "Posting..." : "POST"}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              )}
              {drawerActiveTab === "flags" && (
                <div style={{ marginTop: 12, maxWidth: "100%", overflowX: "hidden" }}>
                  <FlagTab
                    permission={permission}
                    flags={flags || []}
                    id={bookingId}
                    activeColor={activeColor}
                    module="accounts"
                    update={updateCalculusMutation}
                    isTheme2={isTheme2}
                    compact
                  />
                </div>
              )}
              {drawerActiveTab === "notes" && (
                <NotesTab
                  module="accounts"
                  id={bookingId}
                  activeColor={activeColor}
                  permission={{ notes_add: true }}
                  isTheme2={isTheme2}
                />
              )}
              {drawerActiveTab === "more" && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr', gap: '10px' }}>
                    {[
                      {
                        label: "Add Borrower File",
                        action: () => {
                          if (onAddBorrowerFile) onAddBorrowerFile();
                          closeDrawer();
                        },
                        icon: <MdInsertDriveFile size={20} />,
                        visible: true
                      },
                      {
                        label: "Add Collateral Image",
                        action: () => {
                          if (onAddCollateralImage) onAddCollateralImage();
                          closeDrawer();
                        },
                        icon: <MdAddPhotoAlternate size={20} />,
                        visible: true
                      },
                      {
                        label: "Add Collateral File",
                        action: () => {
                          if (onAddCollateralFile) onAddCollateralFile();
                          closeDrawer();
                        },
                        icon: <MdInsertDriveFile size={20} />,
                        visible: true
                      },
                      {
                        label: "Add/Edit Flags",
                        action: () => setDrawerActiveTab("flags"),
                        icon: <MdFlag size={20} />,
                        visible: true
                      },
                    ].filter(item => item.visible).map((item, index) => (
                      <div
                        key={index}
                        onClick={item.action}
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          padding: "16px 20px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: isLightTheme
                            ? "1px solid #e0e0e0"
                            : "1px solid rgba(255, 255, 255, 0.1)",
                          backgroundColor: isLightTheme
                            ? "#fff"
                            : "rgba(255, 255, 255, 0.05)",
                          color: isLightTheme ? "#333" : "#fff",
                          transition: "all 0.2s ease",
                          boxShadow: isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isLightTheme
                            ? "#f8f9fa"
                            : "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow = isLightTheme ? "0 4px 8px rgba(0,0,0,0.05)" : "0 4px 8px rgba(0,0,0,0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isLightTheme
                            ? "#fff"
                            : "rgba(255, 255, 255, 0.05)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none";
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <span style={{ marginRight: '16px', opacity: 0.8 }}>{item.icon}</span>
                          <span>{item.label}</span>
                        </div>
                        <MdChevronRight size={20} style={{ opacity: 0.5 }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {drawerActiveTab === "send" && renderSendContent()}
              {drawerActiveTab === "print" && renderPrintContent()}
            </div>
            </div>
          </div>
        </div >
      )}
    </>
  );
});

export default AccountsRightDrawer;
